import { Helmet } from "react-helmet-async";
import React, { useState, useEffect, useRef, useMemo, lazy, Suspense } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, MapPin, TrendingUp, AlertTriangle, Activity, Database, Loader2, DollarSign, Map as MapIcon, FileText, Stethoscope, Search, ArrowUpDown, ChevronRight, Building2, ExternalLink, Users, Calendar, Banknote, Volume2 } from "lucide-react";
import useEmblaCarousel from "embla-carousel-react";
import Autoplay from "embla-carousel-autoplay";
import { useHospitalScenarios, type HospitalScenarioRow } from "@/hooks/useHospitalScenarios";
import { ScenarioCard, type ScenarioCardData, type CardTheme } from "@/components/ScenarioCard";
import { SCENARIO_CATEGORIES, scenarioMatchesCategory } from "@/config/scenarioCategories";
import { Input } from "@/components/ui/input";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { HospitalMappingsTable } from "@/components/HospitalMappingsTable";
import { HospitalMarkupOverview } from "@/components/HospitalMarkupOverview";
import { HospitalChargemastersTab } from "@/components/HospitalChargemastersTab";
import { HospitalExcludedCodesSection } from "@/components/HospitalExcludedCodesSection";
import { useToast } from "@/hooks/use-toast";
import { Link } from "react-router-dom";
import { useBrand } from "@/config/brands";
import { useTheme } from "@/contexts/ThemeContext";
import { useAhpHeaderOffset } from "@/components/AhpGlobalHeader";
import { useTranslation, Trans } from 'react-i18next';
import { trackHospitalView, trackHospitalTabChange } from "@/utils/analytics";
import { QualityReportCard, type QualityGroupTally } from "@/components/hospital/QualityReportCard";
import { DignityBriefingPip } from "@/components/hospital/DignityBriefingPip";
import { HospitalCityView } from "@/components/hospital/HospitalCityView";
import { ScenarioDistanceTool } from "@/components/hospital/ScenarioDistanceTool";
import { NEIGHBOUR_RADII, useHospitalNeighbours, useNeighbourGrades, type NeighbourRadius } from "@/hooks/useHospitalNeighbours";
import { useHospitalBriefingVideo, type HospitalBriefingVideo } from "@/hooks/useHospitalBriefingVideo";

// Tab ids the homepage profile-demo bridge is allowed to switch to (see the
// `demo:tab` / `demo:hospital` postMessage handler inside HospitalProfile).
const DEMO_TAB_IDS = new Set(['profile', 'quality', 'scenarios', 'nearby', 'services', 'corruption', 'chargemasters']);
import { useStaticHospitalData } from "@/hooks/useStaticHospitalData";
import { loadStaticData } from "@/lib/offlineData";
import { mdLite } from "../../functions/lib/md-lite";
import { renderHospitalProfile, type ProfileShard } from "../../functions/lib/hospital-profile-template";
import { titleCaseHospital } from "@/lib/hospitalName";
import { getHospitalGradeInfo } from "@/lib/grades";

// Reusable stat card matching the Quality slider aesthetic — flag-stripe accent + huge labeled number.
// Used in the page header (national rank, outlier rate, financial summary, quality summary).
function StatCard({
  isDark,
  tone,
  label,
  value,
  unit,
  sub,
  pill,
  cta,
  to,
  onClick,
  tooltip,
}: {
  isDark: boolean;
  tone: 'good' | 'neutral' | 'bad' | 'na' | 'info';
  label: React.ReactNode;
  value: React.ReactNode;
  unit?: string;
  sub?: React.ReactNode;
  pill?: { text: string };
  cta?: string;
  to?: string;
  onClick?: () => void;
  tooltip?: React.ReactNode;
}) {
  // Mirror QualityMeasureSlider's tone palette exactly.
  const tonesDark: Record<string, { fill: string; stripe: string; soft: string; pillBg: string; pillBd: string; pillTx: string }> = {
    good:    { fill: '#10b981', stripe: '#0e9670', soft: 'rgba(16, 185, 129, 0.10)',  pillBg: 'rgba(16, 185, 129, 0.18)',  pillBd: 'rgba(16, 185, 129, 0.55)',  pillTx: '#86efac' },
    neutral: { fill: 'var(--civic-gold)', stripe: 'var(--civic-gold)', soft: 'rgba(var(--civic-gold-rgb), 0.10)',  pillBg: 'rgba(var(--civic-gold-rgb), 0.20)',  pillBd: 'rgba(var(--civic-gold-rgb), 0.65)',  pillTx: 'var(--civic-parchment)' },
    bad:     { fill: '#ff7082', stripe: '#9A1E2D', soft: 'rgba(var(--civic-red-rgb), 0.12)',   pillBg: 'rgba(var(--civic-red-rgb), 0.22)',   pillBd: 'rgba(var(--civic-red-rgb), 0.60)',   pillTx: '#FCA5A5' },
    info:    { fill: '#7BC0E0', stripe: '#5B9CC0', soft: 'rgba(91, 156, 192, 0.10)',  pillBg: 'rgba(91, 156, 192, 0.20)',  pillBd: 'rgba(91, 156, 192, 0.55)',  pillTx: '#BCE0F2' },
    na:      { fill: '#A9B4C6', stripe: '#7B8AA0', soft: 'rgba(var(--civic-parchment-rgb), 0.05)', pillBg: 'rgba(var(--civic-parchment-rgb), 0.10)', pillBd: 'rgba(var(--civic-parchment-rgb), 0.30)', pillTx: '#D4C9AE' },
  };
  const tonesLight: Record<string, { fill: string; stripe: string; soft: string; pillBg: string; pillBd: string; pillTx: string }> = {
    good:    { fill: '#0F8A5F', stripe: '#0F8A5F', soft: 'rgba(15, 138, 95, 0.06)',  pillBg: 'rgba(15, 138, 95, 0.12)',  pillBd: 'rgba(15, 138, 95, 0.45)',  pillTx: '#0F6B49' },
    neutral: { fill: '#9A7B33', stripe: 'var(--civic-gold)', soft: 'rgba(var(--civic-gold-rgb), 0.10)', pillBg: 'rgba(var(--civic-gold-rgb), 0.18)', pillBd: 'rgba(var(--civic-gold-rgb), 0.65)', pillTx: 'var(--civic-gold-ink)' },
    bad:     { fill: 'var(--civic-red)', stripe: 'var(--civic-red)', soft: 'rgba(var(--civic-red-rgb), 0.06)',  pillBg: 'rgba(var(--civic-red-rgb), 0.12)',  pillBd: 'rgba(var(--civic-red-rgb), 0.55)',  pillTx: '#8B1828' },
    info:    { fill: 'var(--civic-navy)', stripe: 'var(--civic-navy)', soft: 'rgba(27, 79, 114, 0.06)',  pillBg: 'rgba(27, 79, 114, 0.10)',  pillBd: 'rgba(27, 79, 114, 0.40)',  pillTx: 'var(--civic-navy-deep)' },
    na:      { fill: '#64748B', stripe: '#94A3B8', soft: 'rgba(var(--civic-navy-deeper-rgb), 0.03)',    pillBg: 'rgba(var(--civic-navy-deeper-rgb), 0.06)',    pillBd: 'rgba(var(--civic-navy-deeper-rgb), 0.20)',    pillTx: '#475569' },
  };
  const c = (isDark ? tonesDark : tonesLight)[tone];
  const t = isDark ? {
    cardBg: 'linear-gradient(180deg, rgba(2, 22, 60, 0.92) 0%, rgba(0, 14, 44, 0.96) 100%)',
    cardBorder: 'rgba(var(--civic-gold-rgb), 0.18)',
    cardShadow: '0 1px 0 rgba(255,255,255,0.04) inset, 0 8px 20px -14px rgba(0,0,0,0.6)',
    nameText: 'var(--civic-parchment)',
    // 0.55 / 0.45 read as grey mush on the navy cards at 10–11px; these clear AA.
    mutedText: 'rgba(var(--civic-parchment-rgb), 0.72)',
    fadedText: 'rgba(var(--civic-parchment-rgb), 0.62)',
    divider: 'rgba(var(--civic-gold-rgb), 0.18)',
    ctaBg: 'rgba(var(--civic-gold-rgb),0.10)',
    ctaBd: 'rgba(var(--civic-gold-rgb),0.35)',
    ctaTx: 'var(--civic-gold)',
  } : {
    cardBg: 'linear-gradient(180deg, #FFFFFF 0%, #FBF7EE 100%)',
    cardBorder: 'rgba(var(--civic-navy-deeper-rgb), 0.12)',
    cardShadow: '0 1px 0 rgba(255,255,255,0.6) inset, 0 4px 14px -6px rgba(var(--civic-navy-deeper-rgb), 0.10)',
    nameText: 'var(--civic-navy-deeper)',
    mutedText: 'rgba(var(--civic-navy-deeper-rgb), 0.65)',
    fadedText: 'rgba(var(--civic-navy-deeper-rgb), 0.55)',
    divider: 'rgba(var(--civic-gold-rgb), 0.30)',
    ctaBg: 'rgba(var(--civic-gold-rgb),0.18)',
    ctaBd: 'rgba(154,123,51,0.45)',
    ctaTx: 'var(--civic-gold-ink)',
  };

  const inner = (
    <div
      className="relative h-full rounded-md p-4 pl-5 transition-shadow hover:shadow-[0_0_0_1px_rgba(var(--civic-gold-rgb),0.45),0_8px_24px_-12px_rgba(var(--civic-navy-deeper-rgb),0.18)]"
      style={{
        background: `${t.cardBg}, ${c.soft}`,
        backgroundBlendMode: 'normal, multiply',
        border: `1px solid ${t.cardBorder}`,
        boxShadow: t.cardShadow,
      }}
    >
      {/* Flag-stripe left accent */}
      <div
        className="absolute top-2 bottom-2 left-0 w-[3px] rounded-full"
        style={{ background: c.stripe, boxShadow: `0 0 8px ${c.stripe}55` }}
        aria-hidden
      />

      {/* Label + optional pill */}
      <div className="flex items-start justify-between gap-2 mb-1">
        <div className="text-[10px] uppercase tracking-[0.10em] font-bold" style={{ color: t.mutedText }}>
          {label}
        </div>
        {pill && (
          <span
            className="text-[9px] uppercase tracking-[0.08em] font-bold px-2 py-[2px] rounded-sm border whitespace-nowrap"
            style={{ background: c.pillBg, borderColor: c.pillBd, color: c.pillTx }}
          >
            {pill.text}
          </span>
        )}
      </div>

      {/* Big number */}
      <div className="flex items-baseline gap-2 flex-wrap mt-1">
        <span className="font-mono text-[32px] md:text-[40px] font-extrabold tabular-nums leading-none" style={{ color: c.fill, letterSpacing: '-0.02em' }}>
          {value}
        </span>
        {unit && (
          <span className="text-[12px] font-medium leading-tight" style={{ color: t.nameText }}>
            {unit}
          </span>
        )}
      </div>

      {/* Sub-line */}
      {sub && (
        <div className="text-[11px] mt-1.5" style={{ color: t.fadedText }}>
          {sub}
        </div>
      )}

      {/* CTA — only renders if `cta` text is provided */}
      {cta && (
        <div
          className="mt-3 inline-flex items-center gap-1 text-[10px] font-semibold rounded-sm px-2 py-1"
          style={{ color: t.ctaTx, background: t.ctaBg, border: `1px solid ${t.ctaBd}` }}
        >
          {cta}
          <ChevronRight className="w-3 h-3" />
        </div>
      )}
    </div>
  );

  if (to) {
    return <Link to={to} className="block h-full" onClick={onClick}>{inner}</Link>;
  }
  if (onClick) {
    return <button type="button" onClick={onClick} className="block h-full text-left w-full">{inner}</button>;
  }
  if (tooltip) {
    return (
      <TooltipProvider delayDuration={150}>
        <Tooltip>
          <TooltipTrigger asChild><div className="h-full">{inner}</div></TooltipTrigger>
          <TooltipContent side="bottom" className="max-w-[280px] text-xs leading-relaxed">{tooltip}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }
  return inner;
}

const HospitalProfile = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const brand = useBrand();
  const isAhp = brand.design === 'civic';
  const { isDark } = useTheme();
  const ahpHeaderOffset = useAhpHeaderOffset();
  const { t } = useTranslation(['hospital', 'common']);
  const [selectedTab, setSelectedTab] = useState('services');

  // Detect deep link to a specific tab via path, hash (#scenarios), or ?tab= query param.
  // The query-param form is used by external links (e.g., from /hospital-rankings Annual Revenue cells
  // → /hospital/{uuid}?tab=financial).
  useEffect(() => {
    const search = new URLSearchParams(location.search);
    const tabParam = search.get('tab');
    // 'financial' is an alias for 'profile' since they were merged into one "Financial & Ownership" tab.
    if (tabParam) {
      setSelectedTab(tabParam === 'financial' ? 'profile' : tabParam);
    } else if (location.pathname.endsWith('/chargemasters')) {
      setSelectedTab('chargemasters');
    } else if (location.pathname.endsWith('/financial')) {
      setSelectedTab('profile');
    } else if (location.pathname.endsWith('/quality')) {
      setSelectedTab('quality');
    } else if (location.pathname.endsWith('/scenarios')) {
      setSelectedTab('scenarios');
    } else if (location.pathname.endsWith('/nearby')) {
      setSelectedTab('nearby');
    } else if (location.hash === '#scenarios') {
      setSelectedTab('scenarios');
    } else if (location.hash === '#profile' || location.hash === '#financial') {
      setSelectedTab('profile');
    } else if (location.hash === '#services') {
      setSelectedTab('services');
    } else if (location.hash === '#quality') {
      setSelectedTab('quality');
    } else {
      setSelectedTab('services');
    }
  }, [location.pathname, location.hash, location.search]);

  // Load static per-hospital JSON (primary data source)
  const { data: staticData, isLoading: staticLoading } = useStaticHospitalData(id);

  // Dignity's written profile (hospital_profile_copy.template_md) rides in the
  // small SSR shard as profile_template; its {{tokens}} are filled here from
  // that same shard's numbers, so the words move with every rebuild and are
  // never static. Same helper the bot page uses (functions/lib/…).
  const { data: ssrShard } = useQuery({
    queryKey: ['hospital-ssr-shard', id],
    enabled: !!id,
    staleTime: 1000 * 60 * 60,
    queryFn: async () => { try { return await loadStaticData<ProfileShard>(`hospitals/${id}/ssr.json`); } catch { return null; } },
  });
  const profileHtml = useMemo(() => { const md = renderHospitalProfile(ssrShard); return md ? mdLite(md) : null; }, [ssrShard]);

  // Per-hospital Dignity briefing video → bottom-right PiP player (all tabs)
  // and the play slot on the Quality report card. Null until the video
  // pipeline emits hospital briefings (or `?briefing=` previews one).
  const briefingVideo = useHospitalBriefingVideo(staticData?.meta as any);
  const [briefingOpenRequest, setBriefingOpenRequest] = useState(0);

  // ─── Homepage demo bridge ──────────────────────────────────────────────
  // HomeBeta's "Today's 5 highest-marked-up hospitals" slide iframes this
  // page (?embed=1) as a scaled-down live preview and flips through the tabs
  // by posting:
  //   { type: "demo:tab",      tab: "<tab id>" }
  //   { type: "demo:hospital", hospitalId: "<uuid>", tab?: "<tab id>" }
  // We echo `{ type: "demo:selected", hospitalId, tab }` back to the parent
  // whenever the active tab changes, so its rail stays in sync with clicks
  // made inside the expanded preview. Only active when actually iframed —
  // the standalone route pays nothing for this.
  const isEmbeddedDemo = typeof window !== 'undefined' && window.parent !== window;
  const tabNavRef = useRef<HTMLElement | null>(null);
  useEffect(() => {
    if (!isEmbeddedDemo) return;
    const handler = (e: MessageEvent) => {
      const msg = e.data;
      if (!msg || typeof msg !== 'object' || typeof msg.type !== 'string') return;
      if (msg.type === 'demo:tab') {
        if (typeof msg.tab === 'string' && DEMO_TAB_IDS.has(msg.tab)) setSelectedTab(msg.tab);
      } else if (msg.type === 'demo:hospital') {
        if (typeof msg.hospitalId !== 'string' || !/^[0-9a-f-]{36}$/i.test(msg.hospitalId)) return;
        const tab = typeof msg.tab === 'string' && DEMO_TAB_IDS.has(msg.tab) ? msg.tab : 'services';
        // Set the tab in the same batch as the navigation so the parent sees
        // ONE demo:selected echo for the new hospital, not an intermediate
        // one carrying the previous hospital's tab.
        setSelectedTab(tab);
        if (msg.hospitalId !== id) {
          navigate(`/hospital/${msg.hospitalId}?embed=1&tab=${tab}`, { replace: true });
        }
      }
    };
    // Once the visitor clicks into the preview, keyboard focus is in this
    // document, so forward Esc for the parent's expanded-mode close.
    const keyHandler = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      try { window.parent.postMessage({ type: 'demo:escape' }, '*'); } catch { /* parent gone */ }
    };
    window.addEventListener('message', handler);
    document.addEventListener('keydown', keyHandler);
    return () => {
      window.removeEventListener('message', handler);
      document.removeEventListener('keydown', keyHandler);
    };
  }, [isEmbeddedDemo, id, navigate]);

  useEffect(() => {
    if (!isEmbeddedDemo) return;
    try {
      window.parent.postMessage({ type: 'demo:selected', hospitalId: id, tab: selectedTab }, '*');
    } catch {
      // Parent may have navigated away; the next change retries.
    }
  }, [isEmbeddedDemo, id, selectedTab]);

  // Fetch hospital data: static JSON first, Supabase fallback
  const { data: hospital, isLoading: hospitalLoading, error } = useQuery({
    queryKey: ['hospital', id, !!staticData],
    queryFn: async () => {
      if (!id) throw new Error('Hospital ID is required');

      // Use static meta if available
      if (staticData?.meta) {
        return staticData.meta as any;
      }

      const { data, error } = await supabase
        .from('hospitals')
        .select('*')
        .eq('id', id)
        .single();

      if (error) throw error;
      return data;
    },
    enabled: !!id,
  });

  // Canonical URL shape (2026-09-16): /hospital/:id/:slug[/tab]. Bare-id
  // URLs (old links, the rankings tables, drilldown chips) are replaced
  // in place with the slugged form so the address bar, history entries and
  // any copied link match the canonical. Skipped inside the homepage iframe,
  // which navigates by id on purpose. A stale slug is left alone — the
  // canonical <link> covers it.
  useEffect(() => {
    if (isEmbeddedDemo || !hospital?.name || !id) return;
    const slug = hospital.name.toLowerCase().replace(/[^\w\s-]/g, '').replace(/\s+/g, '-').replace(/--+/g, '-').slice(0, 100);
    if (!slug) return;
    const m = location.pathname.match(/^\/hospital\/([^/]+)(?:\/(.*))?$/);
    if (!m || m[1] !== id) return;
    const rest = m[2] || '';
    const TABS = ['quality', 'scenarios', 'financial', 'nearby', 'chargemasters'];
    if (rest !== '' && !TABS.includes(rest)) return; // already slugged
    const target = `/hospital/${id}/${slug}${rest ? `/${rest}` : ''}${location.search}${location.hash}`;
    navigate(target, { replace: true });
  }, [isEmbeddedDemo, hospital?.name, id, location.pathname, location.search, location.hash, navigate]);

  // (Homepage demo bridge, part 2 — lives below the hospital query because it
  // keys on hospitalLoading: the tab bar only mounts once the hospital loads.)
  // Keep the tab bar pinned to the top of the (scaled) preview so every flip
  // lands on section content rather than the hero. window.scrollTo, NOT
  // scrollIntoView: scrollIntoView also scrolls the PARENT document to reveal
  // the iframe, which would yank the homepage every few seconds. Re-runs
  // once the static JSON lands because the hero's stat cards shift layout.
  useEffect(() => {
    if (!isEmbeddedDemo) return;
    const scrollToNav = () => {
      const el = tabNavRef.current;
      if (!el) return;
      // Services (the tour's opening step for every hospital) keeps the hero
      // in frame — name, rank, outlier rate, grade — so the visitor sees who
      // they're looking at before the chargemaster; every other tab pins the
      // tab bar to the top to give the section the whole preview.
      const top = selectedTab === 'services' ? 0 : el.getBoundingClientRect().top + window.scrollY;
      window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
    };
    const raf = requestAnimationFrame(scrollToNav);
    const late = window.setTimeout(scrollToNav, 700);
    return () => { cancelAnimationFrame(raf); window.clearTimeout(late); };
  }, [isEmbeddedDemo, selectedTab, id, staticLoading, hospitalLoading]);

  // The hospital's own published price-transparency file URL + the vintage of
  // the data we actually display (data_vintage_year = year of the file our
  // current import came from — NOT latest_chargemaster_year, which tracks the
  // newest file merely discovered). Powers the source link + "as of" label.
  const { data: sourceInfo } = useQuery({
    queryKey: ['hospital-chargemaster-source', id],
    queryFn: async () => {
      if (!id) return null;
      const { data } = await supabase
        .from('hospitals')
        .select('chargemaster_url, data_vintage_year')
        .eq('id', id)
        .maybeSingle();
      return data ?? null;
    },
    enabled: !!id,
    staleTime: 1000 * 60 * 60,
  });
  const sourceUrl = sourceInfo?.chargemaster_url || null;
  const dataVintageYear = sourceInfo?.data_vintage_year || null;

  // Fetch hospital earnings data
  const { data: earnings } = useQuery({
    queryKey: ['hospital-earnings', id],
    queryFn: async () => {
      if (!id) return null;

      const { data, error } = await supabase
        .from('hospital_earnings')
        .select('*')
        .eq('hospital_id', id)
        .order('fiscal_year', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) throw error;
      return data;
    },
    enabled: !!id,
  });

  // Header preview: latest hospital_finances row (powers the Financial summary card)
  const { data: latestFinance } = useQuery({
    queryKey: ['hospital-finance-latest', id],
    enabled: !!id,
    staleTime: 1000 * 60 * 60,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('hospital_finances')
        .select('fiscal_year, total_revenue_usd, operating_margin_pct, charity_care_usd, medicare_ipps_payment_usd')
        .eq('hospital_id', id)
        .order('fiscal_year', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  });

  // Header preview: a single representative quality measure (powers the Quality summary card)
  const { data: qualityPreview } = useQuery({
    queryKey: ['hospital-quality-preview', id],
    enabled: !!id,
    staleTime: 1000 * 60 * 60,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('hospital_quality_measures')
        .select('measure_id, score, compared_to_national')
        .eq('hospital_id', id)
        .in('measure_id', ['MORT_30_HF', 'MORT_30_PN', 'MORT_30_AMI', 'READM_30_HOSP_WIDE'])
        .order('measure_id')
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  });

  // Fetch shame score: static JSON first, Supabase fallback
  const { data: shameData } = useQuery({
    queryKey: ['hospital-shame-score', id, !!staticData],
    queryFn: async () => {
      if (!id) return null;

      // Use static ranking data if available
      if (staticData?.ranking) {
        return staticData.ranking as any;
      }

      const { data, error } = await supabase
        .from('hospital_ranking_summary')
        .select(`
          shame_score_calculated,
          avg_gross_markup_percentage,
          median_gross_markup_percentage,
          total_procedures,
          outlier_expensive_count,
          cpt_avg_markup_percentage,
          hcpcs_avg_markup_percentage,
          drg_avg_markup_percentage,
          rev_avg_markup_percentage,
          ndc_avg_markup_percentage,
          drug_avg_markup_percentage,
          supply_avg_markup_percentage,
          cpt_procedure_count,
          hcpcs_procedure_count,
          drg_procedure_count,
          rev_procedure_count,
          ndc_procedure_count,
          drug_procedure_count,
          supply_procedure_count,
          overall_rank_gross
        `)
        .eq('hospital_id', id)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') throw error;
      return data;
    },
    enabled: !!id,
  });

  // Fetch total count of ranked hospitals
  // Ranked universe — ONLY rows that actually hold a rank. The unfiltered
  // count (~7,470) included excluded/unranked summaries, so the header said
  // "#4,877 of 7,470" while search said "of 4,877" for the same hospital —
  // it read as broken. Both surfaces now share the ~4,877 universe.
  const { data: rankedCounts } = useQuery({
    queryKey: ['total-ranked-hospitals-v2'],
    queryFn: async () => {
      const [ranked, all] = await Promise.all([
        supabase.from('hospital_ranking_summary').select('*', { count: 'exact', head: true }).not('overall_rank_gross', 'is', null),
        supabase.from('hospital_ranking_summary').select('*', { count: 'exact', head: true }),
      ]);
      return { ranked: ranked.count || 0, indexed: all.count || 0 };
    },
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });
  const totalRankedHospitals = rankedCounts?.ranked;
  const totalIndexedHospitals = rankedCounts?.indexed;

  // Fetch procedure stats: static JSON first, Supabase fallback
  const { data: procedureStats } = useQuery({
    queryKey: ['hospital-procedure-stats', id, !!staticData],
    queryFn: async () => {
      if (!id) return null;

      // Use static mappings_meta if available
      if (staticData?.mappings_meta) {
        return {
          totalCount: staticData.mappings_meta.total || 0,
          avgPrice: staticData.mappings_meta.avg_gross || 0,
          maxPrice: staticData.mappings_meta.max_gross || 0,
        };
      }

      const { data, error } = await supabase
        .rpc('calculate_hospital_averages', { p_hospital_id: id });

      if (error) throw error;

      if (!data || data.length === 0) {
        return { totalCount: 0, avgPrice: 0, maxPrice: 0 };
      }

      const result = data[0];
      return {
        totalCount: Number(result.total_count) || 0,
        avgPrice: Number(result.avg_gross_charge) || 0,
        maxPrice: Number(result.max_gross_charge) || 0
      };
    },
    enabled: !!id,
  });

  // Fetch code breakdown: static JSON first, Supabase fallback
  const { data: codeBreakdown } = useQuery({
    queryKey: ['hospital-code-breakdown', id, !!staticData],
    queryFn: async () => {
      if (!id) return { cpt: 0, drg: 0, revenue: 0, ndc: 0, other: 0, cpt_total: 0, drg_total: 0, revenue_total: 0, ndc_total: 0 };

      // Use static ranking data if available
      const r = staticData?.ranking;
      if (r) {
        return {
          cpt: r.cpt_procedure_count || 0,
          drg: r.drg_procedure_count || 0,
          revenue: r.rev_procedure_count || 0,
          ndc: r.ndc_procedure_count || 0,
          other: r.uncoded_procedures || 0,
          cpt_total: r.cpt_total_available || 0,
          drg_total: r.drg_total_available || 0,
          revenue_total: r.rev_total_available || 0,
          ndc_total: r.ndc_total_available || 0,
        };
      }

      const { data: rankingData, error } = await supabase
        .from('hospital_ranking_summary')
        .select(`
          cpt_procedure_count,
          drg_procedure_count,
          rev_procedure_count,
          ndc_procedure_count,
          cpt_total_available,
          drg_total_available,
          rev_total_available,
          ndc_total_available,
          uncoded_procedures
        `)
        .eq('hospital_id', id)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') {
        console.error('Error fetching code breakdown:', error);
      }

      if (rankingData) {
        return {
          cpt: rankingData.cpt_procedure_count || 0,
          drg: rankingData.drg_procedure_count || 0,
          revenue: rankingData.rev_procedure_count || 0,
          ndc: rankingData.ndc_procedure_count || 0,
          other: rankingData.uncoded_procedures || 0,
          cpt_total: rankingData.cpt_total_available || 0,
          drg_total: rankingData.drg_total_available || 0,
          revenue_total: rankingData.rev_total_available || 0,
          ndc_total: rankingData.ndc_total_available || 0,
        };
      }

      // Fallback: get counts from hospitals table
      const { data: hospitalData, error: hospitalError } = await supabase
        .from('hospitals')
        .select('cpt_count, drg_count, revenue_count, ndc_count, hcpcs_count, supply_count, total_procedures')
        .eq('id', id)
        .single();

      if (hospitalError || !hospitalData) {
        return { cpt: 0, drg: 0, revenue: 0, ndc: 0, other: 0, cpt_total: 0, drg_total: 0, revenue_total: 0, ndc_total: 0 };
      }

      const cpt = hospitalData.cpt_count || 0;
      const drg = hospitalData.drg_count || 0;
      const revenue = hospitalData.revenue_count || 0;
      const ndc = hospitalData.ndc_count || 0;
      const hcpcs = hospitalData.hcpcs_count || 0;
      const supply = hospitalData.supply_count || 0;
      const total = hospitalData.total_procedures || 0;
      const other = Math.max(0, total - cpt - drg - revenue - ndc - hcpcs - supply);

      return { cpt, drg, revenue, ndc, other, cpt_total: cpt, drg_total: drg, revenue_total: revenue, ndc_total: ndc };
    },
    enabled: !!id,
  });

  // Track hospital view (must be before conditional returns to satisfy Rules of Hooks)
  useEffect(() => {
    if (hospital?.id) trackHospitalView(hospital.id, hospital.name, hospital.state);
  }, [hospital?.id]);

  const getShameLevel = (score: number, isNationalComparison = false, useOutlierPercentage = false) => {
    if (useOutlierPercentage) {
      // Outlier percentage based shame score (more accurate)
      const outlierPercentage = shameData?.total_procedures && shameData?.outlier_expensive_count
        ? (shameData.outlier_expensive_count / shameData.total_procedures) * 100
        : 0;

      if (outlierPercentage >= 50) return { level: t('common:shameLevel.extremeCorruption'), color: "bg-destructive" };
      if (outlierPercentage >= 30) return { level: t('common:shameLevel.severeGouging'), color: "bg-orange-600" };
      if (outlierPercentage >= 15) return { level: t('common:shameLevel.highMarkup'), color: "bg-yellow-600" };
      if (outlierPercentage >= 5) return { level: t('common:shameLevel.moderatePricing'), color: "bg-blue-600" };
      return { level: t('common:shameLevel.excellentValue'), color: "bg-green-600" };
    } else if (!isNationalComparison) {
      // Raw shame score display (legacy)
      if (score >= 500) return { level: t('common:shameLevel.extremeCorruption'), color: "bg-destructive" };
      if (score >= 300) return { level: t('common:shameLevel.severeGouging'), color: "bg-warning-amber" };
      if (score >= 200) return { level: t('common:shameLevel.highMarkup'), color: "bg-primary" };
      if (score >= 100) return { level: t('common:shameLevel.moderate'), color: "bg-secondary" };
      if (score >= 0) return { level: t('common:shameLevel.fairPricing'), color: "bg-blue-600" };
      return { level: t('common:shameLevel.excellentValue'), color: "bg-green-600" };
    } else {
      // National comparison (based on ranking position)
      // NOTE: Lower rank number = WORSE hospital (more expensive)
      // Rank #1 = Most expensive hospital, Rank #N = Cheapest hospital (N = total ranked hospitals)
      const ranking = shameData?.overall_rank_gross || 0;
      const totalHospitals = totalRankedHospitals || 1; // Dynamic count from hospital_ranking_summary

      if (ranking === 0) return { level: t('common:shameLevel.notRanked'), color: "bg-muted" };

      const percentile = (ranking / totalHospitals) * 100;

      // Inverted logic: Lower rank = worse hospital
      if (percentile <= 10) return {
        level: ranking === 1 ? t('common:shameLevel.worstInNation') : t('common:shameLevel.top10Worst'),
        color: "bg-destructive"
      };
      if (percentile <= 25) return { level: t('common:shameLevel.top25Worst'), color: "bg-orange-500" };
      if (percentile <= 50) return { level: t('common:shameLevel.worseAvg'), color: "bg-yellow-500" };
      if (percentile <= 75) return { level: t('common:shameLevel.betterAvg'), color: "bg-blue-500" };
      if (percentile <= 90) return { level: t('common:shameLevel.top25Best'), color: "bg-green-500" };
      return { level: t('common:shameLevel.top10Best'), color: "bg-green-600" };
    }
  };

  if (hospitalLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
          <div className="text-lg text-muted-foreground">{t('hospital:loading')}</div>
        </div>
      </div>
    );
  }

  if (error || !hospital) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <AlertTriangle className="w-12 h-12 text-destructive mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-foreground mb-2">{t('hospital:error.title')}</h1>
          <p className="text-muted-foreground mb-4">
            {/* Never surface raw PostgREST errors ("JSON object requested,
                multiple (or no) rows returned") to visitors. */}
            {t('hospital:error.description')}
          </p>
          <Button onClick={() => navigate('/search')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            {t('hospital:error.goBack')}
          </Button>
        </div>
      </div>
    );
  }

  // Use real-time calculated shame score, fallback to static score, then 0
  const realShameScore = shameData?.shame_score_calculated || hospital.shame_score || 0;
  const hasRankingData = shameData?.overall_rank_gross > 0;
  const nationalComparison = getShameLevel(realShameScore, true, false);
  const rawScore = getShameLevel(realShameScore, false, true); // Use outlier percentage for raw score

  // Calculate outlier percentage for display
  const outlierPercentage = shameData?.total_procedures && shameData?.outlier_expensive_count
    ? ((shameData.outlier_expensive_count / shameData.total_procedures) * 100).toFixed(1)
    : '0.0';

  // Headline metric = median-baseline markup (what the rank is ordered by);
  // mean fallback until caches carry the field.
  const headlineMarkup = shameData?.median_gross_markup_percentage ?? shameData?.avg_gross_markup_percentage ?? null;
  const markupStr = headlineMarkup != null
    ? `${headlineMarkup >= 0 ? '+' : ''}${Number(headlineMarkup).toFixed(0)}% vs national median price`
    : null;
  const procedureStr = shameData?.total_procedures?.toLocaleString() || hospital.total_procedures?.toLocaleString();
  const rankStr = shameData?.overall_rank_gross && totalRankedHospitals ? `Ranked #${shameData.overall_rank_gross.toLocaleString()} of ${totalRankedHospitals.toLocaleString()} ranked` : null;

  const hospitalSeoDesc = [
    `Real chargemaster prices for ${hospital.name} in ${hospital.city}, ${hospital.state}.`,
    procedureStr ? `${procedureStr} procedures tracked.` : null,
    markupStr ? `Markup: ${markupStr}.` : null,
    rankStr ? `${rankStr} for price transparency.` : null,
    `Compare prices and see actual billing codes under the federal Price Transparency Rule.`
  ].filter(Boolean).join(' ');

  // Build slug-based canonical URL (matches sitemap + better for SEO keyword signals)
  const hospitalSlug = hospital.name
    ? hospital.name.toLowerCase().replace(/[^\w\s-]/g, '').replace(/\s+/g, '-').replace(/--+/g, '-').slice(0, 100)
    : '';
  const hospitalCanonical = hospitalSlug
    ? `${brand.primaryDomain}/hospital/${id}/${hospitalSlug}`
    : `${brand.primaryDomain}/hospital/${id}`;

  // ── One indexable URL per tab (2026-09-16) ─────────────────────────────
  // Hub = /hospital/:id/:slug (Services). Tab pages = hub + /quality,
  // /scenarios, /financial, /nearby (chargemasters too, but it canonicalizes
  // to the hub). Price analysis lives at hub?tab=corruption and also
  // canonicalizes to the hub. Each indexable tab gets its own title,
  // description, canonical and breadcrumb; the tab bar links to these paths.
  const hubPath = hospitalSlug ? `/hospital/${id}/${hospitalSlug}` : `/hospital/${id}`;
  const TAB_PATHS: Record<string, string> = {
    services: hubPath, quality: `${hubPath}/quality`, scenarios: `${hubPath}/scenarios`, profile: `${hubPath}/financial`,
    nearby: `${hubPath}/nearby`, chargemasters: `${hubPath}/chargemasters`, corruption: `${hubPath}?tab=corruption`,
  };
  const tabHref = (tabId: string) => TAB_PATHS[tabId] || hubPath;
  const INDEXABLE_TABS = ['services', 'quality', 'scenarios', 'profile', 'nearby'];
  const canonicalTab = INDEXABLE_TABS.includes(selectedTab) ? selectedTab : 'services';
  const tabCanonical = `${brand.primaryDomain}${TAB_PATHS[canonicalTab]}`;
  const displayName = titleCaseHospital(hospital.name) || hospital.name;
  const displayPlace = `${titleCaseHospital(hospital.city) || hospital.city}, ${hospital.state}`;
  const gradeLetter = headlineMarkup != null ? String(getHospitalGradeInfo(Number(headlineMarkup)).grade) : null;
  const cmsStarsNum = hospital.cms_overall_rating != null && Number.isFinite(Number(hospital.cms_overall_rating)) ? Number(hospital.cms_overall_rating) : null;
  const TAB_META: Record<string, { title: string; description: string }> = {
    services: {
      title: `${displayName} price list: ${procedureStr ? `${procedureStr} published prices, ` : ''}${gradeLetter ? `grade ${gradeLetter} · ` : ''}${displayPlace} | ${brand.name}`,
      description: hospitalSeoDesc,
    },
    quality: {
      title: `${displayName} quality ratings: ${cmsStarsNum != null ? `CMS ${cmsStarsNum}-star` : 'CMS rating'}, patient experience & infections · ${displayPlace} | ${brand.name}`,
      description: `${displayName}'s quality report card from CMS Care Compare and HCAHPS: ${cmsStarsNum != null ? `${cmsStarsNum} of 5 CMS stars` : 'CMS star rating'}, mortality, safety and readmission ratings, patient survey results, infection ratios and ER wait${gradeLetter ? ` — alongside its price grade ${gradeLetter}` : ''}.`,
    },
    scenarios: {
      title: `What care costs at ${displayName}: procedures & scenarios priced vs the U.S. average · ${displayPlace} | ${brand.name}`,
      description: `Complete-bill prices at ${displayName} for thousands of medical scenarios, built from its own published chargemaster, with how each ranks against every U.S. hospital that prices it and which nearby hospitals are cheaper.`,
    },
    profile: {
      title: `${displayName} finances: revenue, operating margin, charity care & executive pay · ${displayPlace} | ${brand.name}`,
      description: `${displayName}'s reported finances from CMS cost reports and IRS Form 990 filings — net patient revenue, operating margin, charity care, Medicare payments, executive compensation, ownership and health system.`,
    },
    nearby: {
      title: `Hospitals near ${displayName}: prices and ratings within 30 miles · ${displayPlace} | ${brand.name}`,
      description: `The hospitals closest to ${displayName} in ${displayPlace}, each with its price grade, markup vs the U.S. median, CMS stars and patient ratings — what the same care costs down the street.`,
    },
  };
  const tabMeta = TAB_META[canonicalTab] || TAB_META.services;
  const TAB_CRUMB: Record<string, string> = { quality: 'Quality report card', scenarios: 'Scenario pricing', profile: 'Financial & ownership', nearby: 'City view' };
  const breadcrumbJsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": `${brand.primaryDomain}/` },
      { "@type": "ListItem", "position": 2, "name": `${hospital.state} hospital rankings`, "item": `${brand.primaryDomain}/hospital-rankings/${String(hospital.state || '').toLowerCase()}` },
      { "@type": "ListItem", "position": 3, "name": displayName, "item": hospitalCanonical },
      ...(canonicalTab !== 'services' ? [{ "@type": "ListItem", "position": 4, "name": TAB_CRUMB[canonicalTab], "item": tabCanonical }] : []),
    ],
  };

  const hospitalJsonLd = {
    "@context": "https://schema.org",
    "@type": "Hospital",
    "name": hospital.name,
    "address": {
      "@type": "PostalAddress",
      "addressLocality": hospital.city,
      "addressRegion": hospital.state,
      "addressCountry": "US"
    },
    "url": hospitalCanonical,
    "sameAs": hospital.chargemaster_url ? [hospital.chargemaster_url] : undefined
  };

  // AHP dark mode theme tokens
  const ahpDark = isDark;
  const ahpPageBg = isAhp ? (ahpDark ? 'var(--civic-bg-dark)' : 'var(--civic-cream)') : undefined;
  const ahpCardBg = isAhp ? (ahpDark ? 'rgba(var(--civic-bg-dark-rgb),0.85)' : 'rgba(255,255,255,0.9)') : undefined;
  const ahpBorder = isAhp ? (ahpDark ? 'rgba(var(--civic-cream-rgb),0.12)' : 'rgba(var(--civic-ink-rgb),0.12)') : undefined;
  const ahpText = isAhp ? (ahpDark ? 'var(--civic-cream)' : 'var(--civic-ink)') : undefined;
  const ahpMuted = isAhp ? (ahpDark ? 'rgba(var(--civic-cream-rgb),0.55)' : 'rgba(var(--civic-ink-rgb),0.62)') : undefined;
  const ahpGold = 'var(--civic-gold)';
  const ahpAccent = isAhp ? ahpGold : undefined;

  return (
    <div className="min-h-screen bg-background" style={isAhp ? { backgroundColor: ahpPageBg } : undefined}>
      <Helmet>
        <title>{tabMeta.title}</title>
        <meta name="description" content={tabMeta.description} />
        <meta name="keywords" content={`${hospital.name} prices, ${hospital.name} chargemaster, ${hospital.city} hospital prices, ${hospital.state} hospital costs, hospital price transparency, chargemaster data`} />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={tabCanonical} />
        <meta property="og:title" content={tabMeta.title} />
        <meta property="og:description" content={tabMeta.description} />
        <meta property="og:image" content={brand.key === 'ahp' ? 'https://americanhospitalprices.com/consumer/og-hospital.png' : brand.social.ogImage} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={tabCanonical} />
        <meta property="og:site_name" content={brand.name} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={tabMeta.title} />
        <meta name="twitter:description" content={tabMeta.description} />
        <meta name="twitter:image" content={brand.key === 'ahp' ? 'https://americanhospitalprices.com/consumer/og-hospital.png' : brand.social.ogImage} />
        {brand.social.twitter && <meta name="twitter:site" content={brand.social.twitter} />}
        <script type="application/ld+json">{JSON.stringify(hospitalJsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbJsonLd)}</script>
      </Helmet>
      {/* Header */}
      <header
        className={`border-b ${isAhp ? '' : 'border-border bg-secondary/30'}`}
        style={isAhp ? { backgroundColor: ahpDark ? 'rgba(var(--civic-bg-dark-rgb),0.6)' : 'rgba(0,0,0,0.03)', borderColor: ahpBorder } : undefined}
      >
        <div className="mx-auto px-6 py-8">
          <div className="flex gap-2 mb-4">
            <Button variant="ghost" onClick={() => navigate('/search')}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              {t('hospital:navigation.backToSearch')}
            </Button>
            <Link to={`/map/${hospital.id}`}>
              <Button variant="outline">
                <MapIcon className="w-4 h-4 mr-2" />
                {t('hospital:navigation.viewOnMap')}
              </Button>
            </Link>
          </div>

          <div className="mb-6">
            <h1
              className={`text-4xl font-bold mb-3 ${isAhp ? (isDark ? 'text-white' : 'text-foreground') : 'text-terminal-green glitch'}`}
              data-text={titleCaseHospital(hospital.name)}
              style={isAhp ? { fontFamily: "'Instrument Serif', Georgia, serif", fontWeight: 400, letterSpacing: '-0.5px' } : undefined}
            >
              {titleCaseHospital(hospital.name)}
            </h1>
            <div className="flex items-center text-muted-foreground">
              <MapPin className="w-5 h-5 mr-2" />
              <span className={isAhp ? '' : 'font-mono'}>{titleCaseHospital(hospital.city)}, {hospital.state}</span>
            </div>
            {/* Source-verification link — every price on this page traces to the
                hospital's OWN published price-transparency file. Surfacing it
                lets anyone (esp. journalists) verify our numbers against the
                original filing, which is the site's entire premise. */}
            {(sourceUrl || hospital.chargemaster_url) && (
              <a
                href={sourceUrl || hospital.chargemaster_url}
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="inline-flex items-center gap-1.5 mt-2 text-sm hover:underline"
                style={{ color: isAhp ? (isDark ? 'var(--civic-navy-soft)' : 'var(--civic-navy)') : undefined }}
              >
                <FileText className="w-4 h-4" />
                {t('hospital:overview.viewSourceFile', { defaultValue: "View this hospital's published price file" })}
                <ExternalLink className="w-3.5 h-3.5" />
              </a>
            )}
            {/* Data-vintage disclosure — the year of the published file our
                current import came from. Honest per-hospital "as of" labeling:
                hospitals are re-ingested on a rolling basis as they republish,
                so vintages legitimately vary by hospital. */}
            {dataVintageYear && (
              <div className="text-xs text-muted-foreground mt-1">
                {t('hospital:overview.dataVintage', {
                  defaultValue: 'Prices from the hospital’s {{year}} published file · re-ingested on a rolling basis',
                  year: dataVintageYear,
                })}
              </div>
            )}
          </div>

          {/* Flat-rate filing annotation. Populated by scripts/compute_flat_rate_flags.py
              when one identical price covers >=50% of the hospital's file (a facility
              case rate, not the itemized gross charges 45 CFR 180.50 requires). The
              same threshold drops the hospital from the national per-code averages/
              medians in workflow/parallel_*_aggregation.sh; the hospital itself is
              still graded on every price it published. */}
          {hospital.flat_rate_share != null && Number(hospital.flat_rate_share) >= 0.5 && (
            <div className="mb-6 flex items-start gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0 text-amber-600 dark:text-amber-400" />
              <span className="text-muted-foreground">
                <strong className="text-foreground">{t('hospital:flatRate.label')}</strong>{' '}
                {t('hospital:flatRate.description', {
                  share: Math.round(Number(hospital.flat_rate_share) * 100),
                  rows: Number(hospital.flat_rate_rows || 0).toLocaleString(),
                  price: Number(hospital.flat_rate_price).toLocaleString(undefined, { maximumFractionDigits: 0 }),
                })}
                {/* The legal specification the file misses — cite the rule, not an opinion. */}
                <span className="block mt-1.5 text-xs leading-relaxed">
                  <Trans
                    i18nKey="hospital:flatRate.law"
                    components={[
                      <a
                        key="cfr"
                        href="https://www.ecfr.gov/current/title-45/section-180.50"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="underline underline-offset-2 text-foreground"
                      />,
                    ]}
                  />
                </span>
                {/* Data policy: flat-rate filings are excluded from the national
                    per-code averages/medians (workflow/parallel_*_aggregation.sh,
                    same hospitals.flat_rate_share >= 0.5 threshold that shows this
                    box) but the hospital is still ranked on every price it filed. */}
                <span className="block mt-1.5 text-xs leading-relaxed text-foreground">
                  {t('hospital:flatRate.policy')}
                </span>
              </span>
            </div>
          )}

          {/* 4-card preview grid — matches the Quality slider aesthetic.
              Tabs hidden behind these cards: pricing rank, outlier rate, financial summary, quality summary.
              Click-throughs jump to the relevant tab. */}
          {(() => {
            // ── Tone + values for each card ──────────────────────────────────────────
            const totalRanked = totalRankedHospitals || 1596;
            const rank = shameData?.overall_rank_gross || null;
            // Lower rank # = more expensive in the rankings system. Top 10% (red), top 50% (gold), bottom 50% (emerald).
            const rankTone: 'good' | 'neutral' | 'bad' | 'na' = rank == null
              ? 'na'
              : (rank <= totalRanked * 0.10 ? 'bad' : (rank <= totalRanked * 0.50 ? 'neutral' : 'good'));
            const rankPillText = rank == null ? t('hospital:overview.pillPending') :
              // AHP uses the factual "Top 10 Most Expensive"; Luigi keeps its
              // "Hall of Shame" brand label (softened accusatory framing on
              // named hospitals per Master 2026-07-13).
              (rank <= 10 ? t(isAhp ? 'hospital:overview.pillTop10Expensive' : 'hospital:overview.pillHallOfShame') :
              rank <= totalRanked * 0.10 ? t('hospital:overview.pillMostExpensive10') :
              rank <= totalRanked * 0.50 ? t('hospital:overview.pillMoreExpensive') :
              t('hospital:overview.pillLessExpensive'));

            const outlierNum = parseFloat(outlierPercentage);
            const outlierTone: 'good' | 'neutral' | 'bad' | 'na' = !isFinite(outlierNum)
              ? 'na'
              : (outlierNum >= 15 ? 'bad' : (outlierNum >= 5 ? 'neutral' : 'good'));

            // Financial summary — annual revenue + op margin from latest hospital_finances row,
            // fall back to hospital row's annual_revenue_usd if needed.
            const finRev = latestFinance?.total_revenue_usd != null
              ? Number(latestFinance.total_revenue_usd)
              : (hospital.annual_revenue_usd != null ? Number(hospital.annual_revenue_usd) : null);
            const finFy = latestFinance?.fiscal_year;
            const finMargin = latestFinance?.operating_margin_pct != null ? Number(latestFinance.operating_margin_pct) : null;
            const fmtRev = (v: number) => {
              if (!isFinite(v) || v <= 0) return '—';
              if (v >= 1e9) return `$${(v / 1e9).toFixed(v >= 1e10 ? 0 : 1)}B`;
              if (v >= 1e6) return `$${(v / 1e6).toFixed(v >= 1e8 ? 0 : 0)}M`;
              if (v >= 1e3) return `$${(v / 1e3).toFixed(0)}K`;
              return `$${v.toFixed(0)}`;
            };
            const finValue = finRev != null ? fmtRev(finRev) : '—';
            const finUnit = finRev != null
              ? (finFy ? t('hospital:overview.annualRevenueFy', { year: finFy }) : t('hospital:overview.annualRevenue'))
              : t('hospital:overview.noFilings');
            const finSub = finMargin != null
              ? t('hospital:overview.operatingMarginSub', { margin: `${finMargin >= 0 ? '+' : ''}${finMargin.toFixed(1)}` })
              : t('hospital:overview.financialSourcesSub');

            // Quality summary — CMS overall stars + Google reviews.
            const cmsStars = hospital.cms_overall_rating ?? null;
            const googleRating = hospital.google_rating ?? null;
            const googleReviews = hospital.google_review_count ?? null;
            const qualValue = cmsStars != null ? `${cmsStars}/5` : (googleRating != null ? `${googleRating}` : '—');
            const qualUnit = cmsStars != null ? t('hospital:overview.cmsStarsUnit') : (googleRating != null ? t('hospital:overview.googleRatingUnit') : t('hospital:overview.notRatedUnit'));
            const qualTone: 'good' | 'neutral' | 'bad' | 'na' = cmsStars == null
              ? 'na'
              : (cmsStars >= 4 ? 'good' : (cmsStars >= 3 ? 'neutral' : 'bad'));
            const qualSubBits: string[] = [];
            if (googleRating != null) qualSubBits.push(`${t('hospital:overview.googleSub', { rating: Number(googleRating).toFixed(1) })}${googleReviews ? ` · ${t('hospital:overview.reviewsCount', { value: Number(googleReviews).toLocaleString() })}` : ''}`);
            if (qualityPreview?.score != null) {
              qualSubBits.push(t('hospital:overview.mortalitySub', { score: Number(qualityPreview.score).toFixed(1) }));
            }
            const qualSub = qualSubBits.length > 0 ? qualSubBits.join(' · ') : t('hospital:overview.qualitySourcesSub');

            return (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
                {/* National pricing rank — shows per-code-type avg markup breakdown */}
                {(() => {
                  // Build breakdown chips: [CPT 1.2k codes · +85%] [HCPCS …] [DRG …] [REV …] [DRUG …] [SUPPLY …]
                  const fmtPct = (p: number | null | undefined) => {
                    if (p == null) return null;
                    const n = Number(p);
                    if (!isFinite(n)) return null;
                    return `${n >= 0 ? '+' : ''}${n.toFixed(0)}%`;
                  };
                  const fmtCount = (n: number | null | undefined) => {
                    if (n == null || !isFinite(Number(n))) return '0';
                    const v = Number(n);
                    if (v >= 1000) return `${(v / 1000).toFixed(v >= 10000 ? 0 : 1)}k`;
                    return v.toLocaleString();
                  };
                  type Bucket = { label: string; count: number | null; markup: number | null };
                  const buckets: Bucket[] = [
                    { label: 'CPT',    count: shameData?.cpt_procedure_count,    markup: shameData?.cpt_avg_markup_percentage    },
                    { label: 'HCPCS',  count: shameData?.hcpcs_procedure_count,  markup: shameData?.hcpcs_avg_markup_percentage  },
                    { label: 'DRG',    count: shameData?.drg_procedure_count,    markup: shameData?.drg_avg_markup_percentage    },
                    { label: 'REV',    count: shameData?.rev_procedure_count,    markup: shameData?.rev_avg_markup_percentage    },
                    { label: 'DRUG',   count: shameData?.drug_procedure_count ?? shameData?.ndc_procedure_count, markup: shameData?.drug_avg_markup_percentage ?? shameData?.ndc_avg_markup_percentage },
                    { label: 'SUPPLY', count: shameData?.supply_procedure_count, markup: shameData?.supply_avg_markup_percentage },
                  ].filter(b => (b.count ?? 0) > 0);

                  const breakdownNode = buckets.length > 0 ? (
                    <span className="block mt-1.5">
                      <span className="flex flex-wrap gap-x-2 gap-y-1 text-[10px] font-mono">
                        {buckets.map(b => {
                          const pct = fmtPct(b.markup);
                          if (!pct) return null;
                          // Color by tone — over national avg = red, under = green, near-zero = neutral
                          const tone = b.markup == null ? 'rgba(var(--civic-navy-deeper-rgb),0.55)'
                            : Number(b.markup) > 50 ? 'var(--civic-red)'
                            : Number(b.markup) > 0  ? '#9A7B33'
                            : '#0F8A5F';
                          const toneDk = b.markup == null ? 'rgba(var(--civic-parchment-rgb),0.55)'
                            : Number(b.markup) > 50 ? '#FCA5A5'
                            : Number(b.markup) > 0  ? 'var(--civic-parchment)'
                            : '#86efac';
                          return (
                            <span key={b.label} className="inline-flex items-baseline gap-0.5 whitespace-nowrap">
                              <span className="font-semibold" style={{ color: isDark ? 'var(--civic-parchment)' : 'var(--civic-navy-deeper)' }}>{b.label}</span>
                              <span className="opacity-60">{fmtCount(b.count)}</span>
                              <span className="font-bold" style={{ color: isDark ? toneDk : tone }}>{pct}</span>
                            </span>
                          );
                        })}
                      </span>
                    </span>
                  ) : null;

                  return (
                    <StatCard
                      isDark={isDark}
                      tone={rankTone}
                      label={<><span>{t('hospital:overview.rankLabel')}</span> <span className="opacity-50 font-normal normal-case tracking-normal text-[9px]">{t('hospital:overview.rankHint')}</span></>}
                      value={rank ? `#${rank.toLocaleString()}` : '—'}
                      unit={rank
                        ? `${t('hospital:overview.rankUnit', { total: totalRanked.toLocaleString() })}${rank > totalRanked * 0.5 ? t('hospital:overview.rankBestValue', { rank: (totalRanked - rank + 1).toLocaleString() }) : ''}`
                        : t('hospital:overview.pending')}
                      pill={{ text: rankPillText }}
                      sub={
                        <span>
                          <span>{hasRankingData ? t('hospital:overview.weightedMarkupSub') : t('hospital:overview.awaitingRanking')}</span>
                          {breakdownNode}
                        </span>
                      }
                      to={`/hospital-rankings/`}
                      tooltip={
                        <span>
                          <strong>How this is calculated:</strong> {rank ? `${rank.toLocaleString()} of ${totalRanked.toLocaleString()} ranked hospitals nationwide,` : 'A weighted ranking'} across every CPT, HCPCS, DRG, revenue, drug, and supply code this hospital has prices for — each price compared against the national median for that code (a baseline no single hospital's filing can skew), weighted by how often each code is used. Lower rank number = more expensive (#1 is the most expensive; #{totalRanked.toLocaleString()} is the best value). {totalIndexedHospitals && totalIndexedHospitals > totalRanked ? `${(totalIndexedHospitals - totalRanked).toLocaleString()} additional tracked hospitals aren't ranked — their published files have too few matching procedure codes, list only flat-rate or per-diem prices, or failed our data-quality audits.` : ''} The chips below show the average markup vs. national for each code type.
                        </span>
                      }
                    />
                  );
                })()}

                {/* Outlier rate — definition: priced >50% above national average for that billing code */}
                <StatCard
                  isDark={isDark}
                  tone={outlierTone}
                  label={t('hospital:overview.outlierLabel')}
                  value={`${outlierPercentage}%`}
                  unit={t('hospital:overview.outlierUnit')}
                  pill={{ text: rawScore.level }}
                  sub={shameData?.outlier_expensive_count ? t('hospital:overview.outlierSubCount', { value: Number(shameData.outlier_expensive_count).toLocaleString() }) : t('hospital:overview.outlierSubGeneric')}
                  onClick={() => setSelectedTab('corruption')}
                  tooltip={
                    <span>
                      <strong>What's an "outlier"?</strong> A procedure this hospital prices at <strong>more than 50% above</strong> the national average for that same billing code (i.e. 1.5× or higher). {shameData?.outlier_expensive_count ? `Found ${Number(shameData.outlier_expensive_count).toLocaleString()} of them in this hospital's chargemaster.` : ''} Click to see them all in the Price Analysis tab.
                    </span>
                  }
                />

                {/* Financial summary — proper URL deep-link */}
                <StatCard
                  isDark={isDark}
                  tone="info"
                  label={t('hospital:overview.financialLabel')}
                  value={finValue}
                  unit={finUnit}
                  sub={finSub}
                  cta={t('hospital:overview.seeFullFinancials')}
                  to={`/hospital/${hospital.id}/financial`}
                  tooltip={
                    <span>
                      <strong>What's in the Financial tab:</strong> revenue and operating margin from CMS Hospital Cost Reports (HCRIS), Schedule J executive compensation from IRS Form 990 filings, charity care, and direct links to the source PDFs we mirrored to our CDN.
                    </span>
                  }
                />

                {/* Quality summary — proper URL deep-link */}
                <StatCard
                  isDark={isDark}
                  tone={qualTone}
                  label={t('hospital:overview.qualityLabel')}
                  value={qualValue}
                  unit={qualUnit}
                  sub={qualSub}
                  cta={t('hospital:overview.seeFullQuality')}
                  to={`/hospital/${hospital.id}/quality`}
                  tooltip={
                    <span>
                      <strong>What's in the Quality tab:</strong> CMS Care Compare measures (30-day mortality, readmissions, hospital-acquired infections, patient experience) — each shown alongside the U.S. and {hospital.state ? (US_STATE_NAMES[hospital.state] || hospital.state) : 'state'} median so you can see how this hospital really compares.
                    </span>
                  }
                />
              </div>
            );
          })()}
        </div>
      </header>

      {/* Navigation Tabs */}
      <nav ref={tabNavRef} className={`border-b ${isAhp ? '' : 'border-border'}`} style={isAhp ? { borderColor: ahpBorder } : undefined}>
        <div className="mx-auto px-2 md:px-6 overflow-x-auto" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', WebkitOverflowScrolling: 'touch' }}>
          <div className="flex space-x-2 md:space-x-8 min-w-max">
            {[
              { id: 'profile', label: t('hospital:tabsExtra.financialOwnership'), icon: Banknote },
              { id: 'quality', label: t('hospital:tabsExtra.quality'), icon: Activity },
              { id: 'scenarios', label: t('hospital:tabsExtra.scenarioPricing'), icon: Stethoscope },
              { id: 'nearby', label: t('hospital:tabsExtra.cityView', { defaultValue: 'City view' }), icon: MapIcon },
              { id: 'services', label: t('hospital:tabs.services'), icon: Database },
              { id: 'corruption', label: isAhp ? t('hospital:tabs.priceAnalysis') : t('hospital:tabs.corruptionAnalysis'), icon: AlertTriangle },
              { id: 'chargemasters', label: t('hospital:tabs.chargemasters'), icon: FileText }
            ].map((tab) => (
              // Real links, one URL per tab, so crawlers (and the back button)
              // see each section as its own page. The location effect above
              // turns the path into the selected tab. The homepage demo iframe
              // keeps its ?embed=1 across tab clicks.
              <Link
                key={tab.id}
                to={isEmbeddedDemo ? `${tabHref(tab.id)}${tabHref(tab.id).includes('?') ? '&' : '?'}embed=1` : tabHref(tab.id)}
                aria-current={selectedTab === tab.id ? 'page' : undefined}
                onClick={() => { trackHospitalTabChange(id || '', tab.id); }}
                className={`flex items-center px-2 md:px-4 py-3 md:py-4 border-b-2 font-medium transition-colors text-sm md:text-base whitespace-nowrap ${
                  selectedTab === tab.id
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:text-foreground'
                }`}
              >
                <tab.icon className="w-4 h-4 mr-1.5 md:mr-2 flex-shrink-0" />
                {tab.label}
              </Link>
            ))}
          </div>
        </div>
      </nav>

      {/* Content. In the homepage demo iframe the tab content is often shorter
          than the viewport (loading states, small hospitals), which would stop
          the tab bar from ever reaching the top of the preview — so reserve a
          full viewport of height there. */}
      <main className={`mx-auto px-6 pt-12 ${selectedTab === 'nearby' ? 'pb-0' : 'pb-32'}`} style={isEmbeddedDemo ? { minHeight: '100vh' } : undefined}>

        {selectedTab === 'profile' && hospital && (
          <div className="space-y-8">
            <HospitalFinancialSection hospital={hospital} isAhp={isAhp} isDark={isDark} ahpCardBg={ahpCardBg} ahpBorder={ahpBorder} ahpText={ahpText} ahpMuted={ahpMuted} />
            <HospitalProfileSection hospitalId={hospital.id} isAhp={isAhp} isDark={isDark} ahpCardBg={ahpCardBg} ahpBorder={ahpBorder} ahpText={ahpText} ahpMuted={ahpMuted} ahpAccent={ahpAccent} />
          </div>
        )}

        {selectedTab === 'quality' && hospital && (
          <HospitalQualitySection
            hospital={hospital} isAhp={isAhp} isDark={isDark} ahpCardBg={ahpCardBg} ahpBorder={ahpBorder} ahpText={ahpText} ahpMuted={ahpMuted} ahpAccent={ahpAccent}
            priceMarkup={headlineMarkup != null ? Number(headlineMarkup) : null}
            priceRank={shameData?.overall_rank_gross ?? null}
            totalRanked={totalRankedHospitals ?? null}
            dataVintageYear={(hospital as any).data_vintage_year ?? (sourceInfo as any)?.data_vintage_year ?? null}
            briefing={briefingVideo}
            onPlayBriefing={() => setBriefingOpenRequest((n) => n + 1)}
          />
        )}

        {selectedTab === 'scenarios' && hospital && (
          <HospitalScenarioSection
            hospitalId={hospital.id} hospitalName={hospital.name} isAhp={isAhp} isDark={isDark}
            hospital={hospital}
            priceMarkup={headlineMarkup != null ? Number(headlineMarkup) : null}
            priceRank={shameData?.overall_rank_gross ?? null}
            totalRanked={totalRankedHospitals ?? null}
          />
        )}

        {selectedTab === 'nearby' && hospital && (
          <HospitalCityView
            hospital={hospital}
            isDark={isDark}
            priceMarkup={headlineMarkup != null ? Number(headlineMarkup) : null}
            priceRank={shameData?.overall_rank_gross ?? null}
            totalRanked={totalRankedHospitals ?? null}
          />
        )}

        {selectedTab === 'services' && (
          <div className="space-y-6">
            <div className="mb-8">
              <h2 className={`text-2xl font-bold mb-2 ${isAhp ? (isDark ? 'text-white' : 'text-foreground') : 'text-terminal-green'}`}
                style={isAhp ? { fontFamily: "'Instrument Serif', Georgia, serif", fontWeight: 400 } : undefined}
              >{t('hospital:sections.procedurePricingData')}</h2>
              <p className="text-muted-foreground">
                {t('hospital:sections.procedurePricingDescription', { hospitalName: hospital.name })}
              </p>
            </div>

            {/* Markup Overview */}
            <HospitalMarkupOverview
              markupData={shameData}
              hospitalName={hospital.name}
              totalCounts={codeBreakdown}
            />

            {/* About this hospital's prices — Dignity's profile, filled live (see ssrShard above) */}
            {profileHtml && (
              <section
                aria-label={t('hospital:dignityProfile.title', { defaultValue: "About this hospital's prices" })}
                className={`rounded-lg border p-5 md:p-6 ${isAhp ? '' : 'terminal-card'}`}
                style={isAhp ? { background: isDark ? 'rgba(2, 22, 60, 0.6)' : 'linear-gradient(180deg, #FFFFFF 0%, #FBF7EE 100%)', borderColor: isDark ? 'rgba(var(--civic-gold-rgb), 0.22)' : 'rgba(var(--civic-navy-deeper-rgb), 0.14)' } : undefined}
              >
                <h2 className={`text-2xl mb-3 ${isAhp ? (isDark ? 'text-white' : 'text-foreground') : 'text-terminal-green font-bold'}`}
                  style={isAhp ? { fontFamily: "'Instrument Serif', Georgia, serif", fontWeight: 400 } : undefined}
                >{t('hospital:dignityProfile.title', { defaultValue: "About this hospital's prices" })}</h2>
                <div className="hospital-profile-copy max-w-none text-[15px] leading-relaxed [&_p+p]:mt-3 [&_strong]:font-semibold [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_h3]:font-semibold [&_h3]:mt-4" dangerouslySetInnerHTML={{ __html: profileHtml }} />
                <p className="mt-3 text-[11px] text-muted-foreground/80">{t('hospital:dignityProfile.note', { defaultValue: "Written by Dignity from this hospital's own published price file and CMS data; every figure is filled in from today's data." })}</p>
              </section>
            )}

            <HospitalMappingsTable
              hospitalId={hospital.id}
              hospitalName={hospital.name}
              staticData={staticData || undefined}
              staticLoading={staticLoading}
            />
          </div>
        )}

        {selectedTab === 'corruption' && (
          <div className="space-y-8">
            {hasRankingData ? (
              // Show corruption analysis for hospitals with actual ranking data
              <Card className={`terminal-card p-8 ${isAhp ? '' : 'bg-gradient-corruption'}`}>
                <h2
                  className={`text-3xl font-bold mb-6 ${isAhp ? (isDark ? 'text-white' : 'text-foreground') : 'text-blood-red glitch'}`}
                  data-text={isAhp ? t('hospital:sections.priceAnalysisReport') : t('hospital:sections.corruptionAnalysisReport')}
                  style={isAhp ? { fontFamily: "'Instrument Serif', Georgia, serif", fontWeight: 400 } : undefined}
                >
                  {isAhp ? t('hospital:sections.priceAnalysisReport') : t('hospital:sections.corruptionAnalysisReport')}
                </h2>

                <div className="grid md:grid-cols-2 gap-8">
                  <div>
                    <h3 className={`text-xl font-bold mb-4 ${isAhp ? (isDark ? 'text-white' : 'text-foreground') : 'text-terminal-green'}`}>
                      {isAhp ? t('hospital:sections.pricingMetrics') : t('hospital:sections.financialExploitationMetrics')}
                    </h3>
                    <div className="space-y-4">
                      {/* Headline metric — median-baseline markup, the number the
                          national rank is ordered by. */}
                      <div className="flex justify-between p-3 bg-secondary rounded">
                        <span>{t('hospital:metrics.overallMarkupVsMedian', { defaultValue: 'Overall Markup (vs national median price)' })}</span>
                        <span className={`font-mono font-bold ${headlineMarkup != null && headlineMarkup < 0 ? 'text-green-400' : 'text-blood-red'}`}>
                          {headlineMarkup != null ?
                            `${headlineMarkup >= 0 ? '+' : ''}${Number(headlineMarkup).toFixed(0)}%` :
                            'N/A'}
                        </span>
                      </div>
                      {/* Mean-baseline markup kept as a secondary stat. */}
                      {shameData?.median_gross_markup_percentage != null && shameData?.avg_gross_markup_percentage != null && (
                        <div className="flex justify-between p-3 bg-secondary rounded">
                          <span>{t('hospital:metrics.overallAverageMarkup')}</span>
                          <span className={`font-mono font-bold ${shameData.avg_gross_markup_percentage < 0 ? 'text-green-400' : 'text-yellow-400'}`}>
                            {`${shameData.avg_gross_markup_percentage >= 0 ? '+' : ''}${shameData.avg_gross_markup_percentage.toFixed(0)}%`}
                          </span>
                        </div>
                      )}

                      {/* Category-specific markups */}
                      {shameData?.cpt_avg_markup_percentage !== null && shameData?.cpt_procedure_count > 0 && (
                        <div className="flex justify-between p-3 bg-secondary rounded">
                          <span>{t('hospital:metrics.cptVsNationalAvg')}</span>
                          <span className={`font-mono font-bold ${shameData.cpt_avg_markup_percentage < 0 ? 'text-green-400' : 'text-yellow-400'}`}>
                            {shameData.cpt_avg_markup_percentage >= 0 ? '+' : ''}{shameData.cpt_avg_markup_percentage.toFixed(0)}% {t('hospital:metrics.nationalAverage', { count: shameData.cpt_procedure_count.toLocaleString() })}
                          </span>
                        </div>
                      )}
                      {shameData?.drg_avg_markup_percentage !== null && shameData?.drg_procedure_count > 0 && (
                        <div className="flex justify-between p-3 bg-secondary rounded">
                          <span>{t('hospital:metrics.drgVsNationalAvg')}</span>
                          <span className={`font-mono font-bold ${shameData.drg_avg_markup_percentage < 0 ? 'text-green-400' : 'text-yellow-400'}`}>
                            {shameData.drg_avg_markup_percentage >= 0 ? '+' : ''}{shameData.drg_avg_markup_percentage.toFixed(0)}% {t('hospital:metrics.nationalAverage', { count: shameData.drg_procedure_count.toLocaleString() })}
                          </span>
                        </div>
                      )}
                      {shameData?.rev_avg_markup_percentage !== null && shameData?.rev_procedure_count > 0 && (
                        <div className="flex justify-between p-3 bg-secondary rounded">
                          <span>{t('hospital:metrics.revVsNationalAvg')}</span>
                          <span className={`font-mono font-bold ${shameData.rev_avg_markup_percentage < 0 ? 'text-green-400' : 'text-yellow-400'}`}>
                            {shameData.rev_avg_markup_percentage >= 0 ? '+' : ''}{shameData.rev_avg_markup_percentage.toFixed(0)}% {t('hospital:metrics.nationalAverage', { count: shameData.rev_procedure_count.toLocaleString() })}
                          </span>
                        </div>
                      )}
                      {shameData?.ndc_avg_markup_percentage !== null && shameData?.ndc_procedure_count > 0 && (
                        <div className="flex justify-between p-3 bg-secondary rounded">
                          <span>{t('hospital:metrics.ndcVsNationalAvg')}</span>
                          <span className={`font-mono font-bold ${shameData.ndc_avg_markup_percentage < 0 ? 'text-green-400' : 'text-yellow-400'}`}>
                            {shameData.ndc_avg_markup_percentage >= 0 ? '+' : ''}{shameData.ndc_avg_markup_percentage.toFixed(0)}% {t('hospital:metrics.nationalAverage', { count: shameData.ndc_procedure_count.toLocaleString() })}
                          </span>
                        </div>
                      )}

                      <div className="flex justify-between p-3 bg-secondary rounded">
                        <span>{t('hospital:metrics.totalProcedures')}</span>
                        <span className="font-mono text-primary font-bold">
                          {shameData?.total_procedures?.toLocaleString() || hospital.total_procedures?.toLocaleString() || 0}
                        </span>
                      </div>
                      <div className="flex justify-between p-3 bg-secondary rounded">
                        <span>{t('hospital:metrics.dataFilesImported')}</span>
                        <span className="font-mono text-terminal-green font-bold">
                          {hospital.total_chargemaster_files || 0}
                        </span>
                      </div>
                      {hospital.data_obfuscation_score && hospital.data_obfuscation_score > 0 && (
                        <div className="flex justify-between p-3 bg-secondary rounded">
                          <span>{t('hospital:metrics.dataObfuscationScore')}</span>
                          <span className="font-mono text-yellow-400 font-bold">
                            {hospital.data_obfuscation_score}/10
                          </span>
                        </div>
                      )}
                      <div className="flex justify-between p-3 bg-secondary rounded">
                        <span>{t('hospital:metrics.priceOutliers')}</span>
                        <span className="font-mono text-destructive font-bold">
                          {outlierPercentage}% ({shameData?.outlier_expensive_count || 0}/{shameData?.total_procedures || 0})
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Right column — chargemaster lines we couldn't fold into rankings.
                      Companion to the metrics on the left: the metrics summarize what we
                      WERE able to score, this surfaces what got dropped and why.
                      defaultExpanded since this is the only thing on the right side. */}
                  <div>
                    <HospitalExcludedCodesSection hospitalId={id || ''} defaultExpanded />
                  </div>
                </div>
              </Card>
            ) : (
              // Show placeholder for hospitals without analysis data
              <Card className="terminal-card p-8">
                <h2 className={`text-3xl font-bold mb-6 ${isAhp ? (isDark ? 'text-white' : 'text-foreground') : 'text-terminal-green'}`}
                  style={isAhp ? { fontFamily: "'Instrument Serif', Georgia, serif", fontWeight: 400 } : undefined}
                >
                  {isAhp ? t('hospital:tabs.priceAnalysis') : t('hospital:tabs.corruptionAnalysis')}
                </h2>

                <div className="text-center py-12">
                  <div className="text-6xl mb-6">📊</div>
                  <h3 className={`text-xl font-bold mb-4 ${isAhp ? (isDark ? 'text-white' : 'text-foreground') : 'text-terminal-green'}`}>
                    {t('hospital:analysisPending.title')}
                  </h3>
                  <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
                    {t('hospital:analysisPending.description')}
                  </p>

                  <div className="grid md:grid-cols-3 gap-4 max-w-3xl mx-auto">
                    <div className="p-4 bg-secondary rounded">
                      <div className="text-2xl font-mono font-bold text-muted-foreground mb-2">—</div>
                      <div className="text-sm text-muted-foreground">{t('hospital:analysisPending.rankingAnalysis')}</div>
                    </div>
                    <div className="p-4 bg-secondary rounded">
                      <div className="text-2xl font-mono font-bold text-muted-foreground mb-2">—</div>
                      <div className="text-sm text-muted-foreground">{t('hospital:analysisPending.comparativeMetrics')}</div>
                    </div>
                    <div className="p-4 bg-secondary rounded">
                      <div className="text-2xl font-mono font-bold text-muted-foreground mb-2">—</div>
                      <div className="text-sm text-muted-foreground">{t('hospital:analysisPending.obfuscationScore')}</div>
                    </div>
                  </div>

                  <p className={`text-sm mt-6 ${isAhp ? (isDark ? 'text-white' : 'text-foreground') : 'text-terminal-green font-mono'}`}>
                    {t('hospital:analysisPending.status')} {isAhp ? '·' : '•'} {t('hospital:analysisPending.rankingRequired')}
                  </p>
                </div>
              </Card>
            )}
          </div>
        )}

        {selectedTab === 'chargemasters' && (
          <HospitalChargemastersTab
            hospitalId={hospital.id}
            hospitalName={hospital.name}
            staticData={staticData || undefined}
            staticLoading={staticLoading}
            vintageYear={dataVintageYear}
          />
        )}
      </main>

      {/* Dignity's briefing — PiP dock above the tab bar, every tab. Hidden in
          the homepage demo iframe (the frame is scaled; the launcher would
          float over the wrong place). */}
      {briefingVideo && !isEmbeddedDemo && (
        <DignityBriefingPip
          hospitalId={hospital.id}
          hospitalName={titleCaseHospital(hospital.name) || hospital.name}
          video={briefingVideo}
          requestOpen={briefingOpenRequest}
          isDark={isDark}
        />
      )}
    </div>
  );
};

// ─── Hospital Scenario Section ────────────────────────────────────────

/** Convert HospitalScenarioRow → ScenarioCardData for the real ScenarioCard */
function toCardData(row: HospitalScenarioRow, dark = false): ScenarioCardData {
  const s = row.scenario;
  return {
    id: row.scenario_id,
    name: s.name,
    description: s.description || '',
    icon_name: s.icon_name || 'Activity',
    color: dark ? 'var(--civic-navy-soft)' : 'var(--civic-navy)',   // the card's icon; navy vanished on the dark card
    duration: s.duration || '',
    total_procedures: s.total_procedures || 0,
    phases: s.phases || [],
    severity_level: s.severity_level,
    calculated_avg_cost: row.nationalCost || s.estimated_cost || 0,
    phaseBreakdown: s.phaseBreakdown || {},
    phaseProcedures: {},
    tags: s.tags || [],
    created_by: null,
    is_verified: true,
  };
}

function toHospitalOverride(row: HospitalScenarioRow, hospitalId?: string) {
  return {
    blendedPrice: row.blendedPrice,
    blendedMarkup: row.blendedMarkup,
    matchedCodes: row.matched_codes,
    totalCodes: row.total_codes,
    nationalCost: row.nationalCost,
    hospitalId,
    spread: {
      p10: row.scenario.p10_cost ?? null,
      median: row.scenario.median_cost ?? null,
      avg: row.scenario.estimated_cost ?? null,
      p90: row.scenario.p90_cost ?? null,
    },
  };
}

type ScenarioSortKey = 'usa_rank_expensive' | 'usa_rank_cheap' | 'price_high' | 'price_low' | 'markup_high' | 'severity';

function HospitalScenarioSection({ hospitalId, hospitalName, isAhp, isDark, hospital, priceMarkup, priceRank, totalRanked }: {
  hospitalId: string;
  hospitalName: string;
  isAhp: boolean;
  isDark: boolean;
  // City panel inputs: coordinates + third-party ratings for the map, and
  // the price side of this hospital's grade.
  hospital: any;
  priceMarkup: number | null;
  priceRank: number | null;
  totalRanked: number | null;
}) {
  const { t } = useTranslation(['hospital', 'common']);
  const { scenarios, totalCount, isLoading } = useHospitalScenarios(hospitalId);
  const location = useLocation();

  // ── City panel ("Scenario & City view", 2026-09-15) ──────────────────────
  // The selected card drives the right-hand panel: this hospital's price for
  // that scenario against every reachable hospital's, ranked by what the
  // drive earns. Deep-linkable: ?scenario=<uuid>&radius=60.
  const cityLat = hospital?.latitude != null && Number.isFinite(Number(hospital.latitude)) ? Number(hospital.latitude) : null;
  const cityLng = hospital?.longitude != null && Number.isFinite(Number(hospital.longitude)) ? Number(hospital.longitude) : null;
  const { neighbours } = useHospitalNeighbours(hospitalId, cityLat, cityLng);
  const [panelScenarioId, setPanelScenarioId] = useState<string | null>(() => new URLSearchParams(location.search).get('scenario'));
  const [panelRadius, setPanelRadius] = useState<NeighbourRadius>(() => {
    const r = Number(new URLSearchParams(location.search).get('radius'));
    return (NEIGHBOUR_RADII as readonly number[]).includes(r) ? (r as NeighbourRadius) : 60;
  });
  const panelRef = useRef<HTMLDivElement>(null);
  const bandIds = useMemo(() => (neighbours || []).filter((n) => n.miles <= panelRadius).slice(0, 1000).map((n) => n.id), [neighbours, panelRadius]);
  const { markupOf: panelMarkupOf, rankOf: panelRankOf } = useNeighbourGrades(bandIds);
  // One scenario is "live" at a time: the row under the reading line (about
  // a third of the way down the viewport) as the visitor scrolls. Picking a
  // card or choosing in the panel PINS it until "follow scroll" is pressed,
  // so reading the panel never yanks the scenario away.
  const [pinned, setPinned] = useState<boolean>(() => !!new URLSearchParams(location.search).get('scenario'));
  const rowRefs = useRef(new Map<string, HTMLDivElement>());
  const spyTimer = useRef<number | null>(null);
  const openInPanel = (scenarioId: string) => {
    setPanelScenarioId(scenarioId);
    setPinned(true);
    // Below lg the panel sits under the list — bring it into view.
    if (typeof window !== 'undefined' && window.innerWidth < 1024) {
      window.setTimeout(() => panelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
    }
  };
  // Deep-link: ?q=foo prefill search; ?cat=heart-cardiovascular prefill category
  const initialSearch = new URLSearchParams(location.search).get('q') || '';
  const initialCat = new URLSearchParams(location.search).get('cat') || 'all';
  const [search, setSearch] = useState(initialSearch);
  const [activeCategory, setActiveCategory] = useState<string>(initialCat);
  const [sortKey, setSortKey] = useState<ScenarioSortKey>('usa_rank_expensive');
  const PAGE = 24;
  const [page, setPage] = useState(1);

  // React to URL changes (e.g. when StatCard CTAs hit ?q=...)
  useEffect(() => {
    const sp = new URLSearchParams(location.search);
    const q = sp.get('q'); if (q != null) setSearch(q);
    const c = sp.get('cat'); if (c != null) setActiveCategory(c);
    setPage(1);
  }, [location.search]);

  // Dark mode: the same palette the /scenarios cards use (Scenarios.tsx
  // SCENARIO_CARD_T_DARK) — these cards were light-only (Trent, 2026-09-27).
  const cardTheme: CardTheme = isDark ? {
    cardBg: 'var(--civic-navy-deeper)',
    border: 'rgba(var(--civic-cream-rgb),0.12)',
    shadow: 'rgba(0,0,0,0.35)',
    shadowTopHighlight: 'rgba(255,255,255,0.06)',
    red: 'var(--civic-red)',
    gold: 'var(--civic-gold)',
    green: '#4ade80',
    blue: '#5B9BD5',
    text: 'var(--civic-cream)',
    textMuted: 'rgba(var(--civic-cream-rgb),0.55)',
  } : {
    cardBg: '#ffffff',
    border: 'rgba(var(--civic-ink-rgb),0.12)',
    shadow: 'rgba(0,0,0,0.08)',
    red: 'var(--civic-red)',
    gold: 'var(--civic-gold)',
    green: 'var(--civic-money)',
    blue: 'var(--civic-navy)',
    text: 'var(--civic-ink)',
    textMuted: 'rgba(var(--civic-ink-rgb),0.62)',
  };

  // ── Family rollup ────────────────────────────────────────────────────────
  // Group all variants of a scenario family together so the headline shows the
  // full severity 0→4 cost range at this hospital, not just one variant in
  // isolation. A "family" represents one base condition (e.g. COPD) with
  // variants for increasing severity / complications.
  type FamilyRollup = {
    familyId: string;
    headName: string;             // base scenario name (the severity 0 row, or first if none)
    variants: HospitalScenarioRow[];
    minPrice: number;             // cheapest variant at this hospital
    maxPrice: number;             // priciest variant
    minSeverity: number | null;
    maxSeverity: number | null;
    /** The variant in this family with the worst (lowest #) USA rank for "expensive". */
    worstUsaRankRow: HospitalScenarioRow;
    /** The variant with the best (lowest #) USA rank for "cheap". */
    bestUsaRankRow: HospitalScenarioRow;
    /** Highest blended markup variant in family (chosen as representative for "biggest markup"). */
    biggestMarkupRow: HospitalScenarioRow;
  };
  const families = useMemo(() => {
    const map = new Map<string, HospitalScenarioRow[]>();
    for (const s of scenarios) {
      const fid = s.scenario.scenario_family_id;
      if (!fid) continue;
      const arr = map.get(fid) || [];
      arr.push(s);
      map.set(fid, arr);
    }
    const rollups: FamilyRollup[] = [];
    map.forEach((variants, familyId) => {
      if (variants.length === 0) return;
      const sortedBySev = [...variants].sort((a, b) => (a.scenario.severity_level ?? 99) - (b.scenario.severity_level ?? 99));
      // Family head = the "baseline" — typically severity 0. Fall back to the first by severity.
      const head = sortedBySev.find(v => (v.scenario.severity_level ?? -1) === 0) || sortedBySev[0];
      // The head's name minus the " — with X / requires Y" tail to get a clean family label.
      const headName = (head.scenario.name || '').split(/\s+—\s+/)[0].split(/\s+--\s+/)[0].trim() || head.scenario.name;

      const prices = variants.map(v => v.blendedPrice).filter(p => p > 0);
      const sevs = variants.map(v => v.scenario.severity_level).filter((s): s is number => s != null);
      const ranked = variants.filter(v => v.usa_rank_expensive != null && v.usa_total_ranked && v.usa_total_ranked >= 10);

      rollups.push({
        familyId,
        headName,
        variants: sortedBySev,
        minPrice: prices.length ? Math.min(...prices) : 0,
        maxPrice: prices.length ? Math.max(...prices) : 0,
        minSeverity: sevs.length ? Math.min(...sevs) : null,
        maxSeverity: sevs.length ? Math.max(...sevs) : null,
        worstUsaRankRow: ranked.length
          ? [...ranked].sort((a, b) => a.usa_rank_expensive! - b.usa_rank_expensive!)[0]
          : sortedBySev[sortedBySev.length - 1],
        bestUsaRankRow: ranked.length
          ? [...ranked].sort((a, b) => (a.usa_rank_cheap ?? 999999) - (b.usa_rank_cheap ?? 999999))[0]
          : head,
        biggestMarkupRow: [...variants].sort((a, b) => b.blendedMarkup - a.blendedMarkup)[0],
      });
    });
    return rollups;
  }, [scenarios]);

  // ── Headlines: family-rolled-up most-newsworthy USA ranks ────────────────
  const headlines = useMemo(() => {
    if (families.length === 0) {
      // No family metadata yet (older static cache). Fall back to single-scenario headlines.
      const ranked = scenarios.filter(s => s.usa_rank_expensive != null && s.usa_total_ranked && s.usa_total_ranked >= 10);
      if (ranked.length === 0) return null;
      const mostExp = [...ranked].sort((a, b) => a.usa_rank_expensive! - b.usa_rank_expensive!)[0];
      const cheap = [...ranked].sort((a, b) => a.usa_rank_cheap! - b.usa_rank_cheap!)[0];
      const markup = [...ranked].filter(s => s.blendedPrice >= 1000).sort((a, b) => b.blendedMarkup - a.blendedMarkup)[0] || null;
      return { mostExpensive: { row: mostExp, family: null as FamilyRollup | null }, cheapest: { row: cheap, family: null as FamilyRollup | null }, biggestMarkup: markup ? { row: markup, family: null as FamilyRollup | null } : null };
    }
    // Sort families by their best USA rank in each direction.
    const byMostExpUsa = [...families]
      .filter(f => f.worstUsaRankRow.usa_rank_expensive != null)
      .sort((a, b) => (a.worstUsaRankRow.usa_rank_expensive! - b.worstUsaRankRow.usa_rank_expensive!));
    const byCheapestUsa = [...families]
      .filter(f => f.bestUsaRankRow.usa_rank_cheap != null)
      .sort((a, b) => (a.bestUsaRankRow.usa_rank_cheap! - b.bestUsaRankRow.usa_rank_cheap!));
    const byBiggestMarkup = [...families]
      .filter(f => f.biggestMarkupRow.blendedPrice >= 1000)
      .sort((a, b) => b.biggestMarkupRow.blendedMarkup - a.biggestMarkupRow.blendedMarkup);

    return {
      mostExpensive: byMostExpUsa.length ? { row: byMostExpUsa[0].worstUsaRankRow, family: byMostExpUsa[0] } : null,
      cheapest: byCheapestUsa.length ? { row: byCheapestUsa[0].bestUsaRankRow, family: byCheapestUsa[0] } : null,
      biggestMarkup: byBiggestMarkup.length ? { row: byBiggestMarkup[0].biggestMarkupRow, family: byBiggestMarkup[0] } : null,
    };
  }, [families, scenarios]);

  // ── Filter + sort pipeline ───────────────────────────────────────────────
  const filtered = useMemo(() => {
    let rows = scenarios;
    if (search.trim()) {
      const q = search.toLowerCase();
      rows = rows.filter(r =>
        r.scenario.name.toLowerCase().includes(q) ||
        (r.scenario.category || '').toLowerCase().includes(q) ||
        (r.scenario.tags || []).some((t: string) => t.toLowerCase().includes(q))
      );
    }
    if (activeCategory !== 'all') {
      const cat = SCENARIO_CATEGORIES.find(c => c.key === activeCategory);
      if (cat) {
        rows = rows.filter(r => {
          if (scenarioMatchesCategory(r.scenario.tags || [], cat)) return true;
          const sc = r.scenario.category?.toLowerCase() || '';
          return sc === cat.browseLabel.toLowerCase() || sc.includes(cat.key);
        });
      }
    }
    const sorted = [...rows];
    switch (sortKey) {
      case 'usa_rank_expensive':
        // Worst USA rank first (1 = most expensive in USA). Nulls last.
        sorted.sort((a, b) => (a.usa_rank_expensive ?? 999999) - (b.usa_rank_expensive ?? 999999));
        break;
      case 'usa_rank_cheap':
        sorted.sort((a, b) => (a.usa_rank_cheap ?? 999999) - (b.usa_rank_cheap ?? 999999));
        break;
      case 'price_high':
        sorted.sort((a, b) => b.blendedPrice - a.blendedPrice);
        break;
      case 'price_low':
        sorted.sort((a, b) => a.blendedPrice - b.blendedPrice);
        break;
      case 'markup_high':
        sorted.sort((a, b) => b.blendedMarkup - a.blendedMarkup);
        break;
      case 'severity':
        sorted.sort((a, b) => (b.scenario.severity_level ?? -1) - (a.scenario.severity_level ?? -1));
        break;
    }
    return sorted;
  }, [scenarios, search, activeCategory, sortKey]);

  // Reset page when filter changes
  useEffect(() => { setPage(1); }, [search, activeCategory, sortKey]);

  // Scroll-spy for the City panel (declared after the filter state it keys on).
  useEffect(() => {
    if (typeof window === 'undefined' || window.innerWidth < 1024 || pinned) return;
    let raf = 0;
    const pick = () => {
      raf = 0;
      const line = window.innerHeight * 0.32;
      let best: { id: string; d: number } | null = null;
      rowRefs.current.forEach((el, id) => {
        const r = el.getBoundingClientRect();
        if (r.bottom < 0 || r.top > window.innerHeight) return;
        const d = r.top <= line && r.bottom >= line ? 0 : Math.min(Math.abs(r.top - line), Math.abs(r.bottom - line));
        if (!best || d < best.d) best = { id, d };
      });
      if (!best) return;
      const id = best.id;
      if (spyTimer.current) window.clearTimeout(spyTimer.current);
      // Settle for a beat so a fling doesn't fetch every scenario it passes.
      spyTimer.current = window.setTimeout(() => setPanelScenarioId((cur) => (cur === id ? cur : id)), 180);
    };
    const onScroll = () => { if (!raf) raf = window.requestAnimationFrame(pick); };
    window.addEventListener('scroll', onScroll, { passive: true });
    pick();
    return () => { window.removeEventListener('scroll', onScroll); if (raf) window.cancelAnimationFrame(raf); if (spyTimer.current) window.clearTimeout(spyTimer.current); };
    // Re-run when the visible page of rows changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pinned, page, search, activeCategory, sortKey]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }
  if (totalCount === 0) {
    return (
      <div className="text-center py-24">
        <Stethoscope className="w-12 h-12 mx-auto mb-4 text-muted-foreground/30" />
        <h3 className="text-lg font-semibold text-foreground mb-2">{t('hospital:scenarios.noPricingTitle')}</h3>
        <p className="text-muted-foreground">{t('hospital:scenarios.noPricingDescription')}</p>
      </div>
    );
  }

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE));
  const start = (page - 1) * PAGE;
  const visible = filtered.slice(start, start + PAGE);

  const renderCard = (row: HospitalScenarioRow, priceContext?: 'expensive' | 'deal') => (
    <div
      key={row.scenario_id}
      ref={(el) => { if (el) rowRefs.current.set(row.scenario_id, el); else rowRefs.current.delete(row.scenario_id); }}
      style={{
        display: 'flex', flexDirection: 'column', gap: 6, borderRadius: 12, padding: 4, margin: -4,
        boxShadow: panelScenarioId === row.scenario_id ? '0 0 0 2px var(--civic-gold), 0 8px 28px -14px rgba(0,0,0,0.35)' : 'none',
        transition: 'box-shadow 200ms ease',
      }}
    >
    <ScenarioCard
      scenario={toCardData(row, isDark)}
      compact
      isStarred={false}
      theme={cardTheme}
      onToggleBookmark={() => {}}
      hospitalOverride={{
        ...toHospitalOverride(row, hospitalId),
        dark: isDark,
        priceContext,
        usaRank: { expensive: row.usa_rank_expensive, cheap: row.usa_rank_cheap, total: row.usa_total_ranked, percentile: row.percentile_rank },
        localRank: {
          cheaper30: row.local_cheaper_30, nearby30: row.local_nearby_30,
          cheaper60: row.local_cheaper_60, nearby60: row.local_nearby_60,
          cheaper120: row.local_cheaper_120, nearby120: row.local_nearby_120,
          bestPrice: row.local_best_price, bestMiles: row.local_best_miles,
          cheaper300: row.local_cheaper_300, nearby300: row.local_nearby_300,
          bestPrice300: row.local_best_price_300, bestMiles300: row.local_best_miles_300,
        },
      }}
    />
    <button
      type="button"
      onClick={() => openInPanel(row.scenario_id)}
      aria-pressed={panelScenarioId === row.scenario_id}
      className="inline-flex items-center justify-center gap-1.5 transition-colors"
      style={{
        padding: '6px 10px', borderRadius: 6, fontSize: 11.5, fontWeight: 700, cursor: 'pointer',
        border: `1px solid ${panelScenarioId === row.scenario_id ? 'var(--civic-gold)' : (isDark ? 'rgba(var(--civic-gold-rgb),0.3)' : 'rgba(var(--civic-navy-rgb),0.2)')}`,
        background: panelScenarioId === row.scenario_id ? 'rgba(var(--civic-gold-rgb),0.18)' : 'transparent',
        color: isDark ? 'var(--civic-parchment)' : 'var(--civic-navy-deeper)',
      }}
    >
      <MapIcon className="w-3.5 h-3.5" /> {panelScenarioId === row.scenario_id ? (pinned ? t('hospital:cityView.panel.pinned', { defaultValue: 'Pinned on the map' }) : t('hospital:cityView.panel.live', { defaultValue: 'Live on the map' })) : t('hospital:cityView.panel.pin', { defaultValue: 'Pin on the map' })}
    </button>
    </div>
  );

  // ── Top-level category counts (for the pill bar) ─────────────────────────
  const categoryCounts: Record<string, number> = { all: scenarios.length };
  for (const cat of SCENARIO_CATEGORIES) {
    if (cat.key === 'all') continue;
    categoryCounts[cat.key] = scenarios.filter(r => {
      if (scenarioMatchesCategory(r.scenario.tags || [], cat)) return true;
      const sc = r.scenario.category?.toLowerCase() || '';
      return sc === cat.browseLabel.toLowerCase() || sc.includes(cat.key);
    }).length;
  }
  const visibleCats = SCENARIO_CATEGORIES.filter(c => c.key === 'all' || (categoryCounts[c.key] || 0) > 0);

  const panelCard: React.CSSProperties = isDark
    ? { background: 'linear-gradient(180deg, rgba(2, 22, 60, 0.94) 0%, rgba(0, 14, 44, 0.97) 100%)', border: '1px solid rgba(var(--civic-gold-rgb), 0.22)', borderRadius: 10, overflow: 'hidden' }
    : { background: 'linear-gradient(180deg, #FFFFFF 0%, #FBF7EE 100%)', border: '1px solid rgba(var(--civic-navy-deeper-rgb), 0.14)', borderRadius: 10, overflow: 'hidden' };
  const numOrNull = (v: unknown) => (v == null || !Number.isFinite(Number(v)) ? null : Number(v));

  return (
    <>
    <div className="space-y-6">
      <div>
        <h2 className={`text-2xl font-bold mb-2 ${isAhp ? (isDark ? 'text-white' : 'text-foreground') : 'text-terminal-green'}`}
          style={isAhp ? { fontFamily: "'Instrument Serif', Georgia, serif", fontWeight: 400 } : undefined}
        >
          {t('hospital:scenarios.heading')}
        </h2>
        <p className="text-sm text-muted-foreground">
          {t('hospital:scenarios.description', { value: totalCount.toLocaleString(), hospitalName })}
        </p>
      </div>

      {/* USA RANK HEADLINES — family-rolled-up.
          Each card represents a base condition (e.g. "COPD") and shows the full price range
          across severity 0-4 variants at this hospital — so consumers see the actual
          cost spread for that condition + all its complications. */}
      {headlines && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4">
          {/* Helper component inlined as a const-fn — renders the family range row */}
          {(() => {
            const fmtUsd = (n: number) => {
              if (n >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
              if (n >= 1e3) return `$${(n / 1e3).toFixed(n >= 1e4 ? 0 : 1)}k`;
              return `$${Math.round(n).toLocaleString()}`;
            };
            const familySub = (h: { row: HospitalScenarioRow; family: FamilyRollup | null }) => {
              const f = h.family;
              const r = h.row;
              return (
                <span className="block">
                  <span className="font-semibold" style={{ color: isDark ? 'var(--civic-parchment)' : 'var(--civic-navy-deeper)' }}>
                    {f?.headName || r.scenario.name}
                  </span>
                  {f && f.variants.length > 1 ? (
                    <span className="block mt-1 text-[11px] leading-snug">
                      <span className="font-mono">{fmtUsd(f.minPrice)} – {fmtUsd(f.maxPrice)}</span>{' '}
                      <span className="opacity-70">{t('hospital:scenarios.acrossSeverityLevels', { count: f.variants.length })}{f.minSeverity != null && f.maxSeverity != null && f.minSeverity !== f.maxSeverity ? ` (${f.minSeverity}–${f.maxSeverity})` : ''}</span>
                    </span>
                  ) : (
                    <span className="block mt-1 text-[11px]"><span className="font-mono">{fmtUsd(r.blendedPrice)}</span></span>
                  )}
                </span>
              );
            };
            const ctaTo = (h: { row: HospitalScenarioRow; family: FamilyRollup | null }) =>
              `/scenarios/${h.row.scenario_id}/${h.row.scenario.slug || ''}/hospital/${hospitalId}`;

            return (
              <>
                {headlines.mostExpensive && (
                  <StatCard
                    isDark={isDark}
                    tone="bad"
                    label={<><span>{t('hospital:scenarios.mostExpensiveLabel')}</span> <span className="opacity-50 font-normal normal-case tracking-normal text-[9px]">{t('hospital:scenarios.rankHintExpensive')}</span></>}
                    value={`#${headlines.mostExpensive.row.usa_rank_expensive!.toLocaleString()}`}
                    unit={t('hospital:scenarios.ofHospitals', { value: headlines.mostExpensive.row.usa_total_ranked!.toLocaleString() })}
                    pill={headlines.mostExpensive.row.usa_rank_expensive! <= 10 ? { text: t('hospital:scenarios.pillTop10Usa') } : undefined}
                    sub={familySub(headlines.mostExpensive)}
                    cta={t('hospital:scenarios.seeFamilyRange')}
                    to={ctaTo(headlines.mostExpensive)}
                    tooltip={
                      <span>
                        <strong>The condition where this hospital prices most aggressively vs. the rest of the country.</strong> The price range covers all severity levels (baseline → with complications) for this condition family at this hospital.
                      </span>
                    }
                  />
                )}
                {headlines.cheapest && (
                  <StatCard
                    isDark={isDark}
                    tone="good"
                    label={<><span>{t('hospital:scenarios.cheapestLabel')}</span> <span className="opacity-50 font-normal normal-case tracking-normal text-[9px]">{t('hospital:scenarios.rankHintCheapest')}</span></>}
                    value={`#${headlines.cheapest.row.usa_rank_cheap!.toLocaleString()}`}
                    unit={t('hospital:scenarios.ofHospitals', { value: headlines.cheapest.row.usa_total_ranked!.toLocaleString() })}
                    pill={headlines.cheapest.row.usa_rank_cheap! <= 10 ? { text: t('hospital:scenarios.pillTop10Cheapest') } : undefined}
                    sub={familySub(headlines.cheapest)}
                    cta={t('hospital:scenarios.seeFamilyRange')}
                    to={ctaTo(headlines.cheapest)}
                    tooltip={
                      <span>
                        <strong>The condition where this hospital comes in cheapest nationally.</strong> The price range covers all severity levels (baseline → with complications) at this hospital.
                      </span>
                    }
                  />
                )}
                {headlines.biggestMarkup && (
                  <StatCard
                    isDark={isDark}
                    tone="neutral"
                    label={t('hospital:scenarios.biggestMarkupLabel')}
                    value={`+${headlines.biggestMarkup.row.blendedMarkup.toFixed(0)}%`}
                    unit={t('hospital:scenarios.vsNationalAverage')}
                    sub={familySub(headlines.biggestMarkup)}
                    cta={t('hospital:scenarios.seeFamilyRange')}
                    to={ctaTo(headlines.biggestMarkup)}
                    tooltip={
                      <span>
                        The condition at this hospital with the largest percentage markup over the U.S. national average. Range shows all severity variants — from baseline to most-complicated.
                      </span>
                    }
                  />
                )}
              </>
            );
          })()}
        </div>
      )}

      {/* Search + Sort row */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder={t('hospital:scenarios.searchPlaceholder', { value: totalCount.toLocaleString() })}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <select
          value={sortKey}
          onChange={(e) => setSortKey(e.target.value as ScenarioSortKey)}
          className="text-sm rounded-md border px-3 py-2 bg-background"
          style={{ borderColor: isDark ? 'rgba(var(--civic-gold-rgb),0.30)' : 'rgba(var(--civic-navy-deeper-rgb),0.20)' }}
        >
          <option value="usa_rank_expensive">{t('hospital:scenarios.sortMostExpensive')}</option>
          <option value="usa_rank_cheap">{t('hospital:scenarios.sortCheapest')}</option>
          <option value="price_high">{t('hospital:scenarios.sortPriceHigh')}</option>
          <option value="price_low">{t('hospital:scenarios.sortPriceLow')}</option>
          <option value="markup_high">{t('hospital:scenarios.sortMarkup')}</option>
          <option value="severity">{t('hospital:scenarios.sortSeverity')}</option>
        </select>
        {(search || activeCategory !== 'all') && (
          <Button variant="outline" size="sm" onClick={() => { setSearch(''); setActiveCategory('all'); }}>
            {t('hospital:scenarios.clearFilters')}
          </Button>
        )}
      </div>

      {/* Category pill bar — horizontal scroll on small screens */}
      <div className="flex flex-wrap gap-1.5">
        <button
          type="button"
          onClick={() => setActiveCategory('all')}
          className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors`}
          style={{
            background: activeCategory === 'all' ? (isDark ? 'rgba(var(--civic-gold-rgb),0.20)' : 'rgba(var(--civic-gold-rgb),0.25)') : 'transparent',
            borderColor: activeCategory === 'all' ? 'var(--civic-gold)' : (isDark ? 'rgba(var(--civic-gold-rgb),0.30)' : 'rgba(var(--civic-navy-deeper-rgb),0.20)'),
            color: activeCategory === 'all' ? (isDark ? 'var(--civic-parchment)' : 'var(--civic-gold-ink)') : undefined,
          }}
        >
          {t('hospital:scenarios.all')} <span className="opacity-60 ml-1">{categoryCounts.all.toLocaleString()}</span>
        </button>
        {visibleCats.filter(c => c.key !== 'all').map(cat => (
          <button
            key={cat.key}
            type="button"
            onClick={() => setActiveCategory(cat.key)}
            className="text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors flex items-center gap-1.5"
            style={{
              background: activeCategory === cat.key ? `${cat.color}22` : 'transparent',
              borderColor: activeCategory === cat.key ? cat.color : (isDark ? 'rgba(var(--civic-gold-rgb),0.20)' : 'rgba(var(--civic-navy-deeper-rgb),0.15)'),
              color: activeCategory === cat.key ? cat.color : undefined,
            }}
          >
            <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: cat.color }} />
            {cat.browseLabel}
            <span className="opacity-60">{(categoryCounts[cat.key] || 0).toLocaleString()}</span>
          </button>
        ))}
      </div>

    </div>

    {/* ── Rows + City panel. The panel starts under the search/filters so the
        three USA-rank cards above keep the full width (Trent, 2026-09-16). */}
    <div className="lg:grid lg:grid-cols-[3fr_2fr] lg:gap-6 lg:items-start" style={{ marginTop: 24 }}>
    <div className="space-y-6 min-w-0">
      {/* Result count + pagination */}
      <div className="flex items-center justify-between text-sm text-muted-foreground">
        <span>
          {t('hospital:scenarios.scenarioCount', { count: filtered.length })}
          {filtered.length !== totalCount && t('hospital:scenarios.ofTotal', { total: totalCount.toLocaleString() })}
          {filtered.length > PAGE && ` · ${t('hospital:scenarios.pageOf', { page, total: totalPages })}`}
        </span>
        {totalPages > 1 && (
          <div className="flex gap-1">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => Math.max(1, p - 1))}>
              <ChevronRight className="w-3.5 h-3.5 rotate-180" />
            </Button>
            <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => Math.min(totalPages, p + 1))}>
              <ChevronRight className="w-3.5 h-3.5" />
            </Button>
          </div>
        )}
      </div>

      {/* Card grid — only the current page */}
      {visible.length > 0 ? (
        <div className="grid grid-cols-1 gap-4">
          {visible.map(row => {
            const ctx: 'expensive' | 'deal' | undefined =
              row.usa_rank_expensive != null && row.usa_total_ranked != null
                ? (row.usa_rank_expensive <= row.usa_total_ranked * 0.10 ? 'expensive' :
                   row.usa_rank_cheap != null && row.usa_rank_cheap <= row.usa_total_ranked * 0.10 ? 'deal' : undefined)
                : undefined;
            return renderCard(row, ctx);
          })}
        </div>
      ) : (
        <div className="text-center py-12 text-muted-foreground">
          {t('hospital:scenarios.noMatch')}{' '}
          <button onClick={() => { setSearch(''); setActiveCategory('all'); }} className="underline">{t('hospital:scenarios.clearFilters')}</button>
        </div>
      )}

      {/* Bottom pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 pt-4">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => Math.max(1, p - 1))}>
            <ChevronRight className="w-3.5 h-3.5 rotate-180 mr-1" /> {t('common:pagination.previous')}
          </Button>
          <span className="text-sm text-muted-foreground">{t('hospital:scenarios.pageOf', { page, total: totalPages })}</span>
          <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => Math.min(totalPages, p + 1))}>
            {t('common:pagination.next')} <ChevronRight className="w-3.5 h-3.5 ml-1" />
          </Button>
        </div>
      )}
    </div>

    {/* ── City panel: nearby prices for the selected scenario ─────────────
        Sticky beside the cards on desktop; below them on narrower screens
        (the card buttons scroll it into view). It pins 20px BELOW the fixed
        banner + header (--ahp-chrome-h, published by AhpGlobalHeader, so it
        follows the banner being dismissed): pinned at top-4 it slid ~90px
        under the header and hid its own title (Trent, 2026-09-26). */}
    <aside
      ref={panelRef}
      className="mt-8 lg:mt-0 lg:sticky lg:top-[calc(var(--ahp-chrome-h,108px)_+_20px)] lg:max-h-[calc(100vh_-_var(--ahp-chrome-h,108px)_-_20px_-_96px)] lg:overflow-y-auto"
      aria-label={t('hospital:cityView.panel.aria', { defaultValue: 'City panel — nearby prices for the selected scenario' })}
    >
      <div style={panelCard}>
        <div aria-hidden style={{ height: 4, background: 'linear-gradient(90deg, var(--civic-navy) 0 33%, var(--civic-parchment, #F4EEDC) 33% 66%, var(--civic-red) 66%)' }} />
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '8px 14px 0', fontSize: 11, color: isDark ? 'rgba(240,235,227,0.6)' : 'rgba(27,27,27,0.62)' }}>
          <span>{pinned ? t('hospital:cityView.panel.pinnedNote', { defaultValue: 'Pinned to the scenario you picked' }) : t('hospital:cityView.panel.followNote', { defaultValue: 'Follows the scenario you are reading' })}</span>
          {pinned && (
            <button type="button" onClick={() => setPinned(false)} style={{ border: 0, background: 'transparent', cursor: 'pointer', fontWeight: 800, color: isDark ? 'var(--civic-gold)' : 'var(--civic-gold-ink)', fontSize: 11, padding: 0 }}>
              {t('hospital:cityView.panel.follow', { defaultValue: 'Follow scroll' })}
            </button>
          )}
        </div>
        {cityLat != null && cityLng != null ? (
          <ScenarioDistanceTool
            hospital={{ id: hospitalId, name: hospitalName, city: hospital?.city ?? null, state: hospital?.state ?? null, lat: cityLat, lng: cityLng, cmsStars: numOrNull(hospital?.cms_overall_rating), googleRating: numOrNull(hospital?.google_rating), googleReviews: numOrNull(hospital?.google_review_count) }}
            priceMarkup={priceMarkup}
            priceRank={priceRank}
            totalRanked={totalRanked}
            isDark={isDark}
            scenarios={scenarios}
            scenariosLoading={isLoading}
            scenarioId={panelScenarioId}
            onScenarioIdChange={(id, source) => { setPanelScenarioId(id); if (source === 'user') setPinned(true); }}
            radius={panelRadius}
            onRadiusChange={setPanelRadius}
            neighbours={neighbours}
            markupOf={panelMarkupOf}
            rankOf={panelRankOf}
            showPicker
            showMap
            compact
          />
        ) : (
          <p className="text-sm text-muted-foreground" style={{ padding: 16 }}>{t('hospital:cityView.noCoords', { defaultValue: "We don't have coordinates for this hospital yet, so the city view can't be drawn." })}</p>
        )}
      </div>
    </aside>
    </div>
    </>
  );
}

// ─── Scenario Carousel Section with Progressive Loading ──────────────
function ScenarioCarouselSection({ title, titleColor, rows, renderCard }: {
  title: string;
  titleColor: string;
  rows: HospitalScenarioRow[];
  renderCard: (row: HospitalScenarioRow) => React.ReactNode;
}) {
  const PAGE_SIZE = 20;
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const visibleRows = rows.slice(0, visibleCount);
  const hasMore = visibleCount < rows.length;

  const [emblaRef, emblaApi] = useEmblaCarousel(
    { loop: false, dragFree: true, align: 'start', slidesToScroll: 1 },
    [Autoplay({ delay: 5000, stopOnInteraction: true, stopOnMouseEnter: true })]
  );
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(false);

  useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => {
      setCanPrev(emblaApi.canScrollPrev());
      setCanNext(emblaApi.canScrollNext());
      // Load more when scrolling near the end
      if (!emblaApi.canScrollNext() && hasMore) {
        setVisibleCount(c => Math.min(c + PAGE_SIZE, rows.length));
      }
    };
    emblaApi.on('select', onSelect);
    emblaApi.on('scroll', onSelect);
    emblaApi.on('reInit', onSelect);
    onSelect();
    return () => { emblaApi.off('select', onSelect); emblaApi.off('scroll', onSelect); emblaApi.off('reInit', onSelect); };
  }, [emblaApi, hasMore, rows.length]);

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <h3 className="text-lg font-semibold" style={{ color: titleColor }}>{title}</h3>
          <span className="text-sm text-muted-foreground">({rows.length})</span>
        </div>
        <div className="flex items-center gap-2">
          {hasMore && (
            <button
              onClick={() => setVisibleCount(c => Math.min(c + PAGE_SIZE, rows.length))}
              className="text-xs text-primary hover:underline"
            >
              Load more
            </button>
          )}
          <div className="flex gap-1">
            <button
              onClick={() => emblaApi?.scrollPrev()}
              disabled={!canPrev}
              className="w-7 h-7 rounded-full flex items-center justify-center border border-border/50 text-muted-foreground hover:text-foreground disabled:opacity-30 transition-opacity"
            >
              <ChevronRight className="w-4 h-4 rotate-180" />
            </button>
            <button
              onClick={() => emblaApi?.scrollNext()}
              disabled={!canNext}
              className="w-7 h-7 rounded-full flex items-center justify-center border border-border/50 text-muted-foreground hover:text-foreground disabled:opacity-30 transition-opacity"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
      <div ref={emblaRef} className="overflow-hidden">
        <div className="flex gap-4">
          {visibleRows.map((row) => (
            <div key={row.scenario_id} className="flex-[0_0_340px] min-w-0">
              {renderCard(row)}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// =====================================================================
// Profile & Ownership Tab
// Shows hospital system, ownership type, founded, beds, revenue,
// legal entity, joint-venture partners, Dignity-voice ownership
// narrative, and per-year HCRIS source documents on R2.
// =====================================================================
type HospitalRow = {
  id: string;
  name: string;
  city: string | null;
  state: string | null;
  hospital_type: string | null;
  ownership: string | null;
  bed_count: number | null;
  hospital_system: string | null;
  hospital_system_url: string | null;
  year_founded: number | null;
  ein: string | null;
  sec_cik: string | null;
  ccn: string | null;
  legal_entity_name: string | null;
  legal_entity_jurisdiction: string | null;
  legal_entity_id: string | null;
  legal_entity_filing_url: string | null;
  cms_ownership_url: string | null;
  joint_venture_partners: string | null;
  ownership_narrative: string | null;
  ownership_narrative_audio_url: string | null;
  annual_revenue_usd: number | null;
  revenue_year: number | null;
  revenue_source: string | null;
  revenue_source_url: string | null;
  profile_research_status: string | null;
  profile_research_notes: string | null;
};

type FinanceFile = {
  fiscal_year: number;
  document_type: string;
  source_org: string;
  file_size_bytes: number | null;
  r2_public_url: string | null;
  status: string;
  parsed_at: string | null;
};

function formatRevenue(usd: number | null | undefined): string {
  if (usd == null) return "—";
  if (usd >= 1_000_000_000) return `$${(usd / 1_000_000_000).toFixed(1)}B`;
  if (usd >= 1_000_000) return `$${(usd / 1_000_000).toFixed(0)}M`;
  return `$${usd.toLocaleString()}`;
}

function HospitalProfileSection({ hospitalId, isAhp, isDark, ahpCardBg, ahpBorder, ahpText, ahpMuted, ahpAccent }: {
  hospitalId: string;
  isAhp: boolean;
  isDark: boolean;
  ahpCardBg: string | undefined;
  ahpBorder: string | undefined;
  ahpText: string | undefined;
  ahpMuted: string | undefined;
  ahpAccent: string | undefined;
}) {
  const { t } = useTranslation(['hospital', 'common']);
  // Pull all ownership/profile fields directly from supabase to avoid stale static cache
  const { data: row, isLoading } = useQuery({
    queryKey: ["hospital-profile-row", hospitalId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("hospitals")
        .select(`id,name,city,state,hospital_type,ownership,bed_count,hospital_system,hospital_system_url,year_founded,ein,sec_cik,ccn,legal_entity_name,legal_entity_jurisdiction,legal_entity_id,legal_entity_filing_url,cms_ownership_url,joint_venture_partners,ownership_narrative,ownership_narrative_audio_url,annual_revenue_usd,revenue_year,revenue_source,revenue_source_url,profile_research_status,profile_research_notes`)
        .eq("id", hospitalId)
        .single();
      if (error) throw error;
      return data as HospitalRow;
    },
    enabled: !!hospitalId,
  });

  // Pull HCRIS + other finance source files mirrored to R2
  const { data: files } = useQuery({
    queryKey: ["hospital-finance-files", hospitalId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("hospital_finance_files")
        .select("fiscal_year,document_type,source_org,file_size_bytes,r2_public_url,status,parsed_at")
        .eq("hospital_id", hospitalId)
        .order("fiscal_year", { ascending: false });
      if (error) throw error;
      return (data || []) as FinanceFile[];
    },
    enabled: !!hospitalId,
  });

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-sm" style={isAhp ? { color: ahpMuted } : undefined}>
        <Loader2 className="w-4 h-4 animate-spin" /> {t('hospital:profile.loading')}
      </div>
    );
  }
  if (!row) return <div className="text-sm text-muted-foreground">{t('hospital:profile.notResearched')}</div>;

  const cardCls = `rounded-lg border p-5 ${isAhp ? "" : "bg-card border-border"}`;
  const cardStyle = isAhp ? { backgroundColor: ahpCardBg, borderColor: ahpBorder, color: ahpText } : undefined;
  const subtleText = isAhp ? { color: ahpMuted } : { className: "text-muted-foreground" };

  const statusUnresearched = !row.profile_research_status || row.profile_research_status === "unresearched";
  const statusInProgress = row.profile_research_status === "in_progress";
  const statusCompleted = row.profile_research_status === "completed";
  const statusUnavailable = row.profile_research_status === "data_unavailable";
  const statusFlagged = row.profile_research_status === "flagged_review";

  // Group files by document type
  const byDocType: Record<string, FinanceFile[]> = {};
  (files || []).forEach(f => { byDocType[f.document_type] ||= []; byDocType[f.document_type].push(f); });

  return (
    <div className="space-y-6">
      {/* === Status banner === */}
      {!statusCompleted && (
        <div className={cardCls} style={cardStyle}>
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: "#f59e0b" }} />
            <div className="flex-1 text-sm leading-relaxed">
              {statusUnresearched && <>{t('hospital:profile.statusUnresearchedPre')} <code className="px-1 py-0.5 rounded text-xs" style={isAhp ? { backgroundColor: ahpBorder } : undefined}>/research-hospital-profile</code> {t('hospital:profile.statusUnresearchedPost')}</>}
              {statusInProgress && <>{t('hospital:profile.statusInProgress')}</>}
              {statusFlagged && <>{t('hospital:profile.statusFlagged', { notes: row.profile_research_notes })}</>}
              {statusUnavailable && <>{t('hospital:profile.statusUnavailable', { notes: row.profile_research_notes })}</>}
            </div>
          </div>
        </div>
      )}

      {/* === Quick facts grid === */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <ProfileStat icon={<Building2 className="w-4 h-4" />} label={t('hospital:profile.parentSystem')} value={row.hospital_system || "—"} url={row.hospital_system_url} isAhp={isAhp} cardCls={cardCls} cardStyle={cardStyle} mutedStyle={isAhp ? { color: ahpMuted } : undefined} accent={ahpAccent}/>
        <ProfileStat icon={<Users className="w-4 h-4" />} label={t('hospital:profile.ownership')} value={row.ownership || "—"} subValue={row.hospital_type} isAhp={isAhp} cardCls={cardCls} cardStyle={cardStyle} mutedStyle={isAhp ? { color: ahpMuted } : undefined} accent={ahpAccent}/>
        <ProfileStat icon={<Calendar className="w-4 h-4" />} label={t('hospital:profile.founded')} value={row.year_founded ? String(row.year_founded) : "—"} subValue={row.year_founded ? t('hospital:profile.yearsOld', { count: new Date().getFullYear() - row.year_founded }) : null} isAhp={isAhp} cardCls={cardCls} cardStyle={cardStyle} mutedStyle={isAhp ? { color: ahpMuted } : undefined} accent={ahpAccent}/>
        <ProfileStat icon={<Activity className="w-4 h-4" />} label={t('hospital:profile.beds')} value={row.bed_count ? row.bed_count.toLocaleString() : "—"} isAhp={isAhp} cardCls={cardCls} cardStyle={cardStyle} mutedStyle={isAhp ? { color: ahpMuted } : undefined} accent={ahpAccent}/>
        <ProfileStat icon={<Banknote className="w-4 h-4" />} label={row.revenue_year ? t('hospital:profile.revenueWithYear', { year: row.revenue_year }) : t('hospital:profile.revenue')} value={formatRevenue(row.annual_revenue_usd)} subValue={row.revenue_source || null} url={row.revenue_source_url} isAhp={isAhp} cardCls={cardCls} cardStyle={cardStyle} mutedStyle={isAhp ? { color: ahpMuted } : undefined} accent={ahpAccent}/>
      </div>

      {/* === Dignity-voice ownership narrative === */}
      {row.ownership_narrative && (
        <div className={cardCls} style={cardStyle}>
          <div className="flex items-center gap-2 mb-3 text-xs uppercase tracking-wider font-semibold" style={{ color: ahpAccent || undefined }}>
            <Volume2 className="w-3.5 h-3.5" />
            {t('hospital:profile.ownershipReport')}
          </div>
          <div className="prose prose-sm max-w-none leading-relaxed" style={isAhp ? { color: ahpText } : undefined}>
            {row.ownership_narrative.split("\n\n").map((para, i) => (
              <p key={i} className="mb-3 last:mb-0" style={{ fontFamily: "'Instrument Serif', Georgia, serif", fontSize: '17px', lineHeight: '1.65' }}>{para}</p>
            ))}
          </div>
          {row.ownership_narrative_audio_url && (
            <audio controls src={row.ownership_narrative_audio_url} className="mt-4 w-full max-w-md" />
          )}
        </div>
      )}

      {/* === Legal entity + JV partners === */}
      {(row.legal_entity_name || row.joint_venture_partners) && (
        <div className={cardCls} style={cardStyle}>
          <h3 className="text-sm font-semibold uppercase tracking-wider mb-3" style={{ color: ahpAccent || undefined }}>{t('hospital:profile.legalEntityOwners')}</h3>
          <dl className="grid grid-cols-1 lg:grid-cols-2 gap-x-6 gap-y-2 text-sm">
            {row.legal_entity_name && (
              <>
                <dt style={isAhp ? { color: ahpMuted } : undefined}>{t('hospital:profile.operatingEntity')}</dt>
                <dd>{row.legal_entity_name}{row.legal_entity_jurisdiction && <span className="ml-2 text-xs" style={isAhp ? { color: ahpMuted } : undefined}>({row.legal_entity_jurisdiction})</span>}</dd>
              </>
            )}
            {row.legal_entity_filing_url && (
              <>
                <dt style={isAhp ? { color: ahpMuted } : undefined}>{t('hospital:profile.stateFilingRecord')}</dt>
                <dd><a href={row.legal_entity_filing_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 hover:underline" style={{ color: ahpAccent || undefined }}>{row.legal_entity_id || t('hospital:profile.view')} <ExternalLink className="w-3 h-3" /></a></dd>
              </>
            )}
            {row.ein && (
              <>
                <dt style={isAhp ? { color: ahpMuted } : undefined}>{t('hospital:profile.irsEin')}</dt>
                <dd className="font-mono">
                  <a href={`https://projects.propublica.org/nonprofits/organizations/${row.ein.replace("-", "")}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 hover:underline" style={{ color: ahpAccent || undefined }}>
                    {row.ein} <ExternalLink className="w-3 h-3" />
                  </a>
                </dd>
              </>
            )}
            {row.sec_cik && (
              <>
                <dt style={isAhp ? { color: ahpMuted } : undefined}>{t('hospital:profile.secCik')}</dt>
                <dd className="font-mono"><a href={`https://www.sec.gov/cgi-bin/browse-edgar?action=getcompany&CIK=${row.sec_cik}&type=10-K`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 hover:underline" style={{ color: ahpAccent || undefined }}>{row.sec_cik} <ExternalLink className="w-3 h-3" /></a></dd>
              </>
            )}
            {row.ccn && (
              <>
                <dt style={isAhp ? { color: ahpMuted } : undefined}>{t('hospital:profile.medicareCcn')}</dt>
                <dd className="font-mono"><a href={`https://www.medicare.gov/care-compare/details/hospital/${row.ccn}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 hover:underline" style={{ color: ahpAccent || undefined }}>{row.ccn} <ExternalLink className="w-3 h-3" /></a></dd>
              </>
            )}
            {row.cms_ownership_url && (
              <>
                <dt style={isAhp ? { color: ahpMuted } : undefined}>{t('hospital:profile.cmsOwnershipDisclosure')}</dt>
                <dd><a href={row.cms_ownership_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 hover:underline" style={{ color: ahpAccent || undefined }}>data.cms.gov <ExternalLink className="w-3 h-3" /></a></dd>
              </>
            )}
          </dl>
          {row.joint_venture_partners && (
            <div className="mt-4 pt-4 border-t" style={{ borderColor: ahpBorder }}>
              <div className="text-xs uppercase tracking-wider mb-1.5" style={isAhp ? { color: ahpMuted } : undefined}>{t('hospital:profile.ownersGte5')}</div>
              <p className="text-sm leading-relaxed">{row.joint_venture_partners}</p>
            </div>
          )}
        </div>
      )}

      {/* === Source documents (HCRIS year-by-year + others) === */}
      {(files && files.length > 0) && (
        <div className={cardCls} style={cardStyle}>
          <h3 className="text-sm font-semibold uppercase tracking-wider mb-3" style={{ color: ahpAccent || undefined }}>{t('hospital:profile.sourceDocuments')}</h3>
          <p className="text-sm mb-4" style={isAhp ? { color: ahpMuted } : undefined}>
            {t('hospital:profile.sourceDocumentsDescription')}
          </p>
          {Object.entries(byDocType).map(([docType, list]) => (
            <div key={docType} className="mb-4 last:mb-0">
              <div className="text-xs uppercase tracking-wider mb-2 font-semibold" style={isAhp ? { color: ahpMuted } : undefined}>
                {docType === "hcris_cost_report" ? t('hospital:profile.docTypes.hcris') :
                 docType === "irs_990" ? t('hospital:profile.docTypes.irs990') :
                 docType === "sec_10k" ? t('hospital:profile.docTypes.sec10k') :
                 docType === "state_audit" ? t('hospital:profile.docTypes.stateAudit') :
                 docType.replace(/_/g, " ")}
              </div>
              <div className="flex flex-wrap gap-2">
                {list.map(f => (
                  <a
                    key={`${f.document_type}_${f.fiscal_year}`}
                    href={f.r2_public_url || "#"}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-2 px-3 py-1.5 rounded border text-xs hover:opacity-80 transition-opacity"
                    style={isAhp ? { borderColor: ahpBorder, color: ahpText } : undefined}
                  >
                    <FileText className="w-3.5 h-3.5" />
                    <span className="font-mono">FY{f.fiscal_year}</span>
                    {f.file_size_bytes != null && (
                      <span className="text-xs opacity-50">{Math.round(f.file_size_bytes / 1024)}KB</span>
                    )}
                    <ExternalLink className="w-3 h-3 opacity-60" />
                  </a>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* === Research notes (caveats) === */}
      {row.profile_research_notes && (
        <div className={cardCls} style={{ ...cardStyle, opacity: 0.85 }}>
          <h3 className="text-xs uppercase tracking-wider mb-2 font-semibold" style={isAhp ? { color: ahpMuted } : undefined}>{t('hospital:profile.researchNotes')}</h3>
          <p className="text-sm leading-relaxed" style={isAhp ? { color: ahpMuted } : undefined}>{row.profile_research_notes}</p>
        </div>
      )}
    </div>
  );
}

function ProfileStat({ icon, label, value, subValue, url, isAhp, cardCls, cardStyle, mutedStyle, accent }: {
  icon: React.ReactNode;
  label: string;
  value: string;
  subValue?: string | null;
  url?: string | null;
  isAhp: boolean;
  cardCls: string;
  cardStyle: React.CSSProperties | undefined;
  mutedStyle: React.CSSProperties | undefined;
  accent: string | undefined;
}) {
  const inner = (
    <>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider" style={mutedStyle}>{icon} {label}</div>
      <div className="font-bold text-base mt-1.5">{value}</div>
      {subValue && <div className="text-xs mt-0.5" style={mutedStyle}>{subValue}</div>}
    </>
  );
  if (url) {
    return (
      <a href={url} target="_blank" rel="noreferrer" className={`${cardCls} hover:opacity-80 transition-opacity`} style={{ ...cardStyle, color: accent ? undefined : (cardStyle as any)?.color, display: 'block' }}>
        {inner}
        <ExternalLink className="w-3 h-3 mt-1 opacity-60" />
      </a>
    );
  }
  return <div className={cardCls} style={cardStyle}>{inner}</div>;
}

// Financial tab — surfaces HCRIS Form 2552 metrics (revenue / op margin / charity care / Medicare $)
// + provides direct links to source documents (IRS Form 990 via ProPublica, CMS Care Compare, HCRIS).
// Embedding the 990 PDF inline isn't reliable (ProPublica blocks X-Frame), so we link out and surface
// the underlying numbers we extracted from HCRIS so they're readable on-page.
function HospitalFinancialSection({ hospital, isAhp, isDark, ahpCardBg, ahpBorder, ahpText, ahpMuted }: {
  hospital: any;
  isAhp: boolean;
  isDark: boolean;
  ahpCardBg: string | undefined;
  ahpBorder: string | undefined;
  ahpText: string | undefined;
  ahpMuted: string | undefined;
}) {
  const { t } = useTranslation(['hospital', 'common']);
  // DB FALLBACK for IDs — only fires when the static per-hospital JSON cache hasn't yet been
  // regenerated to include them. Once `generate_hospital_detail_cache.py` bakes ein/ccn/system
  // into the meta blob, this query is `enabled: false` and never hits the network.
  const propIdsMissing = hospital.ein === undefined || hospital.ccn === undefined || hospital.hospital_system === undefined;
  const { data: dbIds } = useQuery({
    queryKey: ['hospital-ids', hospital.id],
    enabled: propIdsMissing,
    staleTime: 1000 * 60 * 60,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('hospitals')
        .select('id, ein, ccn, npi_number, hospital_system')
        .eq('id', hospital.id)
        .single();
      if (error) throw error;
      return data;
    },
  });
  // Merge: prefer prop value when present (no DB roundtrip needed), fall back to DB.
  const ein = hospital.ein ?? dbIds?.ein;
  const ccn = hospital.ccn ?? dbIds?.ccn;
  const hospitalSystem = hospital.hospital_system ?? dbIds?.hospital_system;

  // Fetch all HCRIS years for this hospital so we can show a year-over-year trend
  const { data: financeRows, isLoading } = useQuery({
    queryKey: ['hospital-finances', hospital.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('hospital_finances')
        .select('fiscal_year, net_patient_revenue_usd, total_revenue_usd, total_expenses_usd, net_income_usd, operating_margin_pct, charity_care_usd, bad_debt_only_usd, medicare_ipps_payment_usd, data_source, extracted_at')
        .eq('hospital_id', hospital.id)
        .order('fiscal_year', { ascending: false });
      if (error) throw error;
      return data || [];
    },
  });

  // Fetch IRS Form 990 data from our DB (hospital_990_filings table). Source: ingested from
  // ProPublica Nonprofit Explorer API (which itself sources from IRS e-File quarterly XML/PDF
  // dataset on AWS S3). We ingest into our DB because ProPublica's API doesn't return CORS headers
  // for browser fetches. Refresh cadence: scripts/ingest_propublica_990.py, run periodically.
  const einClean = ein ? String(ein).replace(/-/g, '') : null;
  const { data: filings990, isLoading: proPubLoading, error: proPubError } = useQuery({
    queryKey: ['hospital-990-filings', hospital.id],
    enabled: !!hospital.id,
    staleTime: 1000 * 60 * 60, // 1h
    refetchOnWindowFocus: false,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('hospital_990_filings')
        .select('*')
        .eq('hospital_id', hospital.id)
        .order('fiscal_year', { ascending: false });
      if (error) throw error;
      return data || [];
    },
  });
  // Schedule J Part II — named exec compensation, parsed from IRS 990 XML by extract_990_executives.py
  const { data: executives } = useQuery({
    queryKey: ['hospital-990-executives', hospital.id],
    enabled: !!hospital.id,
    staleTime: 1000 * 60 * 60,
    refetchOnWindowFocus: false,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('hospital_990_executives')
        .select('fiscal_year, person_name, title, base_comp_usd, bonus_usd, other_comp_usd, retirement_comp_usd, nontaxable_benefits_usd, total_comp_usd')
        .eq('hospital_id', hospital.id)
        .order('fiscal_year', { ascending: false })
        .order('total_comp_usd', { ascending: false });
      if (error) throw error;
      return data || [];
    },
  });
  // Group executives by fiscal_year (most recent year wins for primary display)
  const execByYear: Record<number, any[]> = {};
  (executives || []).forEach(e => { (execByYear[e.fiscal_year] ??= []).push(e); });
  const latestExecYear = Object.keys(execByYear).length ? Math.max(...Object.keys(execByYear).map(Number)) : null;
  const latestExecs = latestExecYear ? execByYear[latestExecYear] : [];

  // Shape the latest filing to match the structure the JSX expects (mapping snake_case DB cols
  // to the legacy ProPublica field names so the rest of the component doesn't change).
  const latestFiling = filings990 && filings990.length > 0 ? {
    tax_prd_yr: filings990[0].fiscal_year,
    totrevenue: filings990[0].total_revenue_usd,
    totfuncexpns: filings990[0].total_expenses_usd,
    totassetsend: filings990[0].total_assets_eoy_usd,
    totnetassetend: filings990[0].net_assets_eoy_usd,
    totliabend: filings990[0].total_liab_eoy_usd,
    totprgmrevnue: filings990[0].program_services_rev_usd,
    totcntrbgfts: filings990[0].contributions_gifts_usd,
    invstmntinc: filings990[0].investment_income_usd,
    royaltsinc: filings990[0].royalties_income_usd,
    netrntlinc: filings990[0].net_rental_income_usd,
    compnsatncurrofcr: filings990[0].officer_comp_usd,
    othrsalwages: filings990[0].other_salaries_usd,
    payrolltx: filings990[0].payroll_tax_usd,
    pct_compnsatncurrofcr: filings990[0].pct_comp_to_officers,
    txexmptbndsend: filings990[0].tax_exempt_bonds_eoy_usd,
    secrdmrtgsend: filings990[0].secured_mortgages_eoy_usd,
    unsecurednotesend: filings990[0].unsecured_notes_eoy_usd,
    pdf_url: filings990[0].pdf_url,
  } : null;
  const proPubData = filings990 && filings990.length > 0 ? {
    organization: {
      name: filings990[0].org_name,
      ntee_code: filings990[0].ntee_code,
      classification_codes: filings990[0].classification_codes,
      ruling_date: filings990[0].ruling_date,
      latest_object_id: filings990[0].irs_object_id,
    },
    filings_with_data: filings990.map(f => ({
      tax_prd_yr: f.fiscal_year,
      totrevenue: f.total_revenue_usd,
      totfuncexpns: f.total_expenses_usd,
      totassetsend: f.total_assets_eoy_usd,
      totnetassetend: f.net_assets_eoy_usd,
      pdf_url: f.pdf_url,
    })),
  } : null;

  // Pick the most recent year with non-null finances as the headline row.
  const latest = (financeRows || []).find(r => r.net_patient_revenue_usd != null || r.operating_margin_pct != null) || (financeRows || [])[0] || null;

  // EIN with no dashes for ProPublica URL (einClean already declared above)
  const proPubUrl = einClean ? `https://projects.propublica.org/nonprofits/organizations/${einClean}` : null;
  const careCompareUrl = ccn ? `https://www.medicare.gov/care-compare/details/hospital/${ccn}` : null;
  // CMS HCRIS public access portal — search by CCN for hospital cost reports
  const hcrisSearchUrl = ccn ? `https://www.cms.gov/data-research/statistics-trends-and-reports/cost-reports/hospital-2010-form` : null;

  const cardCls = `terminal-card p-6 ${isAhp ? '' : 'bg-gradient-corruption'}`;
  const cardStyle = isAhp ? { backgroundColor: ahpCardBg, borderColor: ahpBorder, color: ahpText } : undefined;

  return (
    <div className="space-y-6">
      <div className="mb-4">
        <h2 className={`text-2xl font-bold mb-2 ${isAhp ? (isDark ? 'text-white' : 'text-foreground') : 'text-terminal-green'}`}
            style={isAhp ? { fontFamily: "'Instrument Serif', Georgia, serif", fontWeight: 400 } : undefined}>
          {t('hospital:financial.heading')}
        </h2>
        <p className="text-sm text-muted-foreground">
          {hospital.name} — {t('hospital:financial.sourceBase')}{einClean ? t('hospital:financial.sourceIrs') : ''}{ccn ? t('hospital:financial.sourceCareCompare') : ''}.
        </p>
      </div>

      {/* Headline metrics from latest HCRIS year */}
      <Card className={cardCls} style={cardStyle}>
        <h3 className={`text-lg font-semibold mb-4 ${isAhp ? '' : 'text-terminal-green'}`}>
          {latest ? t('hospital:financial.latestYear', { year: latest.fiscal_year }) : t('hospital:financial.noHcris')}
        </h3>
        {isLoading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="w-4 h-4 animate-spin" /> {t('hospital:financial.loadingFinances')}
          </div>
        ) : latest ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <FinancialStat
              label={t('hospital:financial.netPatientRevenue')}
              value={latest.net_patient_revenue_usd != null ? formatRevenue(Number(latest.net_patient_revenue_usd)) : '—'}
              hint={t('hospital:financial.hintG3')}
              isAhp={isAhp} mutedStyle={isAhp ? { color: ahpMuted } : undefined}
            />
            <FinancialStat
              label={t('hospital:financial.operatingMargin')}
              value={latest.operating_margin_pct != null ? `${Number(latest.operating_margin_pct).toFixed(2)}%` : '—'}
              hint={t('hospital:financial.hintOpMargin')}
              valueColor={latest.operating_margin_pct != null && Number(latest.operating_margin_pct) < 0 ? '#f87171' : (latest.operating_margin_pct != null && Number(latest.operating_margin_pct) > 5 ? '#34d399' : undefined)}
              isAhp={isAhp} mutedStyle={isAhp ? { color: ahpMuted } : undefined}
            />
            <FinancialStat
              label={t('hospital:financial.charityCare')}
              value={latest.charity_care_usd != null ? formatRevenue(Number(latest.charity_care_usd)) : '—'}
              hint={t('hospital:financial.hintS10')}
              isAhp={isAhp} mutedStyle={isAhp ? { color: ahpMuted } : undefined}
            />
            <FinancialStat
              label={t('hospital:financial.medicareReceived')}
              value={latest.medicare_ipps_payment_usd != null ? formatRevenue(Number(latest.medicare_ipps_payment_usd)) : '—'}
              hint={t('hospital:financial.hintIpps')}
              isAhp={isAhp} mutedStyle={isAhp ? { color: ahpMuted } : undefined}
            />
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">{t('hospital:financial.noHcrisParsed')}</p>
        )}
      </Card>

      {/* IRS Form 990 — pulled live from ProPublica's free Nonprofit Explorer API (data sourced from
          IRS e-File quarterly XML/PDF dump on AWS S3). Skips render entirely if no EIN. */}
      {einClean && (
        <Card className={cardCls} style={cardStyle}>
          <div className="flex items-center justify-between mb-4 gap-4 flex-wrap">
            <h3 className={`text-lg font-semibold ${isAhp ? '' : 'text-terminal-green'}`}>
              {t('hospital:financial.irs990Heading')}
              {latestFiling?.tax_prd_yr && <span className="text-sm font-normal opacity-70 ml-2">FY{latestFiling.tax_prd_yr}</span>}
            </h3>
            <a
              href={proPubUrl!}
              target="_blank" rel="noopener noreferrer"
              className="text-xs flex items-center gap-1 underline hover:opacity-80"
            >
              {t('hospital:financial.proPublicaLink')} <ExternalLink className="w-3 h-3" />
            </a>
          </div>
          {proPubLoading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="w-4 h-4 animate-spin" /> {t('hospital:financial.loading990')}
            </div>
          ) : proPubError ? (
            <div className="text-sm text-muted-foreground">
              {t('hospital:financial.err990', { ein })}
            </div>
          ) : !latestFiling ? (
            <div className="text-sm text-muted-foreground">
              {t('hospital:financial.no990', { ein })}
            </div>
          ) : (
            <>
              {/* Headline P&L summary */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <FinancialStat
                  label={t('hospital:financial.totalRevenue')}
                  value={latestFiling.totrevenue != null ? formatRevenue(Number(latestFiling.totrevenue)) : '—'}
                  hint={t('hospital:financial.hint990Line12')}
                  isAhp={isAhp} mutedStyle={isAhp ? { color: ahpMuted } : undefined}
                />
                <FinancialStat
                  label={t('hospital:financial.totalExpenses')}
                  value={latestFiling.totfuncexpns != null ? formatRevenue(Number(latestFiling.totfuncexpns)) : '—'}
                  hint={t('hospital:financial.hint990Line18')}
                  isAhp={isAhp} mutedStyle={isAhp ? { color: ahpMuted } : undefined}
                />
                <FinancialStat
                  label={t('hospital:financial.totalAssets')}
                  value={latestFiling.totassetsend != null ? formatRevenue(Number(latestFiling.totassetsend)) : '—'}
                  hint={t('hospital:financial.hint990Line20')}
                  isAhp={isAhp} mutedStyle={isAhp ? { color: ahpMuted } : undefined}
                />
                <FinancialStat
                  label={t('hospital:financial.netAssets')}
                  value={latestFiling.totnetassetend != null ? formatRevenue(Number(latestFiling.totnetassetend)) : '—'}
                  hint={t('hospital:financial.hint990Line22')}
                  isAhp={isAhp} mutedStyle={isAhp ? { color: ahpMuted } : undefined}
                />
              </div>

              {/* Revenue mix */}
              {(latestFiling.totprgmrevnue != null || latestFiling.totcntrbgfts != null || latestFiling.invstmntinc != null || latestFiling.royaltsinc != null) && (
                <div className="mb-6">
                  <div className="text-xs uppercase tracking-wider opacity-70 mb-3 border-t pt-3" style={{ borderColor: ahpBorder }}>{t('hospital:financial.revenueMix')}</div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <FinancialStat label={t('hospital:financial.programServices')} value={latestFiling.totprgmrevnue != null ? formatRevenue(Number(latestFiling.totprgmrevnue)) : '—'} hint={t('hospital:financial.hintProgramServices')} isAhp={isAhp} mutedStyle={isAhp ? { color: ahpMuted } : undefined} />
                    <FinancialStat label={t('hospital:financial.contributions')} value={latestFiling.totcntrbgfts != null ? formatRevenue(Number(latestFiling.totcntrbgfts)) : '—'} hint={t('hospital:financial.hintContributions')} isAhp={isAhp} mutedStyle={isAhp ? { color: ahpMuted } : undefined} />
                    <FinancialStat label={t('hospital:financial.investmentIncome')} value={latestFiling.invstmntinc != null ? formatRevenue(Number(latestFiling.invstmntinc)) : '—'} hint={t('hospital:financial.hintInvestmentIncome')} isAhp={isAhp} mutedStyle={isAhp ? { color: ahpMuted } : undefined} />
                    <FinancialStat label={t('hospital:financial.royaltiesRent')} value={latestFiling.royaltsinc != null || latestFiling.netrntlinc != null ? formatRevenue((Number(latestFiling.royaltsinc) || 0) + (Number(latestFiling.netrntlinc) || 0)) : '—'} hint={t('hospital:financial.hintRoyaltiesRent')} isAhp={isAhp} mutedStyle={isAhp ? { color: ahpMuted } : undefined} />
                  </div>
                </div>
              )}

              {/* Compensation */}
              {(latestFiling.compnsatncurrofcr != null || latestFiling.othrsalwages != null) && (
                <div className="mb-6">
                  <div className="text-xs uppercase tracking-wider opacity-70 mb-3 border-t pt-3" style={{ borderColor: ahpBorder }}>{t('hospital:financial.compensation')}</div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <FinancialStat
                      label={t('hospital:financial.officerComp')}
                      value={latestFiling.compnsatncurrofcr != null ? formatRevenue(Number(latestFiling.compnsatncurrofcr)) : '—'}
                      hint={t('hospital:financial.hintOfficerComp')}
                      valueColor={latestFiling.compnsatncurrofcr != null && Number(latestFiling.compnsatncurrofcr) > 5_000_000 ? '#fbbf24' : undefined}
                      isAhp={isAhp} mutedStyle={isAhp ? { color: ahpMuted } : undefined}
                    />
                    <FinancialStat label={t('hospital:financial.otherSalaries')} value={latestFiling.othrsalwages != null ? formatRevenue(Number(latestFiling.othrsalwages)) : '—'} hint={t('hospital:financial.hintOtherSalaries')} isAhp={isAhp} mutedStyle={isAhp ? { color: ahpMuted } : undefined} />
                    <FinancialStat label={t('hospital:financial.payrollTaxes')} value={latestFiling.payrolltx != null ? formatRevenue(Number(latestFiling.payrolltx)) : '—'} hint={t('hospital:financial.hintPayrollTaxes')} isAhp={isAhp} mutedStyle={isAhp ? { color: ahpMuted } : undefined} />
                    <FinancialStat
                      label={t('hospital:financial.pctCompOfficers')}
                      value={latestFiling.pct_compnsatncurrofcr != null ? `${Number(latestFiling.pct_compnsatncurrofcr).toFixed(2)}%` : '—'}
                      hint={t('hospital:financial.hintPctCompOfficers')}
                      isAhp={isAhp} mutedStyle={isAhp ? { color: ahpMuted } : undefined}
                    />
                  </div>
                  <div className="text-[11px] opacity-60 mt-2">
                    Individual exec names + salaries are in <strong>Schedule J Part II</strong> of the 990 PDF (download below). Coming next: structured ingest of 990 XML so they appear here directly.
                  </div>
                </div>
              )}

              {/* Balance sheet detail */}
              {(latestFiling.totliabend != null || latestFiling.txexmptbndsend != null || latestFiling.unsecurednotesend != null) && (
                <div className="mb-6">
                  <div className="text-xs uppercase tracking-wider opacity-70 mb-3 border-t pt-3" style={{ borderColor: ahpBorder }}>{t('hospital:financial.balanceSheet')}</div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <FinancialStat label={t('hospital:financial.totalLiabilities')} value={latestFiling.totliabend != null ? formatRevenue(Number(latestFiling.totliabend)) : '—'} hint={t('hospital:financial.hintTotalLiabilities')} isAhp={isAhp} mutedStyle={isAhp ? { color: ahpMuted } : undefined} />
                    <FinancialStat label={t('hospital:financial.taxExemptBonds')} value={latestFiling.txexmptbndsend != null ? formatRevenue(Number(latestFiling.txexmptbndsend)) : '—'} hint={t('hospital:financial.hintTaxExemptBonds')} isAhp={isAhp} mutedStyle={isAhp ? { color: ahpMuted } : undefined} />
                    <FinancialStat label={t('hospital:financial.securedMortgages')} value={latestFiling.secrdmrtgsend != null ? formatRevenue(Number(latestFiling.secrdmrtgsend)) : '—'} hint={t('hospital:financial.hintSecuredMortgages')} isAhp={isAhp} mutedStyle={isAhp ? { color: ahpMuted } : undefined} />
                    <FinancialStat label={t('hospital:financial.unsecuredNotes')} value={latestFiling.unsecurednotesend != null ? formatRevenue(Number(latestFiling.unsecurednotesend)) : '—'} hint={t('hospital:financial.hintUnsecuredNotes')} isAhp={isAhp} mutedStyle={isAhp ? { color: ahpMuted } : undefined} />
                  </div>
                </div>
              )}

              {/* Org metadata + PDF + raw XML */}
              <div className="text-xs text-muted-foreground space-y-1 border-t pt-3" style={{ borderColor: ahpBorder }}>
                {proPubData?.organization?.name && <div>{t('hospital:financial.filingEntity')} <strong>{proPubData.organization.name}</strong></div>}
                {proPubData?.organization?.ntee_code && <div>{t('hospital:financial.nteeCode')} {proPubData.organization.ntee_code} ({proPubData.organization.classification_codes || ''})</div>}
                {proPubData?.organization?.ruling_date && <div>{t('hospital:financial.rulingDate')} {String(proPubData.organization.ruling_date).slice(0, 10)}</div>}
                <div className="flex flex-wrap gap-2 pt-2">
                  {latestFiling.pdf_url && (
                    <a
                      href={latestFiling.pdf_url}
                      target="_blank" rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 px-3 py-1.5 rounded border hover:bg-white/5 transition-colors text-xs font-medium"
                      style={{ borderColor: ahpBorder }}
                    >
                      <FileText className="w-3.5 h-3.5" />
                      {t('hospital:financial.originalPdf', { year: latestFiling.tax_prd_yr })}
                      <ExternalLink className="w-3 h-3 opacity-60" />
                    </a>
                  )}
                  {proPubData?.organization?.latest_object_id && (
                    <a
                      href={`https://s3.amazonaws.com/irs-form-990/${proPubData.organization.latest_object_id}_public.xml`}
                      target="_blank" rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 px-3 py-1.5 rounded border hover:bg-white/5 transition-colors text-xs font-medium"
                      style={{ borderColor: ahpBorder }}
                    >
                      <Database className="w-3.5 h-3.5" />
                      {t('hospital:financial.rawXml')}
                      <ExternalLink className="w-3 h-3 opacity-60" />
                    </a>
                  )}
                </div>
                <div className="opacity-60 mt-1">{t('hospital:financial.scheduleNote')}</div>
              </div>
              {/* Multi-year 990 trend if multiple filings available */}
              {proPubData?.filings_with_data && proPubData.filings_with_data.length > 1 && (
                <div className="overflow-x-auto mt-4 pt-4 border-t" style={{ borderColor: ahpBorder }}>
                  <div className="text-xs uppercase tracking-wider opacity-70 mb-2">{t('hospital:financial.multiYear990')}</div>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b" style={{ borderColor: ahpBorder }}>
                        <th className="text-left py-1.5 font-semibold">{t('hospital:financial.thFy')}</th>
                        <th className="text-right py-1.5 font-semibold">{t('hospital:financial.thRevenue')}</th>
                        <th className="text-right py-1.5 font-semibold">{t('hospital:financial.thExpenses')}</th>
                        <th className="text-right py-1.5 font-semibold">{t('hospital:financial.thAssets')}</th>
                        <th className="text-right py-1.5 font-semibold">{t('hospital:financial.thNetAssets')}</th>
                        <th className="text-right py-1.5 font-semibold">{t('hospital:financial.thPdf')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {proPubData.filings_with_data.slice(0, 8).map((f: any) => (
                        <tr key={f.tax_prd_yr || f.tax_prd} className="border-b" style={{ borderColor: ahpBorder }}>
                          <td className="py-1.5 font-mono">FY{f.tax_prd_yr}</td>
                          <td className="py-1.5 text-right font-mono">{f.totrevenue != null ? formatRevenue(Number(f.totrevenue)) : '—'}</td>
                          <td className="py-1.5 text-right font-mono">{f.totfuncexpns != null ? formatRevenue(Number(f.totfuncexpns)) : '—'}</td>
                          <td className="py-1.5 text-right font-mono">{f.totassetsend != null ? formatRevenue(Number(f.totassetsend)) : '—'}</td>
                          <td className="py-1.5 text-right font-mono">{f.totnetassetend != null ? formatRevenue(Number(f.totnetassetend)) : '—'}</td>
                          <td className="py-1.5 text-right">
                            {f.pdf_url ? (
                              <a href={f.pdf_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-0.5 underline opacity-80 hover:opacity-100">
                                <FileText className="w-3 h-3" /> PDF
                              </a>
                            ) : <span className="opacity-40">—</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </Card>
      )}

      {/* Schedule J Part II — named executive compensation. Parsed directly from IRS 990 XML. */}
      {latestExecs.length > 0 && (
        <Card className={cardCls} style={cardStyle}>
          <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
            <h3 className={`text-lg font-semibold ${isAhp ? '' : 'text-terminal-green'}`}>
              {t('hospital:financial.execComp')}
              <span className="text-sm font-normal opacity-70 ml-2">{t('hospital:financial.execCompSub', { year: latestExecYear })}</span>
            </h3>
            <span className="text-xs opacity-60">{t('hospital:financial.reportedCount', { count: latestExecs.length })}</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b" style={{ borderColor: ahpBorder }}>
                  <th className="text-left py-1.5 font-semibold">{t('hospital:financial.thName')}</th>
                  <th className="text-left py-1.5 font-semibold">{t('hospital:financial.thTitle')}</th>
                  <th className="text-right py-1.5 font-semibold">{t('hospital:financial.thBase')}</th>
                  <th className="text-right py-1.5 font-semibold">{t('hospital:financial.thBonus')}</th>
                  <th className="text-right py-1.5 font-semibold">{t('hospital:financial.thOther')}</th>
                  <th className="text-right py-1.5 font-semibold">{t('hospital:financial.thRetirement')}</th>
                  <th className="text-right py-1.5 font-semibold">{t('hospital:financial.thTotal')}</th>
                </tr>
              </thead>
              <tbody>
                {latestExecs.map((e, i) => {
                  const total = e.total_comp_usd != null ? Number(e.total_comp_usd) : 0;
                  // Highlight the highest-paid in red, top-3 in amber
                  let totalCls = 'text-white/95';
                  if (i === 0 && total >= 1_000_000) totalCls = 'text-red-400 font-bold';
                  else if (i < 3 && total >= 500_000) totalCls = 'text-amber-300 font-semibold';
                  return (
                    <tr key={`${e.person_name}-${i}`} className="border-b" style={{ borderColor: ahpBorder }}>
                      <td className="py-1.5 pr-2 font-medium">{e.person_name}</td>
                      <td className="py-1.5 pr-2 text-xs opacity-80 max-w-[200px] truncate" title={e.title || ''}>{e.title || '—'}</td>
                      <td className="py-1.5 text-right font-mono text-xs">{e.base_comp_usd != null ? formatRevenue(Number(e.base_comp_usd)) : '—'}</td>
                      <td className="py-1.5 text-right font-mono text-xs">{e.bonus_usd != null ? formatRevenue(Number(e.bonus_usd)) : '—'}</td>
                      <td className="py-1.5 text-right font-mono text-xs">{e.other_comp_usd != null ? formatRevenue(Number(e.other_comp_usd)) : '—'}</td>
                      <td className="py-1.5 text-right font-mono text-xs">{e.retirement_comp_usd != null ? formatRevenue(Number(e.retirement_comp_usd)) : '—'}</td>
                      <td className={`py-1.5 text-right font-mono ${totalCls}`}>{e.total_comp_usd != null ? formatRevenue(Number(e.total_comp_usd)) : '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="text-[11px] opacity-60 mt-3">
            {t('hospital:financial.execSource', { count: latestExecs.length })}
          </div>
        </Card>
      )}

      {/* Source-of-truth document links */}
      <Card className={cardCls} style={cardStyle}>
        <h3 className={`text-lg font-semibold mb-4 ${isAhp ? '' : 'text-terminal-green'}`}>{t('hospital:financial.sourceDocs')}</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {proPubUrl ? (
            <a href={proPubUrl} target="_blank" rel="noopener noreferrer"
               className="flex items-start gap-3 p-4 rounded border hover:bg-white/5 transition-colors"
               style={{ borderColor: ahpBorder }}>
              <FileText className="w-5 h-5 mt-0.5 flex-shrink-0 text-emerald-400" />
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm flex items-center gap-1">
                  {t('hospital:financial.irs990Card')} <ExternalLink className="w-3 h-3 opacity-60" />
                </div>
                <div className="text-xs text-muted-foreground">{t('hospital:financial.irs990CardSub', { ein })}</div>
                <div className="text-[11px] mt-1 opacity-70">{t('hospital:financial.irs990CardDesc')}</div>
              </div>
            </a>
          ) : (
            <div className="flex items-start gap-3 p-4 rounded border opacity-70" style={{ borderColor: ahpBorder }}>
              <FileText className="w-5 h-5 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <div className="font-semibold text-sm">{t('hospital:financial.irs990Card')}</div>
                <div className="text-xs text-muted-foreground">{t('hospital:financial.noEin')}</div>
              </div>
            </div>
          )}

          {careCompareUrl && (
            <a href={careCompareUrl} target="_blank" rel="noopener noreferrer"
               className="flex items-start gap-3 p-4 rounded border hover:bg-white/5 transition-colors"
               style={{ borderColor: ahpBorder }}>
              <Building2 className="w-5 h-5 mt-0.5 flex-shrink-0 text-sky-400" />
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm flex items-center gap-1">
                  {t('hospital:financial.careCompareCard')} <ExternalLink className="w-3 h-3 opacity-60" />
                </div>
                <div className="text-xs text-muted-foreground">{t('hospital:financial.careCompareCardSub', { ccn })}</div>
              </div>
            </a>
          )}

          {hcrisSearchUrl && (
            <a href={hcrisSearchUrl} target="_blank" rel="noopener noreferrer"
               className="flex items-start gap-3 p-4 rounded border hover:bg-white/5 transition-colors"
               style={{ borderColor: ahpBorder }}>
              <DollarSign className="w-5 h-5 mt-0.5 flex-shrink-0 text-amber-400" />
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm flex items-center gap-1">
                  {t('hospital:financial.hcrisCard')} <ExternalLink className="w-3 h-3 opacity-60" />
                </div>
                <div className="text-xs text-muted-foreground">{t('hospital:financial.hcrisCardSub', { ccn })}</div>
              </div>
            </a>
          )}
        </div>
      </Card>

      {/* Year-over-year trend if multiple years available */}
      {financeRows && financeRows.length > 1 && (
        <Card className={cardCls} style={cardStyle}>
          <h3 className={`text-lg font-semibold mb-4 ${isAhp ? '' : 'text-terminal-green'}`}>{t('hospital:financial.multiYearTrend')}</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b" style={{ borderColor: ahpBorder }}>
                  <th className="text-left py-2 font-semibold">{t('hospital:financial.thFy')}</th>
                  <th className="text-right py-2 font-semibold">{t('hospital:financial.thNetPatientRev')}</th>
                  <th className="text-right py-2 font-semibold">{t('hospital:financial.thOpMargin')}</th>
                  <th className="text-right py-2 font-semibold">{t('hospital:financial.thCharityCare')}</th>
                  <th className="text-right py-2 font-semibold">{t('hospital:financial.thMedicare')}</th>
                </tr>
              </thead>
              <tbody>
                {financeRows.filter(r => r.fiscal_year).slice(0, 8).map(r => (
                  <tr key={r.fiscal_year} className="border-b" style={{ borderColor: ahpBorder }}>
                    <td className="py-2 font-mono">FY{r.fiscal_year}</td>
                    <td className="py-2 text-right font-mono">{r.net_patient_revenue_usd != null ? formatRevenue(Number(r.net_patient_revenue_usd)) : '—'}</td>
                    <td className="py-2 text-right font-mono">{r.operating_margin_pct != null ? `${Number(r.operating_margin_pct).toFixed(2)}%` : '—'}</td>
                    <td className="py-2 text-right font-mono">{r.charity_care_usd != null ? formatRevenue(Number(r.charity_care_usd)) : '—'}</td>
                    <td className="py-2 text-right font-mono">{r.medicare_ipps_payment_usd != null ? formatRevenue(Number(r.medicare_ipps_payment_usd)) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}

function FinancialStat({ label, value, hint, valueColor, isAhp, mutedStyle }: {
  label: string;
  value: string;
  hint?: string;
  valueColor?: string;
  isAhp: boolean;
  mutedStyle?: React.CSSProperties;
}) {
  return (
    <div>
      <div className={`text-xs uppercase tracking-wider ${mutedStyle ? '' : 'opacity-70'}`} style={mutedStyle}>{label}</div>
      <div className={`text-2xl font-bold font-mono mt-1 ${isAhp ? '' : 'text-terminal-green'}`} style={valueColor ? { color: valueColor } : undefined}>
        {value}
      </div>
      {hint && <div className={`text-[10px] mt-0.5 ${mutedStyle ? '' : 'opacity-50'}`} style={mutedStyle}>{hint}</div>}
    </div>
  );
}

// Lazy-load DignityNarrationPlayer so it doesn't bloat the main bundle for users on other tabs
const DignityNarrationPlayerLazy = lazy(() =>
  import('@/components/dignity/DignityNarrationPlayer').then(m => ({ default: m.DignityNarrationPlayer }))
);

// Per-measure slider — visualizes where this hospital sits within the plausible range, with
// markers reserved for state / regional / national averages. CMS provides lower_estimate +
// higher_estimate (95% CI bounds) which we use as the slider's min/max when present;
// otherwise we widen ±50% from this hospital's score so the marker isn't pinned to an edge.
// Two-letter state code → full state name. Used in Quality slider legend so consumers see "Nevada" not "NV".
const US_STATE_NAMES: Record<string, string> = {
  AL: 'Alabama', AK: 'Alaska', AZ: 'Arizona', AR: 'Arkansas', CA: 'California', CO: 'Colorado', CT: 'Connecticut',
  DE: 'Delaware', DC: 'D.C.', FL: 'Florida', GA: 'Georgia', HI: 'Hawaii', ID: 'Idaho', IL: 'Illinois', IN: 'Indiana',
  IA: 'Iowa', KS: 'Kansas', KY: 'Kentucky', LA: 'Louisiana', ME: 'Maine', MD: 'Maryland', MA: 'Massachusetts',
  MI: 'Michigan', MN: 'Minnesota', MS: 'Mississippi', MO: 'Missouri', MT: 'Montana', NE: 'Nebraska', NV: 'Nevada',
  NH: 'New Hampshire', NJ: 'New Jersey', NM: 'New Mexico', NY: 'New York', NC: 'North Carolina', ND: 'North Dakota',
  OH: 'Ohio', OK: 'Oklahoma', OR: 'Oregon', PA: 'Pennsylvania', RI: 'Rhode Island', SC: 'South Carolina',
  SD: 'South Dakota', TN: 'Tennessee', TX: 'Texas', UT: 'Utah', VT: 'Vermont', VA: 'Virginia', WA: 'Washington',
  WV: 'West Virginia', WI: 'Wisconsin', WY: 'Wyoming', PR: 'Puerto Rico',
};

// CMS measure_id → scenario search query. When set, the Quality card renders a CTA to the Scenarios tab
// pre-filtered to this condition. Mappings are conservative — only measures that map cleanly to a clinical
// concept the consumer would search for. Hospital-wide / surgical-process / patient-experience measures
// have no specific scenario family and return null.
/**
 * Curated mapping: CMS quality measure → canonical scenario family parent.
 *
 * Each entry points at the FAMILY HEAD scenario (severity 0 baseline) for the most-complete
 * family that matches the measure. Clicking the CTA on a Quality slider lands the consumer
 * directly on that scenario page with the Family Explorer modal auto-opened (?family=open),
 * giving them the full severity 0→4 cost range at this hospital plus a shareable link.
 *
 * UUIDs verified against scenarios table 2026-05-09 — the family-head row with severity=0.
 * Every family below has 5 variants spanning sev 0-4 with real procedures.
 * If a measure has no curated mapping, the CTA renders as null (won't show the link).
 *
 * Picking criterion: the family with the most variants (broadest severity coverage) AND
 * a clinically-accurate match to what the CMS measure actually scores. We avoid linking
 * a "30-day pneumonia mortality" measure to a "Pneumocystis Pneumonia" family — even
 * though both contain "pneumonia", PCP is the wrong cohort.
 */
const QUALITY_MEASURE_FAMILY_MAP: Record<string, { uuid: string; slug: string; label: string }> = {
  // Mortality
  MORT_30_AMI:   { uuid: '7856434f-0ab3-4e9d-9fe3-76016feec778', slug: 'heart-attack-treatment',                                       label: 'heart attack treatment' },
  MORT_30_HF:    { uuid: '3ae04f81-08ae-4044-b699-67de2327dd7a', slug: 'heart-failure-management-annual',                              label: 'heart failure management' },
  MORT_30_PN:    { uuid: '742fe78e-7295-4e83-8a7a-902975ab4bd0', slug: 'community-acquired-pneumonia-outpatient-mild-oral-antibiotics', label: 'community-acquired pneumonia' },
  MORT_30_COPD:  { uuid: '0e4744e9-5af9-4301-8797-5f8a893991df', slug: 'copd-management-annual',                                       label: 'COPD management' },
  MORT_30_CABG:  { uuid: 'bfd29f3a-5901-4857-ac37-17787a9a8d9b', slug: 'coronary-artery-bypass-graft-cabg',                            label: 'coronary artery bypass (CABG)' },
  MORT_30_STK:   { uuid: '80533ce2-d231-4c89-8f74-43ae1656b469', slug: 'stroke-tia-mini-stroke-er-evaluation',                         label: 'acute ischemic stroke' },
  // Readmissions — same family heads, same condition cohorts
  READM_30_AMI:  { uuid: '7856434f-0ab3-4e9d-9fe3-76016feec778', slug: 'heart-attack-treatment',                                       label: 'heart attack treatment' },
  READM_30_HF:   { uuid: '3ae04f81-08ae-4044-b699-67de2327dd7a', slug: 'heart-failure-management-annual',                              label: 'heart failure management' },
  READM_30_PN:   { uuid: '742fe78e-7295-4e83-8a7a-902975ab4bd0', slug: 'community-acquired-pneumonia-outpatient-mild-oral-antibiotics', label: 'community-acquired pneumonia' },
  READM_30_COPD: { uuid: '0e4744e9-5af9-4301-8797-5f8a893991df', slug: 'copd-management-annual',                                       label: 'COPD management' },
  READM_30_CABG: { uuid: 'bfd29f3a-5901-4857-ac37-17787a9a8d9b', slug: 'coronary-artery-bypass-graft-cabg',                            label: 'coronary artery bypass (CABG)' },
  READM_30_STK:  { uuid: '80533ce2-d231-4c89-8f74-43ae1656b469', slug: 'stroke-tia-mini-stroke-er-evaluation',                         label: 'acute ischemic stroke' },
  // STK_* — stroke care process measures, same family
  STK_02:        { uuid: '80533ce2-d231-4c89-8f74-43ae1656b469', slug: 'stroke-tia-mini-stroke-er-evaluation',                         label: 'acute ischemic stroke' },
  STK_03:        { uuid: '80533ce2-d231-4c89-8f74-43ae1656b469', slug: 'stroke-tia-mini-stroke-er-evaluation',                         label: 'acute ischemic stroke' },
  STK_05:        { uuid: '80533ce2-d231-4c89-8f74-43ae1656b469', slug: 'stroke-tia-mini-stroke-er-evaluation',                         label: 'acute ischemic stroke' },
  // OP_40 — STEMI process, same family as heart attack
  OP_40:         { uuid: '7856434f-0ab3-4e9d-9fe3-76016feec778', slug: 'heart-attack-treatment',                                       label: 'heart attack treatment' },
  // EDAC (excess days in acute care)
  EDAC_30_AMI:   { uuid: '7856434f-0ab3-4e9d-9fe3-76016feec778', slug: 'heart-attack-treatment',                                       label: 'heart attack treatment' },
  EDAC_30_HF:    { uuid: '3ae04f81-08ae-4044-b699-67de2327dd7a', slug: 'heart-failure-management-annual',                              label: 'heart failure management' },
  EDAC_30_PN:    { uuid: '742fe78e-7295-4e83-8a7a-902975ab4bd0', slug: 'community-acquired-pneumonia-outpatient-mild-oral-antibiotics', label: 'community-acquired pneumonia' },
  // Hip/Knee — both COMP_HIP_KNEE and READM_30_HIP_KNEE map to Total Hip (the larger cohort)
  COMP_HIP_KNEE:     { uuid: '5138f10d-312e-421b-8f4f-63233acbf60d', slug: 'total-hip-replacement', label: 'hip replacement' },
  READM_30_HIP_KNEE: { uuid: '5138f10d-312e-421b-8f4f-63233acbf60d', slug: 'total-hip-replacement', label: 'hip replacement' },
  // Sepsis bundle compliance (severe sepsis 3hr/6hr) — Sepsis Treatment ICU family
  SEP_1:         { uuid: '68c21da0-e72d-465f-8fde-f44e62685461', slug: 'sepsis-treatment-icu-hospitalization',                         label: 'sepsis treatment' },
  SEV_SEP_3HR:   { uuid: '68c21da0-e72d-465f-8fde-f44e62685461', slug: 'sepsis-treatment-icu-hospitalization',                         label: 'sepsis treatment' },
  SEV_SEP_6HR:   { uuid: '68c21da0-e72d-465f-8fde-f44e62685461', slug: 'sepsis-treatment-icu-hospitalization',                         label: 'sepsis treatment' },
  // Septic shock bundle — separate family for the more severe cohort
  SEP_SH_3HR:    { uuid: '7b2d628e-3618-46e3-899f-831e8826e3b2', slug: 'septic-shock-surviving-sepsis-bundle', label: 'septic shock' },
  SEP_SH_6HR:    { uuid: '7b2d628e-3618-46e3-899f-831e8826e3b2', slug: 'septic-shock-surviving-sepsis-bundle', label: 'septic shock' },
  // Colonoscopy — OP_29 (follow-up interval) + OP_32 (unplanned visits after colonoscopy)
  OP_29:         { uuid: '7d63c3e3-f51e-457e-8b1a-33fcd7b7ce81', slug: 'colonoscopy-screening',     label: 'screening colonoscopy' },
  OP_32:         { uuid: '7d63c3e3-f51e-457e-8b1a-33fcd7b7ce81', slug: 'colonoscopy-screening',     label: 'screening colonoscopy' },
  // Cataract — OP_31 visual function improvement
  OP_31:         { uuid: '98f1ecef-9b7f-41be-9e72-7f75538d9994', slug: 'cataract-surgery-phacoemulsification-iol', label: 'cataract surgery' },
  // Outpatient chemotherapy ER visits / readmissions — Febrile Neutropenia family is the right cohort
  // (CMS OP_35 measures complications AFTER outpatient chemo cycles — the canonical bad outcome is febrile neutropenia)
  OP_35_ADM:     { uuid: '20881f40-a081-447a-a015-5d9377ef06c5', slug: 'febrile-neutropenia-inpatient-management',  label: 'chemo-related febrile neutropenia' },
  OP_35_ED:      { uuid: '20881f40-a081-447a-a015-5d9377ef06c5', slug: 'febrile-neutropenia-inpatient-management',  label: 'chemo-related febrile neutropenia' },
  // Hospital harm — opioid adverse events
  HH_ORAE:       { uuid: '05a2fd73-3c69-4199-8895-6d59c03793e6', slug: 'opioid-overdose-witnessed-rapid-naloxone', label: 'opioid overdose / adverse event' },
  // Hospital harm — severe hyperglycemia maps to DKA family (close cohort; HH_HYPO has no clean family yet)
  HH_HYPER:      { uuid: '650181b0-d209-41d1-974b-7ba6397e6a3e', slug: 'diabetic-ketoacidosis-insulin-drip-protocol', label: 'severe hyperglycemia / DKA' },
};

function scenarioFamilyForMeasure(measureId: string | null | undefined): { uuid: string; slug: string; label: string } | null {
  if (!measureId) return null;
  return QUALITY_MEASURE_FAMILY_MAP[measureId.toUpperCase()] || null;
}

// Plain-English description of WHAT the score number actually means. Used as the sub-label under the big number.
// Falls back to a generic "of this hospital's patients" if no specific framing applies.
function scoreUnitLabel(t: (key: string) => string, measureId: string | null | undefined, measureName: string | null | undefined): string {
  const id = (measureId || '').toUpperCase();
  const name = (measureName || '').toLowerCase();
  if (id.startsWith('MORT_30')) return t('hospital:quality.units.died30');
  if (id.startsWith('READM_30')) return t('hospital:quality.units.returned30');
  if (id.startsWith('EDAC_30')) return t('hospital:quality.units.extraDays');
  if (id.startsWith('COMP_')) return t('hospital:quality.units.complication');
  if (id.startsWith('HAI_')) return t('hospital:quality.units.infectionRate');
  if (id.startsWith('PSI_')) return t('hospital:quality.units.safetyRate');
  if (name.includes('readmission') || name.includes('return')) return t('hospital:quality.units.returned30');
  if (name.includes('mortality') || name.includes('death')) return t('hospital:quality.units.died30');
  if (name.includes('infection')) return t('hospital:quality.units.infectionRate');
  if (name.includes('per 1,000') || name.includes('per 1000')) return t('hospital:quality.units.per1000');
  if (name.includes('rate')) return t('hospital:quality.units.ofPatients');
  return t('hospital:quality.units.measured');
}

function QualityMeasureSlider({ measure, verdict, friendlyName, formattedScore, isAhp, isDark, aggregate, stateCode, hospitalId }: {
  measure: any;
  verdict: { text: string; tone: 'good' | 'neutral' | 'bad' | 'na' };
  friendlyName: string;
  formattedScore: string | null;
  isAhp: boolean;
  isDark: boolean;
  aggregate?: { national?: any; state?: any };
  stateCode?: string;
  hospitalId?: string;
}) {
  const { t: i18nT } = useTranslation(['hospital', 'common']);
  const stateName = stateCode ? (US_STATE_NAMES[stateCode] || stateCode) : i18nT('hospital:quality.stateFallback');
  const scenarioFamily = scenarioFamilyForMeasure(measure?.measure_id);
  const unitLabel = scoreUnitLabel(i18nT, measure?.measure_id, measure?.measure_name);
  // Split formattedScore into "8.05%" + " of patients" so we can promote the number visually.
  // formatScore() returns shapes like "8.05% of patients" or "+1.2 days vs national avg" or "5.2 per 1,000 patients".
  const scoreNum = (() => {
    if (!formattedScore) return measure?.score != null ? Number(measure.score).toFixed(1) : '—';
    // Pull the leading numeric+unit token (e.g., "8.05%" or "+1.2 days" or "5.2")
    const m = formattedScore.match(/^([+-]?\d+(?:\.\d+)?(?:\s?%)?(?:\s?days)?(?:\s?per\s?1,?000)?)/);
    return m ? m[1].trim() : formattedScore;
  })();
  const score = measure.score != null ? Number(measure.score) : null;
  // AHP patriotic palette — Old Glory navy, AHP red, AHP gold, cream/parchment, emerald.
  // Verdict-tone color blocks differ by theme: light mode uses higher-contrast text on tinted backgrounds.
  const toneColorsDark = {
    good:    { fill: '#10b981', stripe: '#0e9670', soft: 'rgba(16, 185, 129, 0.10)',  pillBg: 'rgba(16, 185, 129, 0.18)',  pillBd: 'rgba(16, 185, 129, 0.55)',  pillTx: '#86efac' },
    neutral: { fill: 'var(--civic-gold)', stripe: 'var(--civic-gold)', soft: 'rgba(var(--civic-gold-rgb), 0.10)',  pillBg: 'rgba(var(--civic-gold-rgb), 0.20)',  pillBd: 'rgba(var(--civic-gold-rgb), 0.65)',  pillTx: 'var(--civic-parchment)' },
    bad:     { fill: '#ff7082', stripe: '#9A1E2D', soft: 'rgba(var(--civic-red-rgb), 0.12)',   pillBg: 'rgba(var(--civic-red-rgb), 0.22)',   pillBd: 'rgba(var(--civic-red-rgb), 0.60)',   pillTx: '#FCA5A5' },
    na:      { fill: '#A9B4C6', stripe: '#7B8AA0', soft: 'rgba(var(--civic-parchment-rgb), 0.05)', pillBg: 'rgba(var(--civic-parchment-rgb), 0.10)', pillBd: 'rgba(var(--civic-parchment-rgb), 0.30)', pillTx: '#D4C9AE' },
  } as const;
  const toneColorsLight = {
    good:    { fill: '#0F8A5F', stripe: '#0F8A5F', soft: 'rgba(15, 138, 95, 0.06)',  pillBg: 'rgba(15, 138, 95, 0.12)',  pillBd: 'rgba(15, 138, 95, 0.45)',  pillTx: '#0F6B49' },
    neutral: { fill: '#9A7B33', stripe: 'var(--civic-gold)', soft: 'rgba(var(--civic-gold-rgb), 0.10)', pillBg: 'rgba(var(--civic-gold-rgb), 0.18)', pillBd: 'rgba(var(--civic-gold-rgb), 0.65)', pillTx: 'var(--civic-gold-ink)' },
    bad:     { fill: 'var(--civic-red)', stripe: 'var(--civic-red)', soft: 'rgba(var(--civic-red-rgb), 0.06)',  pillBg: 'rgba(var(--civic-red-rgb), 0.12)',  pillBd: 'rgba(var(--civic-red-rgb), 0.55)',  pillTx: '#8B1828' },
    na:      { fill: '#64748B', stripe: '#94A3B8', soft: 'rgba(var(--civic-navy-deeper-rgb), 0.03)',    pillBg: 'rgba(var(--civic-navy-deeper-rgb), 0.06)',    pillBd: 'rgba(var(--civic-navy-deeper-rgb), 0.20)',    pillTx: '#475569' },
  } as const;
  const c = (isDark ? toneColorsDark : toneColorsLight)[verdict.tone];

  // Theme-dependent surface tokens
  const t = isDark ? {
    cardBg: 'linear-gradient(180deg, rgba(2, 22, 60, 0.92) 0%, rgba(0, 14, 44, 0.96) 100%)',
    cardBorder: 'rgba(var(--civic-gold-rgb), 0.18)',
    cardShadow: '0 1px 0 rgba(255,255,255,0.04) inset, 0 8px 20px -14px rgba(0,0,0,0.6)',
    nameText: 'var(--civic-parchment)',
    // 0.55 / 0.45 read as grey mush on the navy cards at 10–11px; these clear AA.
    mutedText: 'rgba(var(--civic-parchment-rgb), 0.72)',
    fadedText: 'rgba(var(--civic-parchment-rgb), 0.62)',
    veryFadedText: 'rgba(var(--civic-parchment-rgb), 0.30)',
    divider: 'rgba(var(--civic-gold-rgb), 0.18)',
    trackBg: 'linear-gradient(180deg, rgba(0,8,28,0.95) 0%, rgba(var(--civic-navy-deeper-rgb),0.95) 100%)',
    trackInset: 'inset 0 1px 0 rgba(0,0,0,0.4), inset 0 -1px 0 rgba(var(--civic-parchment-rgb),0.06), 0 0 0 1px rgba(var(--civic-gold-rgb),0.18)',
    trackTint: 'linear-gradient(90deg, rgba(var(--civic-red-rgb),0.18) 0%, rgba(var(--civic-navy-deeper-rgb),0) 35%, rgba(var(--civic-navy-deeper-rgb),0) 65%, rgba(16,185,129,0.18) 100%)',
    iqrBg: 'linear-gradient(180deg, rgba(var(--civic-parchment-rgb),0.16) 0%, rgba(var(--civic-gold-rgb),0.20) 100%)',
    iqrBorder: 'rgba(var(--civic-gold-rgb), 0.45)',
    iqrInset: 'inset 0 0 8px rgba(var(--civic-gold-rgb),0.15)',
    usaTick: 'var(--civic-parchment)',
    usaTickGlow: '0 0 4px rgba(var(--civic-parchment-rgb),0.6)',
    usaTickPlaceholder: 'rgba(var(--civic-parchment-rgb),0.18)',
    usaLabel: 'var(--civic-parchment)',
    usaLabelPlaceholder: 'rgba(var(--civic-parchment-rgb),0.30)',
    stateTick: 'var(--civic-gold)',
    stateTickGlow: '0 0 4px rgba(var(--civic-gold-rgb),0.7)',
    stateLabel: 'var(--civic-gold)',
    thumbBorder: 'var(--civic-parchment)',
    thumbRing: 'var(--civic-gold)',
    legendIqrBg: 'rgba(var(--civic-gold-rgb),0.20)',
    legendIqrBorder: 'rgba(var(--civic-gold-rgb),0.45)',
  } : {
    cardBg: 'linear-gradient(180deg, #FFFFFF 0%, #FBF7EE 100%)',
    cardBorder: 'rgba(var(--civic-navy-deeper-rgb), 0.12)',
    cardShadow: '0 1px 0 rgba(255,255,255,0.6) inset, 0 4px 14px -6px rgba(var(--civic-navy-deeper-rgb), 0.10)',
    nameText: 'var(--civic-navy-deeper)',
    mutedText: 'rgba(var(--civic-navy-deeper-rgb), 0.65)',
    fadedText: 'rgba(var(--civic-navy-deeper-rgb), 0.55)',
    veryFadedText: 'rgba(var(--civic-navy-deeper-rgb), 0.40)',
    divider: 'rgba(var(--civic-gold-rgb), 0.30)',
    trackBg: 'linear-gradient(180deg, var(--civic-parchment) 0%, #EFE3C8 100%)',
    trackInset: 'inset 0 1px 1px rgba(var(--civic-navy-deeper-rgb),0.08), 0 0 0 1px rgba(var(--civic-gold-rgb),0.45)',
    trackTint: 'linear-gradient(90deg, rgba(var(--civic-red-rgb),0.18) 0%, rgba(var(--civic-parchment-rgb),0) 35%, rgba(var(--civic-parchment-rgb),0) 65%, rgba(15,138,95,0.18) 100%)',
    iqrBg: 'linear-gradient(180deg, rgba(var(--civic-gold-rgb),0.30) 0%, rgba(var(--civic-gold-rgb),0.40) 100%)',
    iqrBorder: 'rgba(154, 123, 51, 0.65)',
    iqrInset: 'inset 0 1px 0 rgba(255,255,255,0.4)',
    usaTick: 'var(--civic-navy-deeper)',
    usaTickGlow: '0 0 0 1px rgba(var(--civic-parchment-rgb),0.8)',
    usaTickPlaceholder: 'rgba(var(--civic-navy-deeper-rgb),0.20)',
    usaLabel: 'var(--civic-navy-deeper)',
    usaLabelPlaceholder: 'rgba(var(--civic-navy-deeper-rgb),0.30)',
    stateTick: '#9A7B33',
    stateTickGlow: '0 0 0 1px rgba(var(--civic-parchment-rgb),0.8)',
    stateLabel: 'var(--civic-gold-ink)',
    thumbBorder: '#FFFFFF',
    thumbRing: 'var(--civic-navy-deeper)',
    legendIqrBg: 'rgba(var(--civic-gold-rgb),0.35)',
    legendIqrBorder: 'rgba(154, 123, 51, 0.65)',
  };
  // Pull national + state aggregates if computed.
  const natl = aggregate?.national;
  const stat = aggregate?.state;
  const natlMedian  = natl?.median_score != null ? Number(natl.median_score) : null;
  const natlP25     = natl?.p25_score    != null ? Number(natl.p25_score)    : null;
  const natlP75     = natl?.p75_score    != null ? Number(natl.p75_score)    : null;
  const natlMin     = natl?.min_score    != null ? Number(natl.min_score)    : null;
  const natlMax     = natl?.max_score    != null ? Number(natl.max_score)    : null;
  const stateMedian = stat?.median_score != null ? Number(stat.median_score) : null;
  // Determine slider range. Prefer national min/max for context, then CMS bounds, then ±50% padding.
  const lower = natlMin != null
    ? natlMin
    : (measure.lower_estimate != null ? Number(measure.lower_estimate) : (score != null ? score * 0.5 : 0));
  const upper = natlMax != null
    ? natlMax
    : (measure.higher_estimate != null ? Number(measure.higher_estimate) : (score != null ? Math.max(score * 1.5, score + 1) : 1));
  const sliderMin = Math.max(0, Math.min(lower, score ?? Infinity, natlP25 ?? Infinity));
  const sliderMax = Math.max(upper, score ?? -Infinity, natlP75 ?? -Infinity, 0.001);
  const range = sliderMax - sliderMin || 1;
  // Compute 0..100% position for any value within [sliderMin, sliderMax]
  const pct = (v: number | null | undefined) => {
    if (v == null || isNaN(v)) return null;
    const p = ((Number(v) - sliderMin) / range) * 100;
    return Math.max(0, Math.min(100, p));
  };
  const hospPct = pct(score);
  const nationalPct = pct(natlMedian);
  const natlP25Pct  = pct(natlP25);
  const natlP75Pct  = pct(natlP75);
  const statePct    = pct(stateMedian);
  // 60-mile radius — not yet computed.
  const localPct: number | null = null;

  return (
    <div
      className="relative rounded-md p-4 pl-5 transition-shadow hover:shadow-[0_0_0_1px_rgba(var(--civic-gold-rgb),0.45),0_8px_24px_-12px_rgba(var(--civic-navy-deeper-rgb),0.18)]"
      style={{
        background: `${t.cardBg}, ${c.soft}`,
        backgroundBlendMode: 'normal, multiply',
        border: `1px solid ${t.cardBorder}`,
        boxShadow: t.cardShadow,
      }}
    >
      {/* Flag-stripe left accent — verdict tone at full saturation, like a flag binding */}
      <div
        className="absolute top-2 bottom-2 left-0 w-[3px] rounded-full"
        style={{ background: c.stripe, boxShadow: `0 0 8px ${c.stripe}55` }}
        aria-hidden
      />

      {/* Header: measure name + verdict pill */}
      <div className="flex items-start justify-between gap-3 mb-1">
        <div className="text-[13px] font-semibold leading-snug flex-1 tracking-tight" style={{ color: t.nameText }}>
          {friendlyName}
        </div>
        <TooltipProvider delayDuration={150}>
          <Tooltip>
            <TooltipTrigger asChild>
              <span
                className="text-[10px] uppercase tracking-[0.08em] font-bold px-2.5 py-[3px] rounded-sm border whitespace-nowrap cursor-help"
                style={{ background: c.pillBg, borderColor: c.pillBd, color: c.pillTx, letterSpacing: '0.08em' }}
              >
                {verdict.text}
              </span>
            </TooltipTrigger>
            <TooltipContent side="top" className="max-w-[260px] text-xs leading-relaxed">
              {verdict.tone === 'good'   && <span><strong>Good news, friend.</strong> CMS compared this rate to every other U.S. hospital and found it's statistically <em>better</em> than most.</span>}
              {verdict.tone === 'bad'    && <span><strong>Heads up.</strong> CMS compared this rate to every other U.S. hospital and found it's statistically <em>worse</em> than most. That's a real signal worth weighing.</span>}
              {verdict.tone === 'neutral' && <span>This hospital's rate is within the normal range — not measurably different from the typical U.S. hospital, statistically speaking.</span>}
              {verdict.tone === 'na'     && <span>CMS didn't have enough patient cases to score this measure reliably for this hospital.</span>}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>

      {/* HUGE labeled score — the headline number. */}
      <div className="my-3 flex items-baseline gap-3 flex-wrap">
        <span className="font-mono text-4xl md:text-5xl font-extrabold tabular-nums leading-none" style={{ color: c.fill, letterSpacing: '-0.02em' }}>
          {scoreNum}
        </span>
        <div className="flex flex-col">
          <span className="text-[12px] font-medium leading-tight" style={{ color: t.nameText }}>
            {unitLabel}
          </span>
          {measure.denominator != null && measure.denominator > 0 && (
            <span className="text-[10px] tabular-nums mt-0.5" style={{ color: t.fadedText }}>
              {i18nT('hospital:quality.basedOnPatients', { value: Number(measure.denominator).toLocaleString() })}
            </span>
          )}
        </div>
      </div>

      {/* Slider track */}
      <div className="relative h-9 mt-4 mb-1">
        {/* Base track */}
        <div
          className="absolute left-0 right-0 top-1/2 -translate-y-1/2 h-[6px] rounded-full"
          style={{ background: t.trackBg, boxShadow: t.trackInset }}
        />

        {/* Subtle direction-of-quality tint — red at "worse" end, emerald at "better" end */}
        <div
          className="absolute left-0 right-0 top-1/2 -translate-y-1/2 h-[6px] rounded-full opacity-70"
          style={{ background: t.trackTint }}
        />

        {/* National p25–p75 interquartile band — gold tinted, theme-aware */}
        {natlP25Pct != null && natlP75Pct != null && (
          <TooltipProvider delayDuration={150}>
            <Tooltip>
              <TooltipTrigger asChild>
                <div
                  className="absolute top-1/2 -translate-y-1/2 h-[10px] rounded-sm cursor-help"
                  style={{
                    left: `${Math.min(natlP25Pct, natlP75Pct)}%`,
                    width: `${Math.abs(natlP75Pct - natlP25Pct)}%`,
                    background: t.iqrBg,
                    border: `1px solid ${t.iqrBorder}`,
                    boxShadow: t.iqrInset,
                  }}
                />
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-[260px] text-xs leading-relaxed">
                <strong>The "normal range."</strong> Half of all U.S. hospitals fall inside this gold band — between <strong>{Number(natl?.p25_score).toFixed(1)}</strong> and <strong>{Number(natl?.p75_score).toFixed(1)}</strong>. If a hospital sits outside it, that's worth a second look.
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}

        {/* National median marker */}
        {nationalPct != null ? (
          <TooltipProvider delayDuration={150}>
            <Tooltip>
              <TooltipTrigger asChild>
                <div
                  className="absolute top-0 bottom-0 w-px cursor-help"
                  style={{ left: `${nationalPct}%`, background: t.usaTick, boxShadow: t.usaTickGlow }}
                >
                  <div className="absolute -top-[2px] -translate-x-1/2 text-[8px] uppercase font-bold whitespace-nowrap tracking-wider" style={{ color: t.usaLabel }}>USA</div>
                </div>
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-[260px] text-xs leading-relaxed">
                <strong>U.S. median: {natlMedian?.toFixed(1)}.</strong> Out of <strong>{natl?.hospital_count?.toLocaleString?.()}</strong> reporting hospitals nationwide, half do better than this number — half do worse. It's the middle of the country.
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ) : (
          <div className="absolute top-0 bottom-0 w-px" style={{ left: '50%', background: t.usaTickPlaceholder }} title={i18nT('hospital:quality.nationalMedianCalculating')}>
            <div className="absolute -top-[2px] -translate-x-1/2 text-[8px] uppercase font-bold whitespace-nowrap tracking-wider" style={{ color: t.usaLabelPlaceholder }}>USA</div>
          </div>
        )}

        {/* State median marker — labeled with state code on the slider, full name in legend & tooltip */}
        {statePct != null && (
          <TooltipProvider delayDuration={150}>
            <Tooltip>
              <TooltipTrigger asChild>
                <div
                  className="absolute top-0 bottom-0 w-px cursor-help"
                  style={{ left: `${statePct}%`, background: t.stateTick, boxShadow: t.stateTickGlow }}
                >
                  <div className="absolute -bottom-[2px] -translate-x-1/2 text-[8px] uppercase font-bold whitespace-nowrap tracking-wider" style={{ color: t.stateLabel }}>{stateCode || 'ST'}</div>
                </div>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="max-w-[260px] text-xs leading-relaxed">
                <strong>{stateName} median: {stateMedian?.toFixed(1)}.</strong> Among <strong>{stat?.hospital_count?.toLocaleString?.()}</strong> hospitals in {stateName}, half do better than this — half do worse. A state-only view if you want to compare neighbors.
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}

        {/* 60-mile radius marker — placeholder */}
        {localPct != null && (
          <div className="absolute top-0 bottom-0 w-px" style={{ left: `${localPct}%`, background: '#FFA500' }} title={i18nT('hospital:quality.within60Title')} />
        )}

        {/* Hospital marker — primary thumb */}
        {hospPct != null && (
          <TooltipProvider delayDuration={150}>
            <Tooltip>
              <TooltipTrigger asChild>
                <div
                  className="absolute top-1/2 -translate-x-1/2 -translate-y-1/2 z-10 cursor-help"
                  style={{ left: `${hospPct}%` }}
                >
                  <div
                    className="w-[18px] h-[18px] rounded-full"
                    style={{
                      background: c.fill,
                      border: `2px solid ${t.thumbBorder}`,
                      boxShadow: `0 0 0 1.5px ${t.thumbRing}, 0 0 10px ${c.fill}66, 0 2px 4px rgba(var(--civic-navy-deeper-rgb),0.25)`,
                    }}
                  />
                </div>
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-[240px] text-xs leading-relaxed">
                <strong>This hospital scored {scoreNum}.</strong> {unitLabel}.
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>

      {/* Slider scale ends */}
      <div className="flex justify-between text-[10px] font-mono mt-1.5" style={{ color: t.fadedText }}>
        <span>{sliderMin.toFixed(1)}</span>
        <span>{sliderMax.toFixed(1)}</span>
      </div>

      {/* Markers legend — full state name, no acronyms */}
      <div className="flex flex-wrap gap-x-3 gap-y-1 mt-3 pt-2.5 text-[10px]"
           style={{ color: t.mutedText, borderTop: `1px solid ${t.divider}` }}>
        <span className="inline-flex items-center gap-1">
          <span className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: c.fill, border: `1px solid ${t.thumbBorder}` }} />
          {i18nT('hospital:quality.legend.thisHospital')}
        </span>
        <span className="inline-flex items-center gap-1" style={{ color: nationalPct != null ? t.usaLabel : t.veryFadedText }}>
          <span className="inline-block w-px h-2.5" style={{ background: nationalPct != null ? t.usaTick : t.usaTickPlaceholder }} />
          {i18nT('hospital:quality.legend.usMedian')}{nationalPct != null && natl?.hospital_count != null ? i18nT('hospital:quality.legend.hospitalsCount', { value: Number(natl.hospital_count).toLocaleString() }) : i18nT('hospital:quality.legend.soon')}
        </span>
        <span className="inline-flex items-center gap-1" style={{ color: statePct != null ? t.stateLabel : t.veryFadedText }}>
          <span className="inline-block w-px h-2.5" style={{ background: statePct != null ? t.stateTick : t.veryFadedText }} />
          {i18nT('hospital:quality.legend.stateMedian', { state: stateName })}{statePct != null && stat?.hospital_count != null ? i18nT('hospital:quality.legend.hospitalsCount', { value: Number(stat.hospital_count).toLocaleString() }) : i18nT('hospital:quality.legend.soon')}
        </span>
        {natlP25Pct != null && natlP75Pct != null && (
          <span className="inline-flex items-center gap-1">
            <span className="inline-block w-3 h-2 rounded-sm" style={{ background: t.legendIqrBg, border: `1px solid ${t.legendIqrBorder}` }} />
            {i18nT('hospital:quality.legend.middle50')}
          </span>
        )}
        <span className="inline-flex items-center gap-1" style={{ color: localPct != null ? '#C57A1F' : t.veryFadedText }}>
          <span className="inline-block w-px h-2.5" style={{ background: localPct != null ? '#C57A1F' : t.veryFadedText }} />
          {i18nT('hospital:quality.legend.within60')}{localPct == null && i18nT('hospital:quality.legend.soon')}
        </span>
      </div>

      {/* Scenario family CTA — deep-link to the canonical family parent for this condition,
          with ?family=open to auto-pop the Family Explorer modal showing all severity 0–4 variants
          and this hospital's price across each. The whole link is shareable. */}
      {scenarioFamily && hospitalId && (
        <Link
          to={`/scenarios/${scenarioFamily.uuid}/${scenarioFamily.slug}/hospital/${hospitalId}?family=open`}
          className="mt-3 inline-flex items-center gap-1.5 text-[11px] font-semibold rounded-sm px-2 py-1 transition-colors"
          style={{
            color: isDark ? 'var(--civic-gold)' : 'var(--civic-gold-ink)',
            background: isDark ? 'rgba(var(--civic-gold-rgb),0.10)' : 'rgba(var(--civic-gold-rgb),0.18)',
            border: `1px solid ${isDark ? 'rgba(var(--civic-gold-rgb),0.35)' : 'rgba(154,123,51,0.45)'}`,
          }}
        >
          <DollarSign className="w-3 h-3" />
          {i18nT('hospital:quality.seeFamilyPriceRange', { label: scenarioFamily.label })}
          <ChevronRight className="w-3 h-3" />
        </Link>
      )}

      {measure.footnote && (
        <div className="text-[10px] mt-2 italic" style={{ color: t.fadedText }}>{measure.footnote}</div>
      )}
    </div>
  );
}

// Quality tab — pulls CMS Hospital Compare measures (mortality, readmission, infection, patient experience)
// from hospital_quality_measures, plus the existing CMS star ratings + Google rating from the hospital row.
function HospitalQualitySection({ hospital, isAhp, isDark, ahpCardBg, ahpBorder, ahpText, ahpMuted, ahpAccent, priceMarkup, priceRank, totalRanked, dataVintageYear, briefing, onPlayBriefing }: {
  hospital: any;
  isAhp: boolean;
  isDark: boolean;
  ahpCardBg: string | undefined;
  ahpBorder: string | undefined;
  ahpText: string | undefined;
  ahpMuted: string | undefined;
  ahpAccent: string | undefined;
  // Price side of the report card — the headline median-baseline markup,
  // national rank, and ranked-pool size the hero already shows.
  priceMarkup: number | null;
  priceRank: number | null;
  totalRanked: number | null;
  // Year of the price file our current import came from — stamped on the
  // card beside the CMS release date (dates are stamps, never filters).
  dataVintageYear: number | null;
  briefing: HospitalBriefingVideo | null;
  onPlayBriefing: () => void;
}) {
  const { t } = useTranslation(['hospital', 'common']);
  const { data: measures, isLoading } = useQuery({
    queryKey: ['hospital-quality-measures', hospital.id],
    enabled: !!hospital.id,
    staleTime: 1000 * 60 * 60,
    refetchOnWindowFocus: false,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('hospital_quality_measures')
        .select('measure_category, measure_id, measure_name, compared_to_national, score, denominator, period_end, footnote')
        .eq('hospital_id', hospital.id)
        .order('period_end', { ascending: false });
      if (error) throw error;
      return data || [];
    },
  });

  // National + state aggregates for every measure this hospital reports — populates the slider markers.
  const measureIds = (measures || []).map(m => m.measure_id).filter(Boolean);
  const { data: aggregates } = useQuery({
    queryKey: ['national-quality-index', hospital.id, hospital.state, measureIds.join(',')],
    enabled: measureIds.length > 0,
    staleTime: 1000 * 60 * 60,
    refetchOnWindowFocus: false,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('national_quality_index')
        .select('measure_id, geography_type, geography_id, hospital_count, min_score, p25_score, median_score, p75_score, max_score, mean_score, better_count, same_count, worse_count')
        .in('measure_id', measureIds);
      if (error) throw error;
      return data || [];
    },
  });
  // Index aggregates: { measure_id: { national: {...}, state: {...} } }
  const aggIndex: Record<string, { national?: any; state?: any }> = {};
  (aggregates || []).forEach(a => {
    aggIndex[a.measure_id] = aggIndex[a.measure_id] || {};
    if (a.geography_type === 'national') {
      aggIndex[a.measure_id].national = a;
    } else if (a.geography_type === 'state' && a.geography_id === hospital.state) {
      aggIndex[a.measure_id].state = a;
    }
  });

  // Re-bucket measures by smart inference on measure_id since CMS bundles diverse measures in one dataset.
  // Patterns drawn from CMS measure ID conventions: MORT_*, READM_*, COMP_*, HAI_*, etc.
  const bucket = (m: any): 'mortality' | 'readmission' | 'complications' | 'infection' | 'patient_experience' | 'other' => {
    const id = (m.measure_id || '').toUpperCase();
    const name = (m.measure_name || '').toLowerCase();
    if (id.startsWith('MORT_') || id.includes('MORTALITY') || name.includes('death') || name.includes('mortality')) return 'mortality';
    if (id.startsWith('READM_') || id.includes('30_HF') || id.includes('30_AMI') || id.includes('30_PN') || id.includes('30_COPD') || id.includes('30_STK') || id.includes('30_HIP_KNEE') || name.includes('readmission') || name.includes('return') || name.includes('unplanned')) return 'readmission';
    if (id.startsWith('COMP_') || name.includes('complication')) return 'complications';
    if (id.startsWith('HAI_') || id.startsWith('OP_') || name.includes('infection') || name.includes('clabsi') || name.includes('cauti') || name.includes('mrsa') || name.includes('c.diff') || name.includes('ssi')) return 'infection';
    if (id.startsWith('H_') || id.startsWith('HCAHPS') || name.includes('patient') || name.includes('hcahps') || name.includes('experience')) return 'patient_experience';
    return 'other';
  };

  const groups: Record<string, any[]> = { mortality: [], readmission: [], complications: [], infection: [], patient_experience: [], other: [] };
  (measures || []).forEach(m => groups[bucket(m)].push(m));

  // Comparison-to-national → consumer-friendly verdict + color
  const verdict = (cmp: string | null): { text: string; tone: 'good' | 'neutral' | 'bad' | 'na' } => {
    if (!cmp) return { text: t('hospital:quality.verdict.noData'), tone: 'na' };
    const lower = cmp.toLowerCase();
    if (lower.includes('better') || lower.includes('fewer days')) return { text: t('hospital:quality.verdict.better'), tone: 'good' };
    if (lower.includes('worse') || lower.includes('more days')) return { text: t('hospital:quality.verdict.worse'), tone: 'bad' };
    if (lower.includes('no different') || lower.includes('same as') || lower.includes('average days')) return { text: t('hospital:quality.verdict.same'), tone: 'neutral' };
    if (lower.includes('too small')) return { text: t('hospital:quality.verdict.tooSmall'), tone: 'na' };
    if (lower.includes('not available')) return { text: t('hospital:quality.verdict.notReported'), tone: 'na' };
    return { text: cmp, tone: 'neutral' };
  };
  const toneStyles = {
    good: { text: 'text-emerald-300', bg: 'bg-emerald-500/15', border: 'border-emerald-500/40', icon: '✓' },
    neutral: { text: 'text-sky-300', bg: 'bg-sky-500/10', border: 'border-sky-500/30', icon: '~' },
    bad: { text: 'text-red-300', bg: 'bg-red-500/15', border: 'border-red-500/40', icon: '✗' },
    na: { text: 'text-white/40', bg: 'bg-white/5', border: 'border-white/10', icon: '—' },
  } as const;

  // Friendly measure-name rewrites (CMS shipping titles are accurate but academic)
  const friendly = (name: string): string => {
    if (!name) return '';
    return name
      .replace(/Hospital return days/i, 'Days patients had to come back')
      .replace(/Hybrid Hospital-Wide All-Cause Risk Standardized Mortality Rate/i, 'Hospital-wide death rate (risk-adjusted)')
      .replace(/Hybrid Hospital-Wide All-Cause Readmission Measure \(HWR\)/i, 'Hospital-wide readmission rate')
      .replace(/Acute Myocardial Infarction \(AMI\)/i, 'Heart attack')
      .replace(/Heart failure \(HF\)/i, 'Heart failure')
      .replace(/chronic obstructive pulmonary disease \(COPD\)/i, 'COPD')
      .replace(/Rate of unplanned hospital visits after/i, 'Unplanned visits after')
      .replace(/Ratio of /i, '')
      .replace(/Rate of /i, '')
      .replace(/30-Day Readmission Rate/i, '30-day readmissions')
      .replace(/(\d+)-Day/i, '$1-day');
  };

  // Plain-English score formatting based on measure family
  const formatScore = (m: any): string | null => {
    if (m.score == null) return null;
    const s = Number(m.score);
    const id = (m.measure_id || '').toUpperCase();
    const name = (m.measure_name || '').toLowerCase();
    if (name.includes('return days') || id.includes('EDAC')) {
      return t('hospital:quality.score.daysVsNational', { value: `${s > 0 ? '+' : ''}${s.toFixed(1)}` });
    }
    if (name.includes('per 1,000') || name.includes('per 1000')) {
      return t('hospital:quality.score.per1000Patients', { value: s.toFixed(1) });
    }
    if (name.includes('rate') || name.includes('readmission') || name.includes('mortality')) {
      return t('hospital:quality.score.pctOfPatients', { value: s.toFixed(1) });
    }
    return `${s.toFixed(1)}`;
  };

  const groupLabels: Record<string, string> = {
    mortality: t('hospital:quality.groups.mortality'),
    readmission: t('hospital:quality.groups.readmission'),
    complications: t('hospital:quality.groups.complications'),
    infection: t('hospital:quality.groups.infection'),
    patient_experience: t('hospital:quality.groups.patientExperience'),
  };
  // Report-card inputs: per-group verdict tallies (same bucketing + verdict
  // the slider cards below use) and a national-median lookup for the ER-wait
  // tile.
  const tallies: QualityGroupTally[] = (['mortality', 'readmission', 'complications', 'infection', 'patient_experience'] as const)
    .map(g => {
      const c = { better: 0, same: 0, worse: 0, na: 0 };
      groups[g].forEach(m => {
        const tone = verdict(m.compared_to_national).tone;
        if (tone === 'good') c.better++; else if (tone === 'neutral') c.same++; else if (tone === 'bad') c.worse++; else c.na++;
      });
      return { key: g, label: groupLabels[g], ...c };
    })
    .filter(g => g.better + g.same + g.worse + g.na > 0);
  const nationalMedian = (measureId: string): number | null => {
    const n = aggIndex[measureId]?.national;
    return n?.median_score != null && Number.isFinite(Number(n.median_score)) ? Number(n.median_score) : null;
  };

  const cardCls = `terminal-card p-6 ${isAhp ? '' : 'bg-gradient-corruption'}`;
  const cardStyle = isAhp ? { backgroundColor: ahpCardBg, borderColor: ahpBorder, color: ahpText } : undefined;

  return (
    <div className="space-y-6">
      <div className="mb-2">
        <h2 className={`text-2xl font-bold mb-2 ${isAhp ? (isDark ? 'text-white' : 'text-foreground') : 'text-terminal-green'}`}
            style={isAhp ? { fontFamily: "'Instrument Serif', Georgia, serif", fontWeight: 400 } : undefined}>
          {t('hospital:quality.heading')}
        </h2>
        <p className="text-sm text-muted-foreground">
          {t('hospital:quality.sourcedFrom', { hospitalName: hospital.name })}
        </p>
      </div>

      {/* Report card — every quality signal we hold, one frame, plus a
          data-generated paragraph unique to this hospital. Dignity's
          briefing slot (video when produced, GLB + Guide-me otherwise) sits
          on its left. */}
      <QualityReportCard
        hospital={hospital}
        measures={measures || []}
        measuresLoading={isLoading}
        tallies={tallies}
        nationalMedian={nationalMedian}
        price={{ markup: priceMarkup, rank: priceRank, totalRanked }}
        dataVintageYear={dataVintageYear}
        isDark={isDark}
        briefing={briefing}
        onPlayBriefing={onPlayBriefing}
        onGuideMe={() => { window.dispatchEvent(new CustomEvent('dignity:guide-play', { detail: { pageKey: 'hospital-quality' } })); }}
        // DignityNarrationPlayer — pageKey hits dignity_narration_lines for
        // the 6 hospital-quality lines; lives in Dignity's column of the card.
        narration={(
          <Suspense fallback={null}>
            <DignityNarrationPlayerLazy pageKey="hospital-quality" compact />
          </Suspense>
        )}
      />

      {isLoading && (
        <Card className={cardCls} style={cardStyle}>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="w-4 h-4 animate-spin" /> {t('hospital:quality.loadingMeasures')}
          </div>
        </Card>
      )}

      {/* Detailed measure groups — consumer cards per measure, color-coded verdict on top.
          Groups labeled in plain English; raw rate + sample size shown small underneath. */}
      {(['mortality', 'readmission', 'complications', 'infection', 'patient_experience'] as const).map(group => {
        const groupLabel = groupLabels[group];
        const groupSubtitle = {
          mortality: t('hospital:quality.groupSubtitles.mortality'),
          readmission: t('hospital:quality.groupSubtitles.readmission'),
          complications: t('hospital:quality.groupSubtitles.complications'),
          infection: t('hospital:quality.groupSubtitles.infection'),
          patient_experience: t('hospital:quality.groupSubtitles.patientExperience'),
        }[group];
        // Sort: bad verdicts first (so worst-performing measures surface), then neutral, then good, then na
        const tonePriority = { bad: 0, neutral: 1, good: 2, na: 3 };
        const sorted = [...groups[group]].sort((a, b) => {
          const pa = tonePriority[verdict(a.compared_to_national).tone];
          const pb = tonePriority[verdict(b.compared_to_national).tone];
          return pa - pb;
        });
        if (sorted.length === 0) return null;
        return (
          <Card key={group} className={cardCls} style={cardStyle}>
            <div className="mb-4">
              <h3 className={`text-lg font-semibold ${isAhp ? '' : 'text-terminal-green'}`} style={{ fontFamily: "'Instrument Serif', Georgia, serif", fontWeight: 400 }}>
                {groupLabel}
              </h3>
              <p className="text-xs opacity-70 mt-1">{groupSubtitle} · {t('hospital:quality.measureCount', { count: sorted.length })}</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {sorted.map((m, i) => (
                <QualityMeasureSlider
                  key={`${m.measure_id}-${i}`}
                  measure={m}
                  verdict={verdict(m.compared_to_national)}
                  friendlyName={friendly(m.measure_name || m.measure_id)}
                  formattedScore={formatScore(m)}
                  isAhp={isAhp}
                  isDark={isDark}
                  aggregate={aggIndex[m.measure_id]}
                  stateCode={hospital.state}
                  hospitalId={hospital.id}
                />
              ))}
            </div>
          </Card>
        );
      })}

      {!isLoading && (!measures || measures.length === 0) && (
        <Card className={cardCls} style={cardStyle}>
          <div className="text-sm text-muted-foreground">
            {t('hospital:quality.noMeasuresPre')} <code className="text-xs bg-black/20 px-1.5 py-0.5 rounded">scripts/ingest_cms_quality.py</code> {t('hospital:quality.noMeasuresPost')}
          </div>
        </Card>
      )}

      <div className="text-[11px] opacity-50">
        {t('hospital:quality.sourceFooter')}
      </div>
    </div>
  );
}


export default HospitalProfile;